import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSiteMode } from '@/context/SiteModeContext';
import TopBar from '@/components/Layout/TopBar';
import LeftPanel from '@/components/Layout/LeftPanel';
import Canvas from '@/components/Canvas/Canvas';
import RightSidebar from '@/components/Layout/RightSidebar';
import Profile from '@/pages/Profile';
import AdminPanel from '@/pages/AdminPanel';
import ManagePatterns from '@/pages/ManagePatterns';
import BackupManagement from '@/pages/BackupManagement';
import WelcomeScreen from '@/components/WelcomeScreen';
import LoadPatternModal from '@/components/LoadPatternModal';
import ActionConfirmModal from '@/components/ActionConfirmModal';
import DraftMeasurementsModal from '@/components/DraftMeasurementsModal';
import UnsavedChangesModal from '@/components/UnsavedChangesModal';
import PrintPreviewWorkspace from '@/components/PrintPreviewWorkspace';
import CurveHandlePanel from '@/components/CurveHandlePanel';
import { toast } from '@/components/ui/sonner';
import {
  generatePattern,
  getDefaultMeasurements,
  getGarmentConfig,
  resolveGeometryVersion,
  resolveFormulaVersion,
} from '@/lib/drafting';
import { buildJacketFrontButtons } from '@/lib/patterns/jacket/buttonPlan';
import { recomputeShirtCollarBandV3 } from '@/lib/patterns/shirt/siy_v1';
import {
  isOwner,
  normalizeRole,
  canAccessUserManagement as canAccessUserManagementByRole,
  canAccessManagePatterns as canAccessManagePatternsByRole,
  canAccessBackupManagement as canAccessBackupManagementByRole,
  canEditPatternCanvas,
  canSavePatternVersion as canSavePatternVersionByRole,
  canSavePrintLayoutPreview,
} from '@/lib/rbac';
import {
  clampLayoutPiecesToViewport,
  compactArrangeLayout,
  createDefaultLayout,
  DEFAULT_PRINT_PIECE_SETTINGS,
  estimateRollLengthInches,
  generatePDF,
  generatePDFFromPreviewSVG,
  getRecommendedCutLengthInches,
  getLayoutDiagnostics,
  resolvePieceLabelInsetInches,
  resolvePiecePrintSettings,
} from '@/lib/pdfExport';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const PATTERN_EDIT_LOCK_MS = 30 * 60 * 1000;
const PATTERN_TAB_HANDOFF_QUERY = 'patternHandoff';
const PATTERN_TAB_HANDOFF_PREFIX = 'patternengine:tab-handoff:';
const VERSION_COLORS = ['#1e40af', '#dc2626', '#16a34a'];
const roundToEighth = (value) => Math.round(value / 0.125) * 0.125;
const roundToSixteenth = (value) => Math.round(value / 0.0625) * 0.0625;
const roundToQuarter = (value) => Math.round(value / 0.25) * 0.25;
const DRAWN_MEASUREMENT_SOLVER_DEFINITIONS = {
  jacket: {
    chest: { read: (patternPayload) => Number(patternPayload?.matchingSummary?.chestHalfDrawn || 0) * 2 },
    waist: { read: (patternPayload) => Number(patternPayload?.matchingSummary?.waistHalfDrawn || 0) * 2 },
    belly: { read: (patternPayload) => Number(patternPayload?.matchingSummary?.bellyHalfDrawn || 0) * 2 },
    seat: { read: (patternPayload) => Number(patternPayload?.matchingSummary?.seatHalfDrawn || 0) * 2 },
    acrossBack: { summaryKey: 'acrossBackDrawn' },
    acrossFront: { summaryKey: 'acrossFrontDrawn' },
    armholeScyeDepth: { summaryKey: 'averageScyeVertical', seedKey: 'armholeScyeDepth' },
    jacketLengthBack: { summaryKey: 'backLengthDrawn' },
    jacketLengthFront: { summaryKey: 'frontLengthDrawn' },
    jacketSleeveLength: { summaryKey: 'jacketSleeveLengthMidpointDrawn' },
    jacketSleeveBicep: { summaryKey: 'jacketSleeveBicepTotalDrawn' },
    jacketSleeveElbow: { summaryKey: 'jacketSleeveElbowTotalDrawn' },
    jacketSleeveHem: { summaryKey: 'jacketSleeveHemTotalDrawn' },
    lapelWidth: { read: (patternPayload) => Number(patternPayload?.lapelSummary?.lapelWidth) },
  },
  shirt: {
    neck: { summaryKey: 'totalNeckFull' },
    chest: { summaryKey: 'totalChestFull' },
    bust: { summaryKey: 'targetBustFull', seedKey: 'bust' },
    waist: { summaryKey: 'totalWaistFull' },
    belly: { summaryKey: 'totalBellyFull' },
    seat: { summaryKey: 'totalSeatFull' },
    shoulder: { summaryKey: 'shoulderHorizontalFull' },
    acrossBack: { summaryKey: 'acrossBackFull' },
    acrossFront: { summaryKey: 'acrossFrontFull' },
    armholeScyeDepth: { summaryKey: 'scyeDepthAverage' },
    shirtLengthBack: { summaryKey: 'shirtLengthBackDrawn' },
    shirtLengthFront: { summaryKey: 'shirtLengthFrontDrawn', seedKey: 'shirtLengthFront' },
    sleeveLength: { summaryKey: 'sleeveDisplayedLength' },
    shortSleeveLength: { summaryKey: 'sleeveDisplayedLength' },
    bicepWidth: { summaryKey: 'sleeveBicepUnwrappedDrawn' },
    elbowWidth: { summaryKey: 'sleeveElbowUnwrappedDrawn' },
    maxCuffWidth: { summaryKey: 'sleeveHemUnwrappedDrawn' },
    shortSleeveHem: { summaryKey: 'sleeveHemUnwrappedDrawn' },
  },
  trouser: {
    waist: { summaryKey: 'waistDrawnNetFull' },
    seat: { summaryKey: 'seatDrawnFull' },
    rise: { summaryKey: 'frontRiseDrawn' },
    outseam: { summaryKey: 'frontOutseamDrawn' },
    thigh: { summaryKey: 'thighDrawn' },
    knee: { summaryKey: 'kneeDrawn' },
    hem: { summaryKey: 'hemDrawn' },
  },
};

const FAST_LOCAL_DRAWN_SOLVER_KEYS = {
  trouser: new Set(['waist', 'seat', 'thigh', 'knee', 'hem', 'rise', 'outseam']),
};

const readDrawnMeasurementValue = (patternPayload, garmentType, drawnKey) => {
  const definition = DRAWN_MEASUREMENT_SOLVER_DEFINITIONS?.[garmentType]?.[drawnKey];
  const value = definition?.read
    ? Number(definition.read(patternPayload))
    : (definition ? Number(patternPayload?.matchingSummary?.[definition.summaryKey]) : Number.NaN);
  return Number.isFinite(value) ? value : null;
};

const shouldUseFastLocalDrawnSolver = (garmentType, drawnKey) => Boolean(
  FAST_LOCAL_DRAWN_SOLVER_KEYS?.[garmentType]?.has(drawnKey),
);

const solveSeedForDrawnTargetFastLocal = ({
  initialSeed,
  startingDrawn,
  target,
  minSeed,
  maxSeed,
  evaluateSeed,
  tolerance = 0.03125,
}) => {
  const normalizedInitialSeed = roundToEighth(Math.min(maxSeed, Math.max(minSeed, initialSeed)));
  const register = (evaluation, currentBest) => {
    if (!evaluation || !Number.isFinite(Number(evaluation.drawnValue))) return currentBest;
    const nextEvaluation = {
      ...evaluation,
      error: Math.abs(Number(evaluation.drawnValue) - target),
    };
    if (!currentBest || nextEvaluation.error < currentBest.error) {
      return nextEvaluation;
    }
    return currentBest;
  };

  let best = {
    seed: normalizedInitialSeed,
    drawnValue: Number(startingDrawn),
    error: Math.abs(Number(startingDrawn) - target),
  };
  if (best.error <= tolerance) return best;

  const desiredDirection = Math.sign(target - Number(startingDrawn)) || 1;
  const visited = new Set([normalizedInitialSeed.toFixed(3)]);

  const evaluateAndTrack = (seedValue) => {
    const evaluation = evaluateSeed(seedValue);
    if (!evaluation) return null;
    visited.add(evaluation.seed.toFixed(3));
    best = register(evaluation, best);
    return {
      ...evaluation,
      error: Math.abs(Number(evaluation.drawnValue) - target),
    };
  };

  const initialGuessOffset = Math.max(0.125, Math.abs(target - Number(startingDrawn)));
  let previous = {
    seed: normalizedInitialSeed,
    drawnValue: Number(startingDrawn),
    error: best.error,
  };
  let current = evaluateAndTrack(normalizedInitialSeed + (desiredDirection * initialGuessOffset));

  if (!current || current.seed === previous.seed) {
    current = evaluateAndTrack(normalizedInitialSeed + (desiredDirection * 0.125));
  }

  if (!current || current.seed === previous.seed) {
    current = evaluateAndTrack(normalizedInitialSeed - (desiredDirection * 0.125));
  }

  if (!current) return null;
  if (current.error <= tolerance) return best;

  for (let iteration = 0; iteration < 4; iteration += 1) {
    const slopeDenominator = Number(current.drawnValue) - Number(previous.drawnValue);
    let nextSeed = Math.abs(slopeDenominator) > 1e-6
      ? Number(current.seed) + (((target - Number(current.drawnValue)) * (Number(current.seed) - Number(previous.seed))) / slopeDenominator)
      : Number(current.seed) + (desiredDirection * 0.125 * (2 ** (iteration + 1)));

    nextSeed = roundToEighth(Math.min(maxSeed, Math.max(minSeed, nextSeed)));
    if (visited.has(nextSeed.toFixed(3))) {
      const fallbackDirection = Math.sign(target - Number(current.drawnValue)) || desiredDirection;
      nextSeed = roundToEighth(Math.min(
        maxSeed,
        Math.max(minSeed, Number(current.seed) + (fallbackDirection * 0.125 * (2 ** (iteration + 1)))),
      ));
    }

    if (visited.has(nextSeed.toFixed(3))) break;

    const next = evaluateAndTrack(nextSeed);
    if (!next) break;
    if (next.error <= tolerance) return best;

    previous = current;
    current = next;
  }

  [-0.5, -0.25, -0.125, 0.125, 0.25, 0.5].forEach((offset) => {
    evaluateAndTrack(Number(best.seed) + offset);
  });

  return best.error <= 0.0625 ? best : null;
};

const solveSeedForDrawnTargetExhaustive = ({
  initialSeed,
  startingDrawn,
  target,
  minSeed,
  maxSeed,
  evaluateSeed,
}) => {
  let best = {
    seed: roundToEighth(Math.min(maxSeed, Math.max(minSeed, initialSeed))),
    error: Math.abs(Number(startingDrawn) - target),
    drawnValue: Number(startingDrawn),
  };
  const visited = new Set();
  let candidateSeed = best.seed;

  const consider = (seedValue) => {
    const evaluation = evaluateSeed(seedValue);
    if (!evaluation) return null;
    const error = Math.abs(Number(evaluation.drawnValue) - target);
    if (error < best.error) {
      best = {
        seed: evaluation.seed,
        drawnValue: Number(evaluation.drawnValue),
        error,
      };
    }
    return {
      seed: evaluation.seed,
      drawnValue: Number(evaluation.drawnValue),
      error,
    };
  };

  for (let iteration = 0; iteration < 6; iteration += 1) {
    const current = consider(candidateSeed);
    if (!current || current.error <= 0.03125) break;
    visited.add(current.seed.toFixed(3));

    const probe = consider(current.seed + 0.125);
    const slope = probe && Math.abs(Number(probe.seed) - Number(current.seed)) > 1e-8
      ? (Number(probe.drawnValue) - Number(current.drawnValue)) / (Number(probe.seed) - Number(current.seed))
      : 0;
    const correction = target - Number(current.drawnValue);
    let nextSeed = Math.abs(slope) > 1e-6
      ? Number(current.seed) + (correction / slope)
      : Number(current.seed) + (Math.sign(correction || 1) * 0.125);

    nextSeed = roundToEighth(Math.min(maxSeed, Math.max(minSeed, nextSeed)));
    if (visited.has(nextSeed.toFixed(3))) {
      nextSeed = roundToEighth(Math.min(
        maxSeed,
        Math.max(minSeed, Number(current.seed) + (Math.sign(correction || 1) * 0.125)),
      ));
    }

    candidateSeed = nextSeed;
  }

  [-0.5, -0.25, -0.125, 0.125, 0.25, 0.5].forEach((offset) => consider(Number(best.seed) + offset));

  if (best.error > 0.03125) {
    const scannedSeeds = new Set();
    [initialSeed, best.seed].forEach((baseSeed) => {
      for (let stepIndex = 0; stepIndex <= 16; stepIndex += 1) {
        const offset = stepIndex * 0.125;
        [baseSeed + offset, baseSeed - offset].forEach((seedCandidate) => {
          const normalizedSeed = roundToEighth(Math.min(maxSeed, Math.max(minSeed, seedCandidate)));
          const cacheKey = normalizedSeed.toFixed(3);
          if (scannedSeeds.has(cacheKey)) return;
          scannedSeeds.add(cacheKey);
          consider(normalizedSeed);
        });
      }
    });
  }

  return Number.isFinite(best.error) ? best : null;
};

const solveSeedForDrawnTarget = ({
  garmentType,
  drawnKey,
  targetDrawn,
  currentMeasurements,
  currentPatternData,
  formulaVersion,
  geometryVersion,
  patternOptions = {},
}) => {
  const definition = DRAWN_MEASUREMENT_SOLVER_DEFINITIONS?.[garmentType]?.[drawnKey];
  if (!definition || !Number.isFinite(Number(targetDrawn))) return null;

  const seedKey = definition.seedKey || drawnKey;
  const initialSeed = Number(currentMeasurements?.[seedKey]);
  if (!Number.isFinite(initialSeed)) return null;

  const minSeed = Number.isFinite(Number(definition.minSeed)) ? Number(definition.minSeed) : 0.125;
  const maxSeed = 200;
  const controls = {
    backCrotchScoopX: currentMeasurements?.backCrotchScoopX,
    backCrotchScoopY: currentMeasurements?.backCrotchScoopY,
    formulaVersion,
    geometryVersion,
    freeModeActive: patternOptions?.freeModeActive === true,
    pointDeltas: patternOptions?.pointDeltas || {},
  };
  const evaluationCache = new Map();
  const evaluateSeed = (seedValue) => {
    const normalizedSeed = roundToEighth(Math.min(maxSeed, Math.max(minSeed, seedValue)));
    const cacheKey = normalizedSeed.toFixed(3);
    if (evaluationCache.has(cacheKey)) {
      return evaluationCache.get(cacheKey);
    }

    try {
      const candidate = generatePattern({
        ...currentMeasurements,
        [seedKey]: normalizedSeed,
      }, controls, garmentType);
      const drawnValue = readDrawnMeasurementValue(candidate, garmentType, drawnKey);
      const result = Number.isFinite(Number(drawnValue))
        ? { seed: normalizedSeed, drawnValue: Number(drawnValue) }
        : null;
      evaluationCache.set(cacheKey, result);
      return result;
    } catch (_error) {
      evaluationCache.set(cacheKey, null);
      return null;
    }
  };

  const visibleDrawn = readDrawnMeasurementValue(currentPatternData, garmentType, drawnKey);
  const generatedInitialDrawn = evaluateSeed(initialSeed)?.drawnValue;
  const startingDrawn = visibleDrawn ?? generatedInitialDrawn;
  if (!Number.isFinite(Number(startingDrawn))) return null;

  const target = Number(targetDrawn);
  const fastSolved = shouldUseFastLocalDrawnSolver(garmentType, drawnKey)
    ? solveSeedForDrawnTargetFastLocal({
        initialSeed,
        startingDrawn,
        target,
        minSeed,
        maxSeed,
        evaluateSeed,
      })
    : null;
  const solved = fastSolved || solveSeedForDrawnTargetExhaustive({
    initialSeed,
    startingDrawn,
    target,
    minSeed,
    maxSeed,
    evaluateSeed,
  });

  return solved && Number.isFinite(solved.error)
    ? { seedKey, seedValue: solved.seed, drawnValue: solved.drawnValue, targetDrawn: target }
    : null;
};
const TROUSER_DEFAULT_BACK_KNEE_EXCESS = 2;
const TROUSER_V4_DEFAULT_BACK_HEM_EXCESS = 1.5;

const resolveTrouserFrontSplitFromBackExcess = (totalValue, backExcess) => {
  const total = Number(totalValue || 0);
  if (!Number.isFinite(total) || total <= 0) return 0.125;
  const front = (total - Number(backExcess || 0)) / 2;
  return roundToEighth(Math.min(Math.max(0.125, total - 0.125), Math.max(0.125, front)));
};

const resolveTrouserDefaultFrontKnee = (totalKnee) => resolveTrouserFrontSplitFromBackExcess(
  totalKnee,
  TROUSER_DEFAULT_BACK_KNEE_EXCESS,
);

const resolveTrouserV4DefaultFrontHem = (totalHem) => resolveTrouserFrontSplitFromBackExcess(
  totalHem,
  TROUSER_V4_DEFAULT_BACK_HEM_EXCESS,
);

const resolveTrouserV4KneeLevelInput = (measurements = {}) => {
  const outseam = Number(measurements?.outseam);
  const rise = Number(measurements?.rise);

  if (!Number.isFinite(outseam) || !Number.isFinite(rise)) {
    return measurements?.kneeLevelInput;
  }

  const kneeYFromHem = roundToQuarter(((outseam - rise) / 2) + 2);
  return roundToSixteenth(outseam - kneeYFromHem);
};

const applyGeometryScopedDefaults = (
  garmentType = 'trouser',
  geometryVersion = '',
  baseMeasurements = {},
  options = {},
) => {
  const { isFreshDraft = false } = options;
  const normalizedGarment = String(garmentType || '').toLowerCase();
  const normalizedGeometry = String(geometryVersion || '').toLowerCase();

  if (normalizedGarment === 'shirt' && (normalizedGeometry === 'shirt_geom_v2' || normalizedGeometry === 'shirt_geom_v3' || normalizedGeometry === 'shirt_geom_v4')) {
    const incomingLastButton = Number(baseMeasurements?.lastButtonPosition);
    const shouldApplyV2Default = isFreshDraft
      && (!Number.isFinite(incomingLastButton) || Math.abs(incomingLastButton - 3.5) < 1e-6);

    return {
      ...baseMeasurements,
      lastButtonPosition: shouldApplyV2Default
        ? 5.5
        : (Number.isFinite(incomingLastButton) ? incomingLastButton : 5.5),
    };
  }

  if (normalizedGarment === 'trouser' && normalizedGeometry === 'trouser_geom_v3') {
    if (isFreshDraft) {
      return {
        ...baseMeasurements,
        cbSeatOffset_X: 0,
      };
    }

    if (baseMeasurements?.cbSeatOffset_X === undefined || baseMeasurements?.cbSeatOffset_X === null || baseMeasurements?.cbSeatOffset_X === '') {
      return {
        ...baseMeasurements,
        cbSeatOffset_X: 0,
      };
    }
  }

  if (normalizedGarment === 'trouser' && normalizedGeometry === 'trouser_geom_v4' && isFreshDraft) {
    const waist = Number(baseMeasurements?.waist);
    const safeQuarterWaist = Number.isFinite(waist) ? waist / 4 : Number(baseMeasurements?.waist || 0) / 4;
    const frontWaistTarget = safeQuarterWaist + 0.5;
    const backWaistTarget = safeQuarterWaist - 0.5;
    const frontWaistAdd = roundToEighth(frontWaistTarget - safeQuarterWaist);
    const backWaistAdd = roundToEighth(backWaistTarget - safeQuarterWaist);

    return {
      ...baseMeasurements,
      frontKnee: resolveTrouserDefaultFrontKnee(baseMeasurements?.knee),
      frontHem: resolveTrouserV4DefaultFrontHem(baseMeasurements?.hem),
      frontWaistAdd,
      backWaistAdd,
      forkWidth: 1.75,
      frontCrotchCurveCtrlDx: -0.125,
      frontCrotchCurveCtrlDy: 0.5,
      kneeLevelInput: resolveTrouserV4KneeLevelInput(baseMeasurements),
      creaseOffset: 0.25,
      frontThighSideOffset: 0.25,
      frontThighSideOffsetExact: true,
      hemShoeShapeEnabled: true,
      hemStepValue: 1,
      cbSeatOffset_X: 0,
      cbSeatOffset_Y: 1,
      cbWaistHeight: 1.5,
      cbWaistOffsetX: -0.5,
      backCreaseOffset: -1,
      backThighSideOffset: 0.75,
      backDartMode: 'two',
      useTwoBackDarts: true,
      backDartWidth: 2,
      backDartDepth: 4.5,
      backDartSpacing: 2.25,
    };
  }

  return baseMeasurements;
};

const buildAutoShirtSideSeamPayload = (baseMeasurements = {}, patternLike = null) => {
  const rawDepthFactor = Number(baseMeasurements?.sideSeamAutoDepth);
  const sideSeamDepthFactor = Number.isFinite(rawDepthFactor)
    ? Math.min(2.5, Math.max(1, rawDepthFactor))
    : 1.625;

  const zeroedSideSeamMeasurements = {
    ...baseMeasurements,
    ...SHIRT_SIDE_SEAM_OFFSET_KEYS.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {}),
  };

  const versionControls = {
    formulaVersion: resolveFormulaVersion('shirt', patternLike || {}),
    geometryVersion: resolveGeometryVersion('shirt', patternLike || {}),
  };

  const basePattern = generatePattern(zeroedSideSeamMeasurements, versionControls, 'shirt');
  const handleMap = buildHandleMap(basePattern?.curveHandles || []);

  const extractChainPoints = (chainIds = []) => {
    if (chainIds.length !== 3) return null;
    const first = handleMap.get(chainIds[0]);
    const second = handleMap.get(chainIds[1]);
    const third = handleMap.get(chainIds[2]);
    if (!first || !second || !third) return null;

    return [
      first.segmentStart,
      first.segmentEnd,
      second.segmentEnd,
      third.segmentEnd,
    ];
  };

  const payload = {};

  Object.entries(SHIRT_SIDE_SEAM_HANDLE_CHAINS).forEach(([pieceKey, chainIds]) => {
    const points = extractChainPoints(chainIds);
    if (!points) return;

    const autoControls = computeCardinalQuadraticControls(points, 0.5, {
      chestWaistDepthFactor: sideSeamDepthFactor,
      interiorSign: pieceKey === 'front' ? 1 : -1,
    });

    chainIds.forEach((handleId, index) => {
      const baseHandle = handleMap.get(handleId);
      const nextControl = autoControls[index];
      if (!baseHandle || !nextControl) return;

      const deltaXInternal = nextControl.x - baseHandle.point.x;
      const deltaYInternal = nextControl.y - baseHandle.point.y;

      payload[baseHandle.xKey] = roundToSixteenth(deltaXInternal);
      payload[baseHandle.yKey] = roundToSixteenth(-deltaYInternal);
    });
  });

  return payload;
};

const buildAutoShirtSleeveCurvesPayload = (baseMeasurements = {}, patternLike = null) => {
  const zeroedSleeveMeasurements = {
    ...baseMeasurements,
    ...SHIRT_SLEEVE_FP_BP_OFFSET_KEYS.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {}),
  };

  const versionControls = {
    formulaVersion: resolveFormulaVersion('shirt', patternLike || {}),
    geometryVersion: resolveGeometryVersion('shirt', patternLike || {}),
  };

  const basePattern = generatePattern(zeroedSleeveMeasurements, versionControls, 'shirt');
  const handleMap = buildHandleMap(basePattern?.curveHandles || []);

  const sleeveFrontUnderarm = handleMap.get('sleeve-front-underarm-ctrl');
  const sleeveFrontCrown = handleMap.get('sleeve-front-crown-ctrl');
  const sleeveBackCrown = handleMap.get('sleeve-back-crown-ctrl');
  const sleeveBackUnderarm = handleMap.get('sleeve-back-underarm-ctrl');

  if (!sleeveFrontUnderarm || !sleeveFrontCrown || !sleeveBackCrown || !sleeveBackUnderarm) {
    return SHIRT_SLEEVE_FP_BP_OFFSET_KEYS.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});
  }

  const BiC1 = sleeveFrontUnderarm.segmentStart;
  const FP = sleeveFrontUnderarm.segmentEnd;
  const SL1 = sleeveFrontCrown.segmentEnd;
  const BP = sleeveBackCrown.segmentEnd;
  const BiC2 = sleeveBackUnderarm.segmentEnd;

  const buildScaledControls = (scale = 1) => {
    const crownBlend = 0.35 * scale;
    const cFrontCrown = {
      x: SL1.x - (SL1.x - FP.x) * crownBlend,
      y: SL1.y,
    };
    const cBackCrown = {
      x: SL1.x + (BP.x - SL1.x) * crownBlend,
      y: SL1.y,
    };

    const targetFrontFromBiC1 = {
      x: BiC1.x + (FP.x - BiC1.x) * (0.25 * scale),
      y: BiC1.y + (FP.y - BiC1.y) * (0.4 * scale),
    };

    const vFp = {
      x: cFrontCrown.x - FP.x,
      y: cFrontCrown.y - FP.y,
    };
    const fpKCandidates = [];
    if (Math.abs(vFp.x) > 1e-6) fpKCandidates.push((FP.x - targetFrontFromBiC1.x) / vFp.x);
    if (Math.abs(vFp.y) > 1e-6) fpKCandidates.push((FP.y - targetFrontFromBiC1.y) / vFp.y);
    const fpK = Math.min(0.9, Math.max(0.45, fpKCandidates.length
      ? fpKCandidates.reduce((sum, value) => sum + value, 0) / fpKCandidates.length
      : 0.62));

    const cFrontUnderarm = {
      x: FP.x - vFp.x * fpK,
      y: FP.y - vFp.y * fpK,
    };

    const targetBackFromBiC2 = {
      x: BiC2.x + (BP.x - BiC2.x) * (0.25 * scale),
      y: BiC2.y + (BP.y - BiC2.y) * (0.4 * scale),
    };

    const vBp = {
      x: BP.x - cBackCrown.x,
      y: BP.y - cBackCrown.y,
    };
    const bpKCandidates = [];
    if (Math.abs(vBp.x) > 1e-6) bpKCandidates.push((targetBackFromBiC2.x - BP.x) / vBp.x);
    if (Math.abs(vBp.y) > 1e-6) bpKCandidates.push((targetBackFromBiC2.y - BP.y) / vBp.y);
    const bpK = Math.min(0.85, Math.max(0.4, bpKCandidates.length
      ? bpKCandidates.reduce((sum, value) => sum + value, 0) / bpKCandidates.length
      : 0.56));

    const cBackUnderarm = {
      x: BP.x + vBp.x * bpK,
      y: BP.y + vBp.y * bpK,
    };

    return { cFrontUnderarm, cFrontCrown, cBackCrown, cBackUnderarm };
  };

  const scaledControls = buildScaledControls(1);
  const payload = {
    sleeveFrontUnderarmCtrlDx: roundToSixteenth(scaledControls.cFrontUnderarm.x - sleeveFrontUnderarm.point.x),
    sleeveFrontUnderarmCtrlDy: roundToSixteenth(-(scaledControls.cFrontUnderarm.y - sleeveFrontUnderarm.point.y)),
    sleeveFrontCrownCtrlDx: roundToSixteenth(scaledControls.cFrontCrown.x - sleeveFrontCrown.point.x),
    sleeveFrontCrownCtrlDy: roundToSixteenth(-(scaledControls.cFrontCrown.y - sleeveFrontCrown.point.y)),
    sleeveBackCrownCtrlDx: roundToSixteenth(scaledControls.cBackCrown.x - sleeveBackCrown.point.x),
    sleeveBackCrownCtrlDy: roundToSixteenth(-(scaledControls.cBackCrown.y - sleeveBackCrown.point.y)),
    sleeveBackUnderarmCtrlDx: roundToSixteenth(scaledControls.cBackUnderarm.x - sleeveBackUnderarm.point.x),
    sleeveBackUnderarmCtrlDy: roundToSixteenth(-(scaledControls.cBackUnderarm.y - sleeveBackUnderarm.point.y)),
  };

  return payload;
};

const normalizeJacketBellyProfile = (value = 'normal') => {
  const normalized = String(value || 'normal').toLowerCase();
  if (['slim', 'normal', 'slight_belly', 'big_belly', 'extreme_belly'].includes(normalized)) {
    return normalized;
  }
  return 'normal';
};

const normalizeJacketDartMode = (value = 'belly_and_fish') => {
  const normalized = String(value || 'belly_and_fish').toLowerCase();
  if (['off', 'fish', 'belly_and_fish'].includes(normalized)) {
    return normalized;
  }
  return 'belly_and_fish';
};

const JACKET_POSTURE_OPTIONS = ['normal', 'stoop', 'sway'];

const normalizeJacketPosture = (value = 'normal') => {
  const normalized = String(value || 'normal').toLowerCase();
  return JACKET_POSTURE_OPTIONS.includes(normalized) ? normalized : 'normal';
};

const getJacketPostureBackToFtHps = (posture = 'normal') => {
  const normalized = normalizeJacketPosture(posture);
  if (normalized === 'stoop') return 1;
  if (normalized === 'sway') return 0;
  return 0.5;
};

const inferJacketPostureFromBackToFtHps = (backToFtHps) => {
  const numeric = Number(backToFtHps);
  if (!Number.isFinite(numeric)) return 'normal';
  if (Math.abs(numeric - 1) < 1e-6) return 'stoop';
  if (Math.abs(numeric - 0) < 1e-6) return 'sway';
  return 'normal';
};

const getJacketBellyProfileRule = (bellyProfile = 'normal') => {
  const key = normalizeJacketBellyProfile(bellyProfile);
  if (key === 'slim') {
    return { sideOffsetFromFcps: 1.25, cfForwardOffset: 0, swpfOffsetFromScpf: -0.5 };
  }
  if (key === 'slight_belly') {
    return { sideOffsetFromFcps: 0.75, cfForwardOffset: 0.25, swpfOffsetFromScpf: 0 };
  }
  if (key === 'big_belly') {
    return { sideOffsetFromFcps: 0.5, cfForwardOffset: 0.5, swpfOffsetFromScpf: 0.25 };
  }
  if (key === 'extreme_belly') {
    return { sideOffsetFromFcps: 0.375, cfForwardOffset: 1, swpfOffsetFromScpf: 0.5 };
  }
  return { sideOffsetFromFcps: 1, cfForwardOffset: 0, swpfOffsetFromScpf: -0.25 };
};

const applyJacketBellyProfileRule = (baseMeasurements = {}, options = {}) => {
  const { preserveExistingManualValues = false } = options;
  const current = { ...baseMeasurements };

  if (!preserveExistingManualValues || current.jacketFwpsDx === undefined || current.jacketFwpsDx === null || current.jacketFwpsDx === '') {
    current.jacketFwpsDx = 0;
  }
  if (!preserveExistingManualValues || current.jacketFbpsDx === undefined || current.jacketFbpsDx === null || current.jacketFbpsDx === '') {
    current.jacketFbpsDx = 0;
  }
  if (!preserveExistingManualValues || current.jacketFspsDx === undefined || current.jacketFspsDx === null || current.jacketFspsDx === '') {
    current.jacketFspsDx = 0;
  }

  return {
    ...current,
    bellyProfile: normalizeJacketBellyProfile(current?.bellyProfile),
  };
};

const applyJacketPostureRule = (baseMeasurements = {}, options = {}) => {
  const { preserveExistingManualValues = false } = options;
  const current = { ...baseMeasurements };
  const resolvedPosture = current?.posture
    ? normalizeJacketPosture(current.posture)
    : inferJacketPostureFromBackToFtHps(current?.backToFtHps);

  return {
    ...current,
    posture: resolvedPosture,
    backToFtHps: preserveExistingManualValues
      ? (current?.backToFtHps ?? getJacketPostureBackToFtHps(resolvedPosture))
      : getJacketPostureBackToFtHps(resolvedPosture),
  };
};

const applyJacketPitchRotationRule = (baseMeasurements = {}, options = {}) => {
  const { preserveExistingManualValues = false } = options;
  const current = { ...baseMeasurements };
  const resolvedPitchRotation = current?.sleevePitchRotation !== undefined
    && current?.sleevePitchRotation !== null
    && current?.sleevePitchRotation !== ''
    ? Number(current.sleevePitchRotation)
    : Number(current?.sleevePitchShift ?? 0);

  return {
    ...current,
    sleevePitchRotation: preserveExistingManualValues
      ? (current?.sleevePitchRotation ?? current?.sleevePitchShift ?? 0)
      : resolvedPitchRotation,
    sleevePitchShift: preserveExistingManualValues
      ? (current?.sleevePitchRotation ?? current?.sleevePitchShift ?? 0)
      : resolvedPitchRotation,
  };
};

const applyJacketDartModeRule = (baseMeasurements = {}) => {
  const current = { ...baseMeasurements };
  const inferredMode = current?.jacketDartMode
    ? normalizeJacketDartMode(current.jacketDartMode)
    : ((current?.fishDartEnabled !== false && current?.fishDartEnabled !== 0 && current?.fishDartEnabled !== '0')
      ? 'belly_and_fish'
      : 'off');

  return {
    ...current,
    jacketDartMode: inferredMode,
    fishDartEnabled: inferredMode !== 'off',
  };
};

const JACKET_CURVE_DEFAULTS = {
  frontNeckCtrlDx: -0.75,
  frontNeckCtrlDy: 1.25,
  jacketRoundedNotchCurveCtrlDx: 0,
  jacketRoundedNotchCurveCtrlDy: 0,
  jacketRoundedNotchCollarCurveCtrlDx: 0,
  jacketRoundedNotchCollarCurveCtrlDy: 0,
  underCollarCurveStartCtrlDx: -1,
  underCollarCurveStartCtrlDy: 2.75,
  underCollarCurveEndCtrlDx: -2.625,
  underCollarCurveEndCtrlDy: -3.25,
  topCollarCurveStartCtrlDx: 0,
  topCollarCurveStartCtrlDy: 0,
  topCollarCurveEndCtrlDx: -1.75,
  topCollarCurveEndCtrlDy: -2,
  shawlTcol2Lp0CtrlDx: 1,
  shawlTcol2Lp0CtrlDy: 1.5,
  shawlLp0Rl1CtrlDx: 4,
  shawlLp0Rl1CtrlDy: 4,
  jacketRl2Dx: 0,
  jacketRl2Dy: 0,
  diamondShawlTcol2Rl2CtrlDx: 0,
  diamondShawlTcol2Rl2CtrlDy: 0,
  frontArmPart1CtrlDx: 0.625,
  frontArmPart1CtrlDy: 0,
  frontArmPart2CtrlDx: 1,
  frontArmPart2CtrlDy: 1.25,
  frontChestWaistCtrlDx: 0.25,
  frontChestWaistCtrlDy: 0,
  sideFrontArmCtrlDx: -0.75,
  sideFrontArmCtrlDy: 1.375,
  sideFrontChestWaistCtrlDx: -0.375,
  sideFrontChestWaistCtrlDy: 0,
  sideFrontBellySeatCtrlDx: 0.125,
  sideFrontBellySeatCtrlDy: 0,
  sideBackArmCtrlDx: -0.125,
  sideBackArmCtrlDy: 0,
  sideBackWaistBellyCtrlDx: 0.125,
  sideBackWaistBellyCtrlDy: 0,
  sideBackBellySeatCtrlDx: -0.25,
  sideBackBellySeatCtrlDy: 0,
  frontJacketHemCurveCtrlDx: 1.5,
  frontJacketHemCurveCtrlDy: 4.5,
};

const applyJacketCurveDefaultRule = (baseMeasurements = {}, options = {}) => {
  const { preserveExistingManualValues = false } = options;
  const current = { ...baseMeasurements };

  Object.entries(JACKET_CURVE_DEFAULTS).forEach(([key, defaultValue]) => {
    if (!preserveExistingManualValues || current[key] === undefined || current[key] === null || current[key] === '') {
      current[key] = defaultValue;
    }
  });

  return current;
};

const getShirtPortlyDelta = (portlyType = 'normal') => {
  const key = String(portlyType || 'normal').toLowerCase();
  if (key === 'thin') return 0;
  if (key === 'slight_belly') return 1;
  if (key === 'big_belly') return 1.5;
  return 0.5;
};

const clampShirtFrontSplitGlobal = (frontValue, totalHalf, min = 0.75) => {
  const safeTotalHalf = Number(totalHalf || 0);
  const safeFront = Number(frontValue || 0);
  const maxFront = Math.max(min, safeTotalHalf - min);
  return roundToEighth(Math.min(maxFront, Math.max(min, safeFront)));
};

const isShirtGeometryV2 = (geometryVersion = '') => {
  const normalized = String(geometryVersion || '').toLowerCase();
  return normalized === 'shirt_geom_v2' || normalized === 'shirt_geom_v3' || normalized === 'shirt_geom_v4';
};

const resolvePortlyFrontSplit = ({
  total = 0,
  portlyType = 'normal',
  dartIntake = 0,
  applyDartCompensation = false,
}) => {
  const safeTotal = Number(total || 0);
  const safeDart = Number(dartIntake || 0);
  const delta = getShirtPortlyDelta(portlyType);
  const targetHalf = safeTotal / 2;
  const rawHalfTarget = targetHalf + (applyDartCompensation ? safeDart : 0);
  const frontFromPortly = (targetHalf / 2) + (delta / 2) + (applyDartCompensation ? safeDart / 2 : 0);
  return clampShirtFrontSplitGlobal(frontFromPortly, rawHalfTarget);
};

const applyShirtPortlySplitRule = (baseMeasurements = {}, options = {}) => {
  const { geometryVersion = '' } = options;
  const chest = Number(baseMeasurements?.chest || 0);
  const waist = Number(baseMeasurements?.waist || 0);
  const belly = Number(baseMeasurements?.belly || 0);
  const seat = Number(baseMeasurements?.seat || 0);
  const waistDart = Number(baseMeasurements?.dartWidthWaist || 0);
  const bellyDart = Number(baseMeasurements?.dartWidthBelly || 0);
  const portlyType = String(baseMeasurements?.portlyType || 'normal').toLowerCase();
  const shouldCompensateDart = isShirtGeometryV2(geometryVersion);

  return {
    ...baseMeasurements,
    frontChestSplit: resolvePortlyFrontSplit({ total: chest, portlyType }),
    frontWaistSplit: resolvePortlyFrontSplit({
      total: waist,
      portlyType,
      dartIntake: waistDart,
      applyDartCompensation: shouldCompensateDart,
    }),
    frontBellySplit: resolvePortlyFrontSplit({
      total: belly,
      portlyType,
      dartIntake: bellyDart,
      applyDartCompensation: shouldCompensateDart,
    }),
    frontSeatSplit: resolvePortlyFrontSplit({ total: seat, portlyType }),
  };
};

const SHIRT_NEW_DRAFT_SLEEVE_PRESET_VALUES = {
  sleeveFrontCrownCtrlDx: 2.375,
  sleeveFrontCrownCtrlDy: 0,
  sleeveFrontUnderarmCtrlDx: -1.75,
  sleeveFrontUnderarmCtrlDy: 0.625,
  sleeveBackCrownCtrlDx: -2.25,
  sleeveBackCrownCtrlDy: -0.25,
  sleeveBackUnderarmCtrlDx: 2.5,
  sleeveBackUnderarmCtrlDy: 0.5,
  sleeveFrontPitchPercent: 0.6,
  sleeveBackPitchPercent: 0.4,
};

const SHIRT_LEGACY_SLEEVE_LOAD_VALUES = {
  sleeveFrontCrownCtrlDx: 0,
  sleeveFrontCrownCtrlDy: 0,
  sleeveFrontUnderarmCtrlDx: 0,
  sleeveFrontUnderarmCtrlDy: 0,
  sleeveBackCrownCtrlDx: 0,
  sleeveBackCrownCtrlDy: 0,
  sleeveBackUnderarmCtrlDx: 0,
  sleeveBackUnderarmCtrlDy: 0,
  sleeveFrontPitchPercent: 0.55,
  sleeveBackPitchPercent: 0.45,
};

const applyShirtNewDraftSleevePreset = (baseMeasurements = {}) => ({
  ...baseMeasurements,
  ...SHIRT_NEW_DRAFT_SLEEVE_PRESET_VALUES,
});

const preserveLegacyShirtSleeveValuesForLoadedPattern = (baseMeasurements = {}, storedMeasurements = {}) => {
  const stored = storedMeasurements && typeof storedMeasurements === 'object'
    ? storedMeasurements
    : {};

  return Object.entries(SHIRT_LEGACY_SLEEVE_LOAD_VALUES).reduce((acc, [key, value]) => {
    if (!Object.prototype.hasOwnProperty.call(stored, key)) {
      acc[key] = value;
    }
    return acc;
  }, { ...baseMeasurements });
};

const normalizeJacketPointDeltaMap = (rawPointDeltas = {}) => {
  const source = (rawPointDeltas && typeof rawPointDeltas === 'object' && rawPointDeltas.deltas && typeof rawPointDeltas.deltas === 'object')
    ? rawPointDeltas.deltas
    : rawPointDeltas;

  if (!source || typeof source !== 'object') return {};

  return Object.entries(source).reduce((acc, [pointName, delta]) => {
    if (!pointName || !delta || typeof delta !== 'object') return acc;
    const x = roundToSixteenth(Number(delta.x) || 0);
    const y = roundToSixteenth(Number(delta.y) || 0);
    if (Math.abs(x) < 1e-8 && Math.abs(y) < 1e-8) return acc;
    acc[pointName] = { x, y };
    return acc;
  }, {});
};

const mergeJacketPointDeltaMaps = (baseMap = {}, deltaMap = {}) => {
  const base = normalizeJacketPointDeltaMap(baseMap);
  const delta = normalizeJacketPointDeltaMap(deltaMap);
  const merged = { ...base };

  Object.entries(delta).forEach(([pointName, pointDelta]) => {
    const existing = merged[pointName] || { x: 0, y: 0 };
    const next = {
      x: roundToSixteenth((Number(existing.x) || 0) + (Number(pointDelta?.x) || 0)),
      y: roundToSixteenth((Number(existing.y) || 0) + (Number(pointDelta?.y) || 0)),
    };

    if (Math.abs(next.x) < 1e-8 && Math.abs(next.y) < 1e-8) {
      delete merged[pointName];
    } else {
      merged[pointName] = next;
    }
  });

  return merged;
};

const parseJacketPointDeltasPayload = (pointDeltasPayload = {}) => {
  const payload = pointDeltasPayload && typeof pointDeltasPayload === 'object'
    ? pointDeltasPayload
    : {};

  const hasStructuredPayload = Object.prototype.hasOwnProperty.call(payload, 'deltas')
    || Object.prototype.hasOwnProperty.call(payload, 'baseline_deltas');

  const deltas = normalizeJacketPointDeltaMap(hasStructuredPayload ? payload.deltas : payload);
  const baselineDeltas = normalizeJacketPointDeltaMap(payload.baseline_deltas || {});
  const mode = payload?.mode === 'free' ? 'free' : 'fixed';

  return {
    mode,
    deltas,
    baselineDeltas,
  };
};

const resolveJacketCreationMode = (pointDeltasPayload) => {
  return parseJacketPointDeltasPayload(pointDeltasPayload).mode;
};

const resolveEffectiveJacketPointDeltas = ({ mode = 'fixed', deltas = {}, baselineDeltas = {} } = {}) => {
  if (mode === 'free') {
    return mergeJacketPointDeltaMaps(baselineDeltas, deltas);
  }
  return normalizeJacketPointDeltaMap(baselineDeltas);
};

const parseTrouserPointDeltasPayload = (pointDeltasPayload = {}) => parseJacketPointDeltasPayload(pointDeltasPayload);
const resolveTrouserCreationMode = (pointDeltasPayload) => parseTrouserPointDeltasPayload(pointDeltasPayload).mode;
const resolveEffectiveTrouserPointDeltas = (payload = {}) => resolveEffectiveJacketPointDeltas(payload);
const parseShirtPointDeltasPayload = (pointDeltasPayload = {}) => parseJacketPointDeltasPayload(pointDeltasPayload);
const resolveShirtCreationMode = (pointDeltasPayload) => parseShirtPointDeltasPayload(pointDeltasPayload).mode;
const resolveEffectiveShirtPointDeltas = (payload = {}) => resolveEffectiveJacketPointDeltas(payload);

const JACKET_COLLAR_FREE_POINT_KEYS = ['UCOL0', 'UCOL1', 'UCOL2', 'TCOL1', 'TCOL2'];
const JACKET_LAPEL_FREE_POINT_KEYS = ['FND', 'LP0', 'LP1', 'LP2', 'LP3', 'GP', 'CP0', 'LB0', 'LB1'];
const JACKET_BUTTON_FREE_POINT_KEYS = ['BTN_WAIST', 'BTN_LOWER', 'BTN_ROLL', 'DB_TOP_BTN', 'DB_BTN_UPPER_EDGE', 'DB_BTN_UPPER_INNER', 'DB_BTN_LOWER_EDGE', 'DB_BTN_LOWER_INNER'];

const mapNamedPointsToModelMap = (patternPayload = null) => {
  const namedPoints = Array.isArray(patternPayload?.namedPoints) ? patternPayload.namedPoints : [];
  return namedPoints.reduce((acc, point) => {
    const pointName = String(point?.name || '').trim();
    if (!pointName) return acc;

    const x = Number(point?.x);
    const yCanvas = Number(point?.y);
    if (!Number.isFinite(x) || !Number.isFinite(yCanvas)) return acc;

    acc[pointName] = {
      x,
      y: -yCanvas,
    };
    return acc;
  }, {});
};

const mapCurveHandlesToModelMap = (patternPayload = null) => {
  const curveHandles = Array.isArray(patternPayload?.curveHandles) ? patternPayload.curveHandles : [];
  return curveHandles.reduce((acc, handle) => {
    const handleId = String(handle?.id || '').trim();
    const xKey = String(handle?.xKey || '').trim();
    const yKey = String(handle?.yKey || '').trim();
    const x = Number(handle?.point?.x);
    const yCanvas = Number(handle?.point?.y);
    if (!handleId || !xKey || !yKey || !Number.isFinite(x) || !Number.isFinite(yCanvas)) return acc;

    acc[handleId] = {
      id: handleId,
      xKey,
      yKey,
      point: {
        x,
        y: -yCanvas,
      },
    };
    return acc;
  }, {});
};

const resolveJacketCurveHandleMeasurementPatch = (targetPatternPayload, basePatternPayload, currentMeasurements = {}) => {
  const targetHandles = mapCurveHandlesToModelMap(targetPatternPayload);
  const baseHandles = mapCurveHandlesToModelMap(basePatternPayload);
  const patch = {};

  Object.entries(targetHandles).forEach(([handleId, targetHandle]) => {
    const baseHandle = baseHandles[handleId];
    if (!baseHandle) return;

    const dx = roundToSixteenth((targetHandle.point.x || 0) - (baseHandle.point.x || 0));
    const dy = roundToSixteenth((targetHandle.point.y || 0) - (baseHandle.point.y || 0));
    if (Math.abs(dx) < 1e-8 && Math.abs(dy) < 1e-8) return;

    const currentX = Number(currentMeasurements?.[targetHandle.xKey]) || 0;
    const currentY = Number(currentMeasurements?.[targetHandle.yKey]) || 0;
    patch[targetHandle.xKey] = roundToSixteenth(currentX + dx);
    patch[targetHandle.yKey] = roundToSixteenth(currentY + dy);
  });

  return patch;
};

const enrichJacketSnapshotButtonData = (patternPayload = null, currentMeasurements = {}) => {
  if (String(patternPayload?.garmentType || '').toLowerCase() !== 'jacket') return patternPayload;

  const pointMap = mapNamedPointsToModelMap(patternPayload);
  const jacketButtonPlan = buildJacketFrontButtons(pointMap, currentMeasurements);

  if (!jacketButtonPlan.buttons.length && !jacketButtonPlan.buttonholes.length) {
    return patternPayload;
  }

  return {
    ...patternPayload,
    buttons: jacketButtonPlan.buttons,
    buttonholes: jacketButtonPlan.buttonholes,
    matchingSummary: {
      ...(patternPayload?.matchingSummary || {}),
      jacketButtonLayout: jacketButtonPlan.layout,
      jacketButtonCount: jacketButtonPlan.buttons.length,
      jacketButtonholeLength: Number(jacketButtonPlan.buttonholeLength.toFixed(3)),
      jacketLowerButtonDistance: Number(jacketButtonPlan.lowerButtonDistance.toFixed(3)),
      jacketUpperButtonDistance: Number(jacketButtonPlan.upperButtonDistance.toFixed(3)),
      jacketButtonEdgeOffset: Number(jacketButtonPlan.edgeOffset.toFixed(3)),
    },
  };
};

const AUTOSAVE_DEBOUNCE_MS = 1500;
const AUTOSAVE_INTERVAL_MS = 10000;
const DRAWN_STEPPER_AUTOSAVE_HOLD_MS = 4000;
const SHIRT_SIDE_SEAM_OFFSET_KEYS = [
  'frontChestWaistCtrlDx',
  'frontChestWaistCtrlDy',
  'frontWaistBellyCtrlDx',
  'frontWaistBellyCtrlDy',
  'frontBellySeatCtrlDx',
  'frontBellySeatCtrlDy',
  'frontJacketHemCurveCtrlDx',
  'frontJacketHemCurveCtrlDy',
  'backChestWaistCtrlDx',
  'backChestWaistCtrlDy',
  'backWaistBellyCtrlDx',
  'backWaistBellyCtrlDy',
  'backBellySeatCtrlDx',
  'backBellySeatCtrlDy',
];

const SHIRT_SIDE_SEAM_HANDLE_CHAINS = {
  front: ['front-chest-waist-ctrl', 'front-waist-belly-ctrl', 'front-belly-seat-ctrl'],
  back: ['back-chest-waist-ctrl', 'back-waist-belly-ctrl', 'back-belly-seat-ctrl'],
};

const SHIRT_YOKE_DART_OFFSET_KEYS = {
  panel: ['yokeDartYokeCtrlDx', 'yokeDartYokeCtrlDy'],
  back: ['yokeDartBackCtrlDx', 'yokeDartBackCtrlDy'],
};

const SHIRT_ARMHOLE_OFFSET_KEYS = [
  'frontArmPart1CtrlDx',
  'frontArmPart1CtrlDy',
  'frontArmPart2CtrlDx',
  'frontArmPart2CtrlDy',
  'backArmPart1CtrlDx',
  'backArmPart1CtrlDy',
  'backArmPart2CtrlDx',
  'backArmPart2CtrlDy',
  'backYokeArmSplitCtrlDx',
  'backYokeArmSplitCtrlDy',
  'backPanelYokeSplitCtrlDx',
  'backPanelYokeSplitCtrlDy',
];

const SHIRT_SLEEVE_FP_BP_OFFSET_KEYS = [
  'sleeveFrontUnderarmCtrlDx',
  'sleeveFrontUnderarmCtrlDy',
  'sleeveFrontCrownCtrlDx',
  'sleeveFrontCrownCtrlDy',
  'sleeveBackCrownCtrlDx',
  'sleeveBackCrownCtrlDy',
  'sleeveBackUnderarmCtrlDx',
  'sleeveBackUnderarmCtrlDy',
];

const reflectPoint = (point, center) => ({
  x: 2 * center.x - point.x,
  y: 2 * center.y - point.y,
});

const quadraticPointAt = (p0, c, p1, t) => {
  const mt = 1 - t;
  return {
    x: (mt * mt * p0.x) + (2 * mt * t * c.x) + (t * t * p1.x),
    y: (mt * mt * p0.y) + (2 * mt * t * c.y) + (t * t * p1.y),
  };
};

const quadraticLength = (p0, c, p1, steps = 72) => {
  let length = 0;
  let prev = p0;

  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;
    const curr = quadraticPointAt(p0, c, p1, t);
    length += Math.hypot(curr.x - prev.x, curr.y - prev.y);
    prev = curr;
  }

  return length;
};

const buildHandleMap = (curveHandles = []) => new Map((curveHandles || []).map((handle) => [handle.id, handle]));

const computeCardinalQuadraticControls = (points = [], _tension = 0.5, options = {}) => {
  if (!Array.isArray(points) || points.length < 4) return [];

  const {
    chestWaistDepthFactor = 1,
    interiorSign: providedInteriorSign,
  } = options;

  const [pChest, pWaist, pBelly, pSeat] = points;

  const normalize = (vector, fallback = { x: 0, y: 1 }) => {
    const mag = Math.hypot(vector.x, vector.y);
    if (!mag || !Number.isFinite(mag)) return fallback;
    return { x: vector.x / mag, y: vector.y / mag };
  };

  const downSign = Math.sign(pSeat.y - pChest.y) || 1;
  const interiorSign = providedInteriorSign || Math.sign((pWaist?.x || 0) - (pChest?.x || 0)) || 1;
  const outsideSign = -interiorSign;

  const chestWaistLen = Math.hypot(pWaist.x - pChest.x, pWaist.y - pChest.y);
  const bellySeatLen = Math.hypot(pSeat.x - pBelly.x, pSeat.y - pBelly.y);

  const waistHandleLength = Math.max(
    chestWaistLen * 0.16,
    Math.min(chestWaistLen * 0.62, chestWaistLen * (0.28 + ((Math.max(1, chestWaistDepthFactor) - 1) * 0.12))),
  );
  const bellyHandleLength = Math.max(
    bellySeatLen * 0.14,
    Math.min(bellySeatLen * 0.56, bellySeatLen * 0.24),
  );

  const yCenter = (pWaist.y + pBelly.y) / 2;
  const xCenter = (pWaist.x + pBelly.x) / 2;
  const minWBX = Math.min(pWaist.x, pBelly.x);
  const maxWBX = Math.max(pWaist.x, pBelly.x);
  const searchSpan = Math.max(0.18, Math.abs(pBelly.x - pWaist.x) * 0.45);
  const preferredWaistBellyX = pWaist.x + ((pBelly.x - pWaist.x) * 0.42);

  const targetChestInwardX = pWaist.x + interiorSign * (
    Math.max(0, chestWaistDepthFactor - 1) * Math.max(0.125, chestWaistLen * 0.22)
  );

  const targetBellyOutwardX = pBelly.x + outsideSign * clampValue(
    Math.abs(pSeat.x - pBelly.x) * 0.55,
    0.12,
    0.55,
  );

  const targetBellyOutwardY = pBelly.y + ((pSeat.y - pBelly.y) * 0.55);

  let best = null;

  for (let i = 0; i <= 24; i += 1) {
    const t = i / 24;
    const xCandidate = clampValue(xCenter + ((t - 0.5) * 2 * searchSpan), minWBX, maxWBX);
    const cWaistBelly = { x: xCandidate, y: yCenter };

    const waistDirOut = normalize(
      {
        x: cWaistBelly.x - pWaist.x,
        y: cWaistBelly.y - pWaist.y,
      },
      { x: 0, y: downSign },
    );

    const cChestWaist = {
      x: pWaist.x - waistDirOut.x * waistHandleLength,
      y: pWaist.y - waistDirOut.y * waistHandleLength,
    };

    const bellyDirIn = normalize(
      {
        x: pBelly.x - cWaistBelly.x,
        y: pBelly.y - cWaistBelly.y,
      },
      { x: 0, y: downSign },
    );

    const cBellySeat = {
      x: pBelly.x + bellyDirIn.x * bellyHandleLength,
      y: pBelly.y + bellyDirIn.y * bellyHandleLength,
    };

    const score = (
      (Math.abs(cWaistBelly.x - preferredWaistBellyX) * 0.45)
      + (Math.abs(cChestWaist.x - targetChestInwardX) * 0.3)
      + (Math.abs(cBellySeat.x - targetBellyOutwardX) * 0.2)
      + (Math.abs(cBellySeat.y - targetBellyOutwardY) * 0.05)
    );

    if (!best || score < best.score) {
      best = {
        score,
        controls: [cChestWaist, cWaistBelly, cBellySeat],
      };
    }
  }

  return best?.controls || [];
};

const SHIRT_CURVE_HANDLE_KEYS = [
  'backNeckCtrlDx',
  'backNeckCtrlDy',
  'frontNeckCtrlDx',
  'frontNeckCtrlDy',
  'backArmPart1CtrlDx',
  'backArmPart1CtrlDy',
  'backArmPart2CtrlDx',
  'backArmPart2CtrlDy',
  'frontArmPart1CtrlDx',
  'frontArmPart1CtrlDy',
  'frontArmPart2CtrlDx',
  'frontArmPart2CtrlDy',
  'backHemCtrl1Dx',
  'backHemCtrl1Dy',
  'backHemCtrl2Dx',
  'backHemCtrl2Dy',
  'frontHemCtrl1Dx',
  'frontHemCtrl1Dy',
  'frontHemCtrl2Dx',
  'frontHemCtrl2Dy',
  'frontChestWaistCtrlDx',
  'frontChestWaistCtrlDy',
  'frontWaistBellyCtrlDx',
  'frontWaistBellyCtrlDy',
  'frontBellySeatCtrlDx',
  'frontBellySeatCtrlDy',
  'backChestWaistCtrlDx',
  'backChestWaistCtrlDy',
  'backWaistBellyCtrlDx',
  'backWaistBellyCtrlDy',
  'backBellySeatCtrlDx',
  'backBellySeatCtrlDy',
  'yokeDartYokeCtrlDx',
  'yokeDartYokeCtrlDy',
  'yokeDartBackCtrlDx',
  'yokeDartBackCtrlDy',
  'backYokeArmSplitCtrlDx',
  'backYokeArmSplitCtrlDy',
  'backPanelYokeSplitCtrlDx',
  'backPanelYokeSplitCtrlDy',
  'sleeveFrontUnderarmCtrlDx',
  'sleeveFrontUnderarmCtrlDy',
  'sleeveFrontCrownCtrlDx',
  'sleeveFrontCrownCtrlDy',
  'sleeveBackCrownCtrlDx',
  'sleeveBackCrownCtrlDy',
  'sleeveBackUnderarmCtrlDx',
  'sleeveBackUnderarmCtrlDy',
  'sleeveFrontSideUpperCtrlDx',
  'sleeveFrontSideUpperCtrlDy',
  'sleeveFrontSideLowerCtrlDx',
  'sleeveFrontSideLowerCtrlDy',
  'sleeveBackSideUpperCtrlDx',
  'sleeveBackSideUpperCtrlDy',
  'sleeveBackSideLowerCtrlDx',
  'sleeveBackSideLowerCtrlDy',
  'collarBandTopStartCtrlDx',
  'collarBandTopStartCtrlDy',
  'collarBandTopEndCtrlDx',
  'collarBandTopEndCtrlDy',
  'collarBandBottomStartCtrlDx',
  'collarBandBottomStartCtrlDy',
  'collarBandBottomEndCtrlDx',
  'collarBandBottomEndCtrlDy',
  'collarBandNeckCtrlDx',
  'collarBandNeckCtrlDy',
  'collarLeafTopStartCtrlDx',
  'collarLeafTopStartCtrlDy',
  'collarLeafTopEndCtrlDx',
  'collarLeafTopEndCtrlDy',
];

const SHIRT_COLLAR_TOP_HANDLE_KEYS = [
  'collarBandTopStartCtrlDx',
  'collarBandTopStartCtrlDy',
  'collarBandTopEndCtrlDx',
  'collarBandTopEndCtrlDy',
];

const JACKET_CURVE_HANDLE_KEYS = [
  'frontNeckCtrlDx',
  'frontNeckCtrlDy',
  'jacketLapelFallCurveCtrlDx',
  'jacketLapelFallCurveCtrlDy',
  'jacketRoundedNotchCurveCtrlDx',
  'jacketRoundedNotchCurveCtrlDy',
  'jacketRoundedNotchCollarCurveCtrlDx',
  'jacketRoundedNotchCollarCurveCtrlDy',
  'jacketPeakLapelFallCurveCtrlDx',
  'jacketPeakLapelFallCurveCtrlDy',
  'shawlTcol2Lp0CtrlDx',
  'shawlTcol2Lp0CtrlDy',
  'shawlLp0Rl1CtrlDx',
  'shawlLp0Rl1CtrlDy',
  'jacketRl2Dx',
  'jacketRl2Dy',
  'diamondShawlTcol2Rl2CtrlDx',
  'diamondShawlTcol2Rl2CtrlDy',
  'underCollarCurveStartCtrlDx',
  'underCollarCurveStartCtrlDy',
  'underCollarCurveEndCtrlDx',
  'underCollarCurveEndCtrlDy',
  'topCollarCurveStartCtrlDx',
  'topCollarCurveStartCtrlDy',
  'topCollarCurveEndCtrlDx',
  'topCollarCurveEndCtrlDy',
  'frontChestWaistCtrlDx',
  'frontChestWaistCtrlDy',
  'frontWaistBellyCtrlDx',
  'frontWaistBellyCtrlDy',
  'frontBellySeatCtrlDx',
  'frontBellySeatCtrlDy',
  'backChestWaistCtrlDx',
  'backChestWaistCtrlDy',
  'backWaistBellyCtrlDx',
  'backWaistBellyCtrlDy',
  'backBellySeatCtrlDx',
  'backBellySeatCtrlDy',
  'sideFrontArmCtrlDx',
  'sideFrontArmCtrlDy',
  'sideFrontChestWaistCtrlDx',
  'sideFrontChestWaistCtrlDy',
  'sideFrontWaistBellyCtrlDx',
  'sideFrontWaistBellyCtrlDy',
  'sideFrontBellySeatCtrlDx',
  'sideFrontBellySeatCtrlDy',
  'sideBackArmCtrlDx',
  'sideBackArmCtrlDy',
  'sideBackChestWaistCtrlDx',
  'sideBackChestWaistCtrlDy',
  'sideBackWaistBellyCtrlDx',
  'sideBackWaistBellyCtrlDy',
  'sideBackBellySeatCtrlDx',
  'sideBackBellySeatCtrlDy',
  'jacketRl1Dx',
  'jacketRl1Dy',
  'jacketLp1Dx',
  'jacketLp1Dy',
  'jacketLp0Dx',
  'jacketLp0Dy',
  'jacketCpoDx',
  'jacketCpoDy',
  'jacketLboDx',
  'jacketLboDy',
];

const clampValue = (value, min, max) => Math.min(max, Math.max(min, value));

export default function PatternEngine() {
  const { user, token, logout } = useAuth();
  const { siteMode, updateSiteMode, meta: siteModeMeta, refreshSiteMode } = useSiteMode();
  const trouserDefaults = getDefaultMeasurements('trouser');

  const resolvePatternGeometryVersion = (pattern) => {
    const garmentType = pattern?.garment_type || 'trouser';
    return resolveGeometryVersion(garmentType, pattern || {});
  };

  const resolvePatternFormulaVersion = (pattern) => {
    const garmentType = pattern?.garment_type || 'trouser';
    return resolveFormulaVersion(garmentType, pattern || {});
  };

  const applyResolvedLabelInsets = (pieces = [], fallbackPieces = []) => {
    const fallbackMap = new Map((fallbackPieces || []).map((piece) => [piece.key, piece]));
    return (pieces || []).map((piece) => {
      const pieceWithInset = {
        ...piece,
        labelInsetInches: resolvePieceLabelInsetInches(
          piece.key,
          piece.labelInsetInches ?? fallbackMap.get(piece.key)?.labelInsetInches,
        ),
        labelOffsetXmm: Number(piece.labelOffsetXmm ?? fallbackMap.get(piece.key)?.labelOffsetXmm ?? 0),
        labelOffsetYmm: Number(piece.labelOffsetYmm ?? fallbackMap.get(piece.key)?.labelOffsetYmm ?? 0),
      };
      return resolvePiecePrintSettings(pieceWithInset, fallbackMap.get(piece.key) || DEFAULT_PRINT_PIECE_SETTINGS);
    });
  };

  const normalizeComparisonPatternData = (rawPatternData, garmentType, versionMeta = {}) => {
    if (!rawPatternData || typeof rawPatternData !== 'object') return null;
    const normalizedPatternData = {
      ...rawPatternData,
      garmentType: rawPatternData.garmentType || rawPatternData.garment_type || garmentType,
      frontPath: rawPatternData.frontPath || rawPatternData.front_path || '',
      sidePath: rawPatternData.sidePath || rawPatternData.side_path || '',
      backPath: rawPatternData.backPath || rawPatternData.back_path || '',
      yokePath: rawPatternData.yokePath || rawPatternData.yoke_path || '',
      collarBandPath: rawPatternData.collarBandPath || rawPatternData.collar_band_path || '',
      collarBandLayoutPath: rawPatternData.collarBandLayoutPath || rawPatternData.collar_band_layout_path || '',
      collarLeafLayoutPath: rawPatternData.collarLeafLayoutPath || rawPatternData.collar_leaf_layout_path || '',
      collarLeafPath: rawPatternData.collarLeafPath || rawPatternData.collar_leaf_path || '',
      sleevePath: rawPatternData.sleevePath || rawPatternData.sleeve_path || '',
      topSleevePath: rawPatternData.topSleevePath || rawPatternData.top_sleeve_path || '',
      underSleevePath: rawPatternData.underSleevePath || rawPatternData.under_sleeve_path || '',
      fCreasePath: rawPatternData.fCreasePath || rawPatternData.f_crease_path || '',
      bCreasePath: rawPatternData.bCreasePath || rawPatternData.b_crease_path || '',
      dartPath: rawPatternData.dartPath || rawPatternData.dart_path || '',
      frontDartPath: rawPatternData.frontDartPath || rawPatternData.front_dart_path || '',
      formulaVersion: rawPatternData.formulaVersion || rawPatternData.formula_version || versionMeta.formulaVersion,
      geometryVersion: rawPatternData.geometryVersion || rawPatternData.geometry_version || versionMeta.geometryVersion,
    };
    const isTrouserV4Snapshot = String(normalizedPatternData.garmentType || '').toLowerCase() === 'trouser'
      && (
        String(normalizedPatternData.formulaVersion || '').toLowerCase() === 'siy_v4'
        || String(normalizedPatternData.geometryVersion || '').toLowerCase() === 'trouser_geom_v4'
      );

    let normalizedSnapshot = normalizedPatternData;

    if (isTrouserV4Snapshot) {
      const namedPoints = Array.isArray(normalizedPatternData.namedPoints) ? normalizedPatternData.namedPoints : [];
      const backHipCenter = namedPoints.find((point) => point?.name === 'BHC');
      const backWaistCenter = namedPoints.find((point) => point?.name === 'BWC');

      if (backHipCenter && backWaistCenter) {
        const curveHandles = Array.isArray(normalizedPatternData.curveHandles) ? normalizedPatternData.curveHandles : [];
        const curveSegments = Array.isArray(normalizedPatternData.curveSegments) ? normalizedPatternData.curveSegments : [];

        const hasBackWaistCenterHandle = curveHandles.some((handle) => handle?.id === 'back-waist-center-curve-ctrl');
        const hasBackWaistCenterSegment = curveSegments.some((segment) => segment?.id === 'back-waist-center-curve');

        if (!hasBackWaistCenterHandle || !hasBackWaistCenterSegment) {
          const midpoint = {
            x: (Number(backHipCenter.x) + Number(backWaistCenter.x)) / 2,
            y: (Number(backHipCenter.y) + Number(backWaistCenter.y)) / 2,
          };

          normalizedSnapshot = {
            ...normalizedPatternData,
            curveHandles: hasBackWaistCenterHandle ? curveHandles : [
              ...curveHandles,
              {
                id: 'back-waist-center-curve-ctrl',
                curveId: 'back-waist-center-curve',
                label: 'Back Waist Center→Seat',
                xKey: 'backWaistCenterCurveCtrlDx',
                yKey: 'backWaistCenterCurveCtrlDy',
                point: midpoint,
                segmentStart: backHipCenter,
                segmentEnd: backWaistCenter,
                color: '#dc2626',
              },
            ],
            curveSegments: hasBackWaistCenterSegment ? curveSegments : [
              ...curveSegments,
              {
                id: 'back-waist-center-curve',
                label: 'Back Waist Center→Seat',
                d: `M ${Number(backHipCenter.x)},${Number(backHipCenter.y)} Q ${midpoint.x},${midpoint.y} ${Number(backWaistCenter.x)},${Number(backWaistCenter.y)}`,
                color: '#dc2626',
              },
            ],
          };
        }
      }
    }

    if (String(normalizedPatternData.garmentType || '').toLowerCase() === 'jacket') {
      const namedPoints = Array.isArray(normalizedSnapshot.namedPoints) ? normalizedSnapshot.namedPoints : [];
      const pointMap = namedPoints.reduce((acc, point) => {
        if (point?.name) acc[point.name] = point;
        return acc;
      }, {});
      const slt = pointMap.SLT;
      const hb = pointMap.HB;
      const tshf = pointMap.TSHF;

      if (slt && hb && tshf && !normalizedSnapshot.sleeveLengthGuide) {
        const midpointX = (Number(hb.x) + Number(tshf.x)) / 2;
        const midpointY = (Number(hb.y) + Number(tshf.y)) / 2;
        const lengthValue = Number(Math.hypot(midpointX - Number(slt.x), midpointY - Number(slt.y)).toFixed(3));
        normalizedSnapshot = {
          ...normalizedSnapshot,
          sleeveLengthPath: normalizedSnapshot.sleeveLengthPath || `M ${Number(slt.x)} ${-Number(slt.y)} L ${midpointX} ${-midpointY}`,
          sleeveLengthGuide: {
            id: 'jacket-top-sleeve-length-measurement',
            p1: { x: Number(slt.x), y: Number(slt.y) },
            p2: { x: midpointX, y: midpointY },
            label: 'Sleeve Length',
            type: 'measurement',
            showLabel: true,
            showLabelInPreview: false,
            showLabelInPdf: false,
            value: lengthValue,
            labelText: `${lengthValue.toFixed(2)}"`,
            labelOffset: 0.26,
            labelOffsetMm: 3.4,
            piece: 'topSleeve',
          },
          matchingSummary: {
            ...(normalizedSnapshot.matchingSummary || {}),
            jacketSleeveLengthMidpointDrawn: normalizedSnapshot?.matchingSummary?.jacketSleeveLengthMidpointDrawn ?? lengthValue,
          },
        };
      }

      const normalizedLapelType = String(
        normalizedSnapshot?.lapelSummary?.lapelType
        || normalizedSnapshot?.lapelType
        || normalizedPatternData?.lapelSummary?.lapelType
        || normalizedPatternData?.lapelType
        || ''
      ).toLowerCase();

      if (normalizedLapelType === 'rounded_notch') {
        const cp0Mirror = pointMap.CP0_M;
        const tcol2 = pointMap.TCOL2;
        const gpMirror = pointMap.GP_M;
        const hasCp1Mirror = Boolean(pointMap.CP1_M);
        const hasCp2Mirror = Boolean(pointMap.CP2_M);

        const buildOffsetPoint = (name, startPoint, endPoint, distance) => {
          const sx = Number(startPoint?.x);
          const sy = Number(startPoint?.y);
          const ex = Number(endPoint?.x);
          const ey = Number(endPoint?.y);
          if (![sx, sy, ex, ey].every(Number.isFinite)) return null;
          const dx = ex - sx;
          const dy = ey - sy;
          const length = Math.hypot(dx, dy);
          if (length < 1e-8) return null;
          return {
            name,
            x: Number((sx + ((dx / length) * distance)).toFixed(4)),
            y: Number((sy + ((dy / length) * distance)).toFixed(4)),
            piece: 'front',
            selectable: false,
          };
        };

        const nextNamedPoints = [...namedPoints];
        const cp2Mirror = !hasCp2Mirror && cp0Mirror && tcol2
          ? buildOffsetPoint('CP2_M', cp0Mirror, tcol2, 0.5)
          : null;
        const cp1Mirror = !hasCp1Mirror && cp0Mirror && gpMirror
          ? buildOffsetPoint('CP1_M', cp0Mirror, gpMirror, 0.5)
          : null;

        if (cp2Mirror) nextNamedPoints.push(cp2Mirror);
        if (cp1Mirror) nextNamedPoints.push(cp1Mirror);

        if (cp2Mirror || cp1Mirror) {
          normalizedSnapshot = {
            ...normalizedSnapshot,
            namedPoints: nextNamedPoints,
          };
        }
      }
    }

    return normalizedSnapshot;
  };

  const hasRenderablePatternSnapshot = (patternSnapshot) => Boolean(
    patternSnapshot?.frontPath
    || patternSnapshot?.sidePath
    || patternSnapshot?.backPath
    || patternSnapshot?.yokePath
    || patternSnapshot?.collarBandPath
    || patternSnapshot?.sleevePath
    || patternSnapshot?.topSleevePath
    || patternSnapshot?.underSleevePath
  );

  const reconcileJacketMeasurementsToSnapshotGeometry = (patternLike = null, measurementSource = {}) => {
    if (String(patternLike?.garment_type || '').toLowerCase() !== 'jacket') return measurementSource;

    const rawPatternPayload = patternLike?.pattern_data || patternLike?.patternData || patternLike?.data;
    if (!rawPatternPayload) return measurementSource;

    const formulaVersion = resolvePatternFormulaVersion(patternLike);
    const geometryVersion = resolvePatternGeometryVersion(patternLike);
    const normalizedSnapshot = normalizeComparisonPatternData(rawPatternPayload, 'jacket', {
      formulaVersion,
      geometryVersion,
    });
    if (!hasRenderablePatternSnapshot(normalizedSnapshot)) return measurementSource;

    const parsedPointDeltas = parseJacketPointDeltasPayload(patternLike?.point_deltas || {});
    const effectivePointDeltas = resolveEffectiveJacketPointDeltas(parsedPointDeltas);
    const regenerated = generatePattern(
      measurementSource,
      {
        backCrotchScoopX: measurementSource?.backCrotchScoopX,
        backCrotchScoopY: measurementSource?.backCrotchScoopY,
        formulaVersion,
        geometryVersion,
        freeModeActive: parsedPointDeltas.mode === 'free',
        pointDeltas: effectivePointDeltas,
      },
      'jacket',
    );

    const curveHandlePatch = resolveJacketCurveHandleMeasurementPatch(normalizedSnapshot, regenerated, measurementSource);
    if (!Object.keys(curveHandlePatch).length) return measurementSource;

    return {
      ...measurementSource,
      ...curveHandlePatch,
    };
  };

  const REQUESTED_MEASUREMENT_KEYS_BY_GARMENT = {
    trouser: ['waist', 'seat', 'rise', 'outseam', 'thigh', 'knee', 'hem'],
    shirt: [
      'neck',
      'chest',
      'bust',
      'waist',
      'belly',
      'seat',
      'shoulder',
      'armholeScyeDepth',
      'acrossBack',
      'acrossFront',
      'shirtLengthBack',
      'sleeveLength',
      'shortSleeveLength',
      'bicepWidth',
      'elbowWidth',
      'maxCuffWidth',
      'shortSleeveHem',
    ],
    jacket: [
      'neck',
      'chest',
      'waist',
      'belly',
      'seat',
      'shoulder',
      'armholeScyeDepth',
      'acrossBack',
      'acrossFront',
      'jacketLengthBack',
      'jacketLengthFront',
      'jacketSleeveLength',
      'jacketSleeveBicep',
      'jacketSleeveElbow',
      'jacketSleeveHem',
    ],
  };

  const pickRequestedMeasurementValues = (garmentType = 'trouser', measurementSource = {}, fallbackSource = {}) => {
    const normalizedGarmentType = String(garmentType || 'trouser').toLowerCase();
    const keys = REQUESTED_MEASUREMENT_KEYS_BY_GARMENT[normalizedGarmentType] || [];

    return keys.reduce((acc, key) => {
      const primaryValue = measurementSource?.[key];
      const fallbackValue = fallbackSource?.[key];
      acc[key] = primaryValue !== undefined ? primaryValue : (fallbackValue !== undefined ? fallbackValue : '');
      return acc;
    }, {});
  };

  const isRequestedMeasurementKey = (garmentType = 'trouser', key = '') => {
    const normalizedGarmentType = String(garmentType || 'trouser').toLowerCase();
    return Boolean((REQUESTED_MEASUREMENT_KEYS_BY_GARMENT[normalizedGarmentType] || []).includes(key));
  };

  const resolvePatternRequestedMeasurements = (patternLike = null, fallbackMeasurements = {}) => {
    const garmentType = String(patternLike?.garment_type || 'trouser').toLowerCase();
    const savedRequested = patternLike?.parameters?.requested_measurements;
    return pickRequestedMeasurementValues(garmentType, savedRequested || {}, fallbackMeasurements);
  };

  const resolvePatternGeometryEditState = (patternLike = null) => {
    const savedState = String(patternLike?.parameters?.geometry_edit_state || '').toLowerCase();
    if (savedState === 'free-edited' || savedState === 'parametric') {
      return savedState;
    }

    if (String(patternLike?.garment_type || '').toLowerCase() === 'jacket') {
      const parsedPointDeltas = parseJacketPointDeltasPayload(patternLike?.point_deltas || {});
      if (Object.keys(parsedPointDeltas.deltas || {}).length > 0 || Object.keys(parsedPointDeltas.baselineDeltas || {}).length > 0) {
        return 'free-edited';
      }
    }

    return 'parametric';
  };
  
  const normalizedUserRole = normalizeRole(user?.role);
  const canEditPattern = canEditPatternCanvas(normalizedUserRole);
  const canSavePatternVersion = canSavePatternVersionByRole(normalizedUserRole);
  const canManagePatterns = canAccessManagePatternsByRole(normalizedUserRole);
  const canAccessUserManagement = canAccessUserManagementByRole(normalizedUserRole);
  const canAccessBackupManagement = canAccessBackupManagementByRole(normalizedUserRole);
  const canManageSiteMode = isOwner(normalizedUserRole);
  const canSavePrintLayout = canSavePrintLayoutPreview(normalizedUserRole);
  
  // View State
  const [currentView, setCurrentView] = useState('pattern'); // 'pattern', 'profile', 'admin', 'manage_patterns'
  
  // Current Pattern State
  const [currentPattern, setCurrentPattern] = useState(null); // null = no pattern loaded
  const [patternEditLocked, setPatternEditLocked] = useState(false);
  const lastPatternActivityRef = useRef(Date.now());
  const canEditLoadedPattern = canEditPattern && !patternEditLocked;
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isOverwriteMode, setIsOverwriteMode] = useState(false); // true = editing existing, false = new version
  
  // Dialogs
  const [loadPatternModalOpen, setLoadPatternModalOpen] = useState(false);
  const [actionConfirmModalOpen, setActionConfirmModalOpen] = useState(false);
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);
  const [selectedPatternForAction, setSelectedPatternForAction] = useState(null);
  const [draftMeasurementsModalOpen, setDraftMeasurementsModalOpen] = useState(false);
  const [pendingDraftInfo, setPendingDraftInfo] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'load', 'new_version', 'manage_patterns', 'home'
  const patternTabHandoffConsumedRef = useRef(false);
  const patternTabHandoffActionsRef = useRef({});
  
  // Pattern State
  const [measurements, setMeasurements] = useState(trouserDefaults);
  const [requestedMeasurements, setRequestedMeasurements] = useState(() => pickRequestedMeasurementValues('trouser', trouserDefaults));
  const [geometryEditState, setGeometryEditState] = useState('parametric');
  const [hasGeometryChanges, setHasGeometryChanges] = useState(false);
  const [patternData, setPatternData] = useState(null);
  const [modifiedParams, setModifiedParams] = useState([]);
  const [draftingNotice, setDraftingNotice] = useState('');
  
  // View State
  const [zoom, setZoom] = useState(1.4);
  const [showFront, setShowFront] = useState(true);
  const [showSide, setShowSide] = useState(true);
  const [showBack, setShowBack] = useState(true);
  const [showYoke, setShowYoke] = useState(true);
  const [showCollar, setShowCollar] = useState(false);
  const [showSleeve, setShowSleeve] = useState(true);
  const [showMeasurements, setShowMeasurements] = useState(true);
  const [showPoints, setShowPoints] = useState(false);
  const [seamWalkMode, setSeamWalkMode] = useState(false);
  const [sleeveOnBodyMode, setSleeveOnBodyMode] = useState(false);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [selectedCurveId, setSelectedCurveId] = useState(null);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);

  // Jacket dual-mode state
  const [jacketCreationMode, setJacketCreationMode] = useState('free');
  const [jacketPointDeltas, setJacketPointDeltas] = useState({});
  const [jacketBaselinePointDeltas, setJacketBaselinePointDeltas] = useState({});
  const [selectedJacketFreePoints, setSelectedJacketFreePoints] = useState([]);

  // Trouser dual-mode state
  const [trouserCreationMode, setTrouserCreationMode] = useState('free');
  const [trouserPointDeltas, setTrouserPointDeltas] = useState({});
  const [trouserBaselinePointDeltas, setTrouserBaselinePointDeltas] = useState({});
  const [selectedTrouserFreePoints, setSelectedTrouserFreePoints] = useState([]);

  // Shirt dual-mode state
  const [shirtCreationMode, setShirtCreationMode] = useState('free');
  const [shirtPointDeltas, setShirtPointDeltas] = useState({});
  const [shirtBaselinePointDeltas, setShirtBaselinePointDeltas] = useState({});
  const [selectedShirtFreePoints, setSelectedShirtFreePoints] = useState([]);
  
  // Version Control
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [comparisonPatterns, setComparisonPatterns] = useState([]);

  // Export
  const [isPrintPreviewMode, setIsPrintPreviewMode] = useState(false);
  const [previewLayoutPieces, setPreviewLayoutPieces] = useState([]);
  const [previewCutLengthInches, setPreviewCutLengthInches] = useState(0);
  
  // Auto-save warning
  const [lastSaveTime, setLastSaveTime] = useState(Date.now());
  const [showSaveWarning, setShowSaveWarning] = useState(false);
  const [canvasViewportResetToken, setCanvasViewportResetToken] = useState(0);

  // Hybrid autosave state
  const [isPatternAutosaveDirty, setIsPatternAutosaveDirty] = useState(false);
  const [patternAutosaveStatus, setPatternAutosaveStatus] = useState('idle');
  const [patternAutosaveError, setPatternAutosaveError] = useState('');
  const [patternAutosaveUpdatedAt, setPatternAutosaveUpdatedAt] = useState(null);
  const [patternAutosaveHoldUntil, setPatternAutosaveHoldUntil] = useState(0);
  const [isLayoutAutosaveDirty, setIsLayoutAutosaveDirty] = useState(false);
  const [layoutAutosaveStatus, setLayoutAutosaveStatus] = useState('idle');
  const [layoutAutosaveError, setLayoutAutosaveError] = useState('');
  const [layoutAutosaveUpdatedAt, setLayoutAutosaveUpdatedAt] = useState(null);

  const patternSaveInFlightRef = useRef(false);
  const patternQueuedSaveRef = useRef(null);
  const patternLastSavedSignatureRef = useRef('');
  const patternEditVersionRef = useRef(0);
  const patternEditSignatureRef = useRef('');
  const layoutSaveInFlightRef = useRef(false);
  const layoutQueuedSaveRef = useRef(null);
  const layoutLastSavedSignatureRef = useRef('');
  const previousViewRef = useRef('pattern');
  const patternErrorToastAtRef = useRef(0);
  const layoutErrorToastAtRef = useRef(0);

  const handleCanvasZoomOut = () => {
    setZoom((prev) => Math.max(0.2, roundToSixteenth(prev * 0.9)));
  };

  const handleCanvasZoomIn = () => {
    setZoom((prev) => Math.min(10, roundToSixteenth(prev * 1.1)));
  };

  const handleCanvasZoomReset = () => {
    setZoom(1);
  };

  // Browser refresh warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && currentPattern) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, currentPattern]);

  useEffect(() => {
    if (!currentPattern?.id || currentPattern.isNew) {
      lastPatternActivityRef.current = Date.now();
      setPatternEditLocked(false);
      return undefined;
    }

    const serverActivity = Date.parse(
      currentPattern.last_edit_activity_at || currentPattern.created_at || '',
    );
    lastPatternActivityRef.current = Number.isFinite(serverActivity) ? serverActivity : 0;

    const tick = () => {
      const idleLocked = Date.now() - lastPatternActivityRef.current >= PATTERN_EDIT_LOCK_MS;
      setPatternEditLocked(currentPattern.edit_locked === true || idleLocked);
    };
    tick();
    const intervalId = setInterval(tick, 15000);
    return () => clearInterval(intervalId);
  }, [currentPattern?.id, currentPattern?.isNew, currentPattern?.last_edit_activity_at, currentPattern?.created_at, currentPattern?.edit_locked]);

  useEffect(() => {
    if (!patternEditLocked) return undefined;
    setSelectedJacketFreePoints([]);
    setSelectedTrouserFreePoints([]);
    setSelectedShirtFreePoints([]);
    setSelectedCurveId(null);
    return undefined;
  }, [patternEditLocked]);

  // Auto-regenerate pattern on measurement change
  useEffect(() => {
    if (currentPattern) {
      const garmentType = currentPattern.garment_type || 'trouser';
      const formulaVersion = resolvePatternFormulaVersion(currentPattern);
      const geometryVersion = resolvePatternGeometryVersion(currentPattern);
      const isJacket = garmentType === 'jacket';
      const isTrouser = garmentType === 'trouser';
      const isShirt = garmentType === 'shirt';
      const savedSnapshot = normalizeComparisonPatternData(currentPattern?.pattern_data, garmentType, {
        formulaVersion,
        geometryVersion,
      });
      const enrichedSavedSnapshot = isJacket
        ? enrichJacketSnapshotButtonData(savedSnapshot, measurements)
        : savedSnapshot;
      const currentRenderablePatternData = hasRenderablePatternSnapshot(patternData) ? patternData : null;

      if (!hasGeometryChanges) {
        const lockedPatternData = !currentPattern?.isNew && hasRenderablePatternSnapshot(enrichedSavedSnapshot)
          ? enrichedSavedSnapshot
          : currentRenderablePatternData;

        if (lockedPatternData) {
          setPatternData(lockedPatternData);
          setDraftingNotice(lockedPatternData?.draftingStatus === 'planned' ? lockedPatternData.message : '');
          return;
        }
      }

      if (!hasUnsavedChanges && !currentPattern?.isNew && hasRenderablePatternSnapshot(enrichedSavedSnapshot)) {
        setPatternData(enrichedSavedSnapshot);
        setDraftingNotice(enrichedSavedSnapshot?.draftingStatus === 'planned' ? enrichedSavedSnapshot.message : '');
        return;
      }

      const effectiveJacketPointDeltas = resolveEffectiveJacketPointDeltas({
        mode: jacketCreationMode,
        deltas: jacketPointDeltas,
        baselineDeltas: jacketBaselinePointDeltas,
      });
      const effectiveTrouserPointDeltas = resolveEffectiveTrouserPointDeltas({
        mode: trouserCreationMode,
        deltas: trouserPointDeltas,
        baselineDeltas: trouserBaselinePointDeltas,
      });
      const effectiveShirtPointDeltas = resolveEffectiveShirtPointDeltas({
        mode: shirtCreationMode,
        deltas: shirtPointDeltas,
        baselineDeltas: shirtBaselinePointDeltas,
      });

      const data = generatePattern(measurements, {
        backCrotchScoopX: measurements.backCrotchScoopX,
        backCrotchScoopY: measurements.backCrotchScoopY,
        formulaVersion,
        geometryVersion,
        freeModeActive: (isJacket && jacketCreationMode === 'free')
          || (isTrouser && trouserCreationMode === 'free')
          || (isShirt && shirtCreationMode === 'free'),
        pointDeltas: isJacket
          ? effectiveJacketPointDeltas
          : (isTrouser
            ? effectiveTrouserPointDeltas
            : (isShirt ? effectiveShirtPointDeltas : {})),
      }, garmentType);

      setPatternData(data);
      setDraftingNotice(data?.draftingStatus === 'planned' ? data.message : '');
    }
  }, [measurements, currentPattern, hasUnsavedChanges, hasGeometryChanges, jacketCreationMode, jacketPointDeltas, jacketBaselinePointDeltas, trouserCreationMode, trouserPointDeltas, trouserBaselinePointDeltas, shirtCreationMode, shirtPointDeltas, shirtBaselinePointDeltas]);

  useEffect(() => {
    if (patternData?.garmentType === 'shirt' && !patternData?.yokePath) {
      setShowYoke(false);
    }
  }, [patternData?.garmentType, patternData?.yokePath]);

  useEffect(() => {
    if (patternData?.garmentType === 'shirt' && !patternData?.sleevePath) {
      setShowSleeve(false);
    }
  }, [patternData?.garmentType, patternData?.sleevePath]);

  useEffect(() => {
    setShowCollar(false);
  }, [currentPattern?.id]);

  useEffect(() => {
    if (patternData?.garmentType === 'shirt' && !patternData?.collarBandPath) {
      setShowCollar(false);
    }
  }, [patternData?.garmentType, patternData?.collarBandPath]);

  useEffect(() => {
    const garmentConfig = getGarmentConfig(currentPattern?.garment_type || 'trouser');
    const supportsSeamWalk = Boolean(garmentConfig?.displayOptions?.seamWalk);

    if (!supportsSeamWalk && seamWalkMode) {
      setSeamWalkMode(false);
    }
  }, [currentPattern?.garment_type, seamWalkMode]);

  useEffect(() => {
    if (!currentPattern || selectedVersions.length === 0) {
      setComparisonPatterns([]);
      return;
    }

    const overlays = selectedVersions
      .map((versionId, index) => {
        const version = availableVersions.find((v) => v.id === versionId);
        if (!version) return null;

        try {
          const currentGarmentType = (currentPattern?.garment_type || 'trouser').toLowerCase();
          const candidateGarmentType = (version.garment_type || 'trouser').toLowerCase();
          if (candidateGarmentType !== currentGarmentType) {
            return null;
          }

          const garmentType = version.garment_type || currentPattern.garment_type || 'trouser';
          const baseMeasurements = getDefaultMeasurements(garmentType);
          const versionMeasurements = {
            ...baseMeasurements,
            ...((version.measurements && typeof version.measurements === 'object') ? version.measurements : {}),
          };

          const versionFormula = resolvePatternFormulaVersion(version);
          const versionGeometry = resolvePatternGeometryVersion(version);
          const rawPatternPayload = version.pattern_data || version.patternData || version.data;
          const normalizedPayload = normalizeComparisonPatternData(rawPatternPayload, garmentType);
          const hasRenderablePayload = Boolean(
            normalizedPayload?.frontPath
            || normalizedPayload?.sidePath
            || normalizedPayload?.backPath
            || normalizedPayload?.yokePath
            || normalizedPayload?.collarBandPath
            || normalizedPayload?.sleevePath
            || normalizedPayload?.topSleevePath
            || normalizedPayload?.underSleevePath
          );

          const generated = hasRenderablePayload
            ? normalizedPayload
            : generatePattern(
                versionMeasurements,
                {
                  backCrotchScoopX: versionMeasurements.backCrotchScoopX,
                  backCrotchScoopY: versionMeasurements.backCrotchScoopY,
                  formulaVersion: versionFormula,
                  geometryVersion: versionGeometry,
                  freeModeActive: (garmentType === 'jacket' && resolveJacketCreationMode(version.point_deltas) === 'free')
                    || (garmentType === 'trouser' && resolveTrouserCreationMode(version.point_deltas) === 'free')
                    || (garmentType === 'shirt' && resolveShirtCreationMode(version.point_deltas) === 'free'),
                  pointDeltas: garmentType === 'jacket'
                    ? resolveEffectiveJacketPointDeltas(parseJacketPointDeltasPayload(version.point_deltas))
                    : (garmentType === 'trouser'
                      ? resolveEffectiveTrouserPointDeltas(parseTrouserPointDeltasPayload(version.point_deltas))
                      : (garmentType === 'shirt'
                        ? resolveEffectiveShirtPointDeltas(parseShirtPointDeltasPayload(version.point_deltas))
                        : {})),
                },
                garmentType,
              );

          return {
            ...generated,
            id: version.id,
            pattern_number: version.pattern_number,
            color: VERSION_COLORS[index % VERSION_COLORS.length],
          };
        } catch (error) {
          console.warn(`Skipping comparison overlay for version ${version.pattern_number}`, error);
          return null;
        }
      })
      .filter(Boolean);

    setComparisonPatterns(overlays);
  }, [selectedVersions, availableVersions, currentPattern]);

  useEffect(() => {
    if (!selectedVersions.length) return;
    const availableIds = new Set((availableVersions || []).map((version) => version.id));
    const filtered = selectedVersions.filter((versionId) => availableIds.has(versionId)).slice(0, 3);
    if (filtered.length !== selectedVersions.length) {
      setSelectedVersions(filtered);
    }
  }, [availableVersions, selectedVersions]);

  // 5-minute save warning
  useEffect(() => {
    if (!currentPattern) return;
    
    const interval = setInterval(() => {
      const timeSinceLastSave = Date.now() - lastSaveTime;
      if (timeSinceLastSave > 5 * 60 * 1000 && hasUnsavedChanges) {
        setShowSaveWarning(true);
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(interval);
  }, [lastSaveTime, hasUnsavedChanges, currentPattern]);

  useEffect(() => {
    patternEditVersionRef.current = 0;
    patternEditSignatureRef.current = '';
  }, [currentPattern?.id]);

  useEffect(() => {
    if (!currentPattern) return;

    const editSignature = JSON.stringify({
      measurements,
      requestedMeasurements,
      geometryEditState,
      hasGeometryChanges,
      jacketCreationMode,
      jacketPointDeltas,
      jacketBaselinePointDeltas,
      trouserCreationMode,
      trouserPointDeltas,
      trouserBaselinePointDeltas,
      shirtCreationMode,
      shirtPointDeltas,
      shirtBaselinePointDeltas,
    });

    if (patternEditSignatureRef.current !== editSignature) {
      patternEditVersionRef.current += 1;
      patternEditSignatureRef.current = editSignature;
    }
  }, [
    currentPattern,
    measurements,
    requestedMeasurements,
    geometryEditState,
    hasGeometryChanges,
    jacketCreationMode,
    jacketPointDeltas,
    jacketBaselinePointDeltas,
    trouserCreationMode,
    trouserPointDeltas,
    trouserBaselinePointDeltas,
    shirtCreationMode,
    shirtPointDeltas,
    shirtBaselinePointDeltas,
  ]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    setIsPatternAutosaveDirty(true);
    setPatternAutosaveStatus('idle');
    setPatternAutosaveError('');
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (currentPattern) return;
    patternLastSavedSignatureRef.current = '';
    layoutLastSavedSignatureRef.current = '';
    setIsPatternAutosaveDirty(false);
    setPatternAutosaveStatus('idle');
    setPatternAutosaveError('');
    setPatternAutosaveUpdatedAt(null);
    setIsLayoutAutosaveDirty(false);
    setLayoutAutosaveStatus('idle');
    setLayoutAutosaveError('');
    setLayoutAutosaveUpdatedAt(null);
  }, [currentPattern]);

  const markGeometryAsFreeEdited = () => {
    setGeometryEditState('free-edited');
  };

  const formatAutosaveTime = (time) => {
    if (!time) return '';
    try {
      return new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (_error) {
      return '';
    }
  };

  const patternAutosaveMessage = patternAutosaveStatus === 'saving'
    ? 'Saving…'
    : patternAutosaveStatus === 'saved'
      ? `Saved ${formatAutosaveTime(patternAutosaveUpdatedAt)}`
      : patternAutosaveStatus === 'error'
        ? `Save failed: ${patternAutosaveError || 'Retrying…'}`
        : isPatternAutosaveDirty && patternAutosaveHoldUntil > Date.now()
          ? 'Adjusting…'
        : isPatternAutosaveDirty
          ? 'Changes pending…'
          : 'Idle';

  const layoutAutosaveMessage = layoutAutosaveStatus === 'saving'
    ? 'Saving…'
    : layoutAutosaveStatus === 'saved'
      ? `Saved ${formatAutosaveTime(layoutAutosaveUpdatedAt)}`
      : layoutAutosaveStatus === 'error'
        ? `Save failed: ${layoutAutosaveError || 'Retrying…'}`
        : isLayoutAutosaveDirty
          ? 'Changes pending…'
          : 'Idle';

  const getCurrentGarmentPatternOptions = (garmentType) => {
    if (garmentType === 'jacket') {
      return {
        freeModeActive: jacketCreationMode === 'free',
        pointDeltas: resolveEffectiveJacketPointDeltas({
          mode: jacketCreationMode,
          deltas: jacketPointDeltas,
          baselineDeltas: jacketBaselinePointDeltas,
        }),
      };
    }

    if (garmentType === 'trouser') {
      return {
        freeModeActive: trouserCreationMode === 'free',
        pointDeltas: resolveEffectiveTrouserPointDeltas({
          mode: trouserCreationMode,
          deltas: trouserPointDeltas,
          baselineDeltas: trouserBaselinePointDeltas,
        }),
      };
    }

    if (garmentType === 'shirt') {
      return {
        freeModeActive: shirtCreationMode === 'free',
        pointDeltas: resolveEffectiveShirtPointDeltas({
          mode: shirtCreationMode,
          deltas: shirtPointDeltas,
          baselineDeltas: shirtBaselinePointDeltas,
        }),
      };
    }

    return { freeModeActive: false, pointDeltas: {} };
  };

  const shouldShowAutosaveErrorToast = (toastRef) => {
    const now = Date.now();
    if (now - toastRef.current < 20000) return false;
    toastRef.current = now;
    return true;
  };

  const fetchPatternById = async (patternId) => {
    if (!patternId) return null;

    const response = await fetch(`${API_BASE}/api/patterns/${patternId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to load pattern details');
    }

    return response.json();
  };

  const handleMeasurementChange = (key, value) => {
    if (patternEditLocked) return;
    lastPatternActivityRef.current = Date.now();
    const clampFrontSplit = (frontValue, totalValue) => {
      const total = Number(totalValue || 0);
      const front = Number(frontValue || 0);
      if (!Number.isFinite(total) || total <= 0) return roundToEighth(Math.max(0.125, front || 0.125));
      const min = 0.125;
      const max = Math.max(min, total - 0.125);
      return roundToEighth(Math.min(max, Math.max(min, front)));
    };

    const markModified = (keys = []) => {
      setModifiedParams((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.add(k));
        return Array.from(next);
      });
    };

    const markGeometryDirty = (keys = []) => {
      setHasUnsavedChanges(true);
      setHasGeometryChanges(true);
      markModified(keys);
    };

    const clampShirtFrontSplit = (frontValue, totalHalf, min = 0.75) => {
      const targetHalf = roundToEighth(Math.max(min * 2, Number(totalHalf || 0)));
      return clampShirtFrontSplitGlobal(frontValue, targetHalf, min);
    };

    const getShirtDartIntake = (source, intakeKey) => {
      const raw = Number(source?.[intakeKey]);
      return Number.isFinite(raw) ? roundToEighth(Math.max(0, raw)) : 0;
    };

    const currentGarmentType = String(currentPattern?.garment_type || 'trouser').toLowerCase();

    let normalizedValue;

    if (typeof value === 'boolean') {
      normalizedValue = value;
    } else if (typeof value === 'number') {
      normalizedValue = Number.isNaN(value) ? 0 : value;
    } else if (typeof value === 'string') {
      const parsed = parseFloat(value);
      normalizedValue = Number.isNaN(parsed) ? value : parsed;
    } else {
      const parsed = parseFloat(value);
      normalizedValue = Number.isNaN(parsed) ? 0 : parsed;
    }

    if (key === 'frontChestResolved') {
      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) return;
      const halfChest = Number(measurements?.chest || 0) / 2;
      const frontChestSplit = clampShirtFrontSplit(parsed, halfChest);
      setMeasurements((prev) => ({
        ...prev,
        frontChestSplit,
      }));
      markGeometryDirty(['frontChestSplit']);
      return;
    }

    if (key === 'frontWaistResolvedShirt') {
      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) return;
      const waistHalf = Number(measurements?.waist || 0) / 2;
      const frontWaistSplit = clampShirtFrontSplit(parsed, waistHalf);
      setMeasurements((prev) => ({
        ...prev,
        frontWaistSplit,
      }));
      markGeometryDirty(['frontWaistSplit']);
      return;
    }

    if (key === 'frontBellyResolvedShirt') {
      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) return;
      const bellyHalf = Number(measurements?.belly || 0) / 2;
      const frontBellySplit = clampShirtFrontSplit(parsed, bellyHalf);
      setMeasurements((prev) => ({
        ...prev,
        frontBellySplit,
      }));
      markGeometryDirty(['frontBellySplit']);
      return;
    }

    if (key === 'frontSeatResolvedShirt') {
      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) return;
      const halfSeat = Number(measurements?.seat || 0) / 2;
      const frontSeatSplit = clampShirtFrontSplit(parsed, halfSeat);
      setMeasurements((prev) => ({
        ...prev,
        frontSeatSplit,
      }));
      markGeometryDirty(['frontSeatSplit']);
      return;
    }

    if (key === 'frontWaistResolved') {
      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) return;

      const quarterWaist = Number(measurements.waist || 0) / 4;
      const frontWaistAdd = roundToEighth(parsed - quarterWaist);
      const backWaistAdd = roundToEighth(-frontWaistAdd);

      setMeasurements((prev) => ({
        ...prev,
        frontWaistAdd,
        backWaistAdd,
      }));
      markGeometryDirty(['frontWaistAdd', 'backWaistAdd']);
      return;
    }

    if (key === 'frontHipResolved') {
      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) return;

      const quarterSeat = Number(measurements.seat || 0) / 4;
      const frontHipAdd = roundToEighth(parsed - quarterSeat);
      const backHipAdd = roundToEighth(-frontHipAdd);

      setMeasurements((prev) => ({
        ...prev,
        frontHipAdd,
        backHipAdd,
      }));
      markGeometryDirty(['frontHipAdd', 'backHipAdd']);
      return;
    }

    if (key === 'backDartMode') {
      const normalizedMode = ['none', 'one', 'two'].includes(value) ? value : 'one';
      setMeasurements((prev) => ({
        ...prev,
        backDartMode: normalizedMode,
        useTwoBackDarts: normalizedMode === 'two',
      }));
      markGeometryDirty(['backDartMode', 'useTwoBackDarts']);
      return;
    }

    if (key === 'sleeveStyle') {
      const normalizedStyle = value === 'short' ? 'short' : 'long';
      setMeasurements((prev) => ({
        ...prev,
        sleeveStyle: normalizedStyle,
      }));
      markGeometryDirty(['sleeveStyle']);
      return;
    }

    if (key === 'frontKneeResolved') {
      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) return;

      const frontKnee = clampFrontSplit(parsed, measurements.knee);
      setMeasurements((prev) => ({
        ...prev,
        frontKnee,
      }));
      markGeometryDirty(['frontKnee']);
      return;
    }

    if (key === 'frontHemResolved') {
      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) return;

      const frontHem = clampFrontSplit(parsed, measurements.hem);
      setMeasurements((prev) => ({
        ...prev,
        frontHem,
      }));
      markGeometryDirty(['frontHem']);
      return;
    }

    if (key === 'useTwoBackDarts') {
      const twoDartsEnabled = value === true;
      setMeasurements((prev) => ({
        ...prev,
        useTwoBackDarts: twoDartsEnabled,
        backDartMode: twoDartsEnabled ? 'two' : (prev.backDartMode === 'none' ? 'none' : 'one'),
      }));
      markGeometryDirty(['useTwoBackDarts', 'backDartMode']);
      return;
    }

    if (key === 'hemShoeShapeEnabled') {
      const enabled = value === true;
      setMeasurements((prev) => ({
        ...prev,
        hemShoeShapeEnabled: enabled,
      }));
      markGeometryDirty(['hemShoeShapeEnabled']);
      return;
    }

    setMeasurements((prev) => {
      if (key === 'knee') {
        const knee = Number(normalizedValue || 0);
        const currentFrontKnee = Number(prev.frontKnee);
        const hasFrontKnee = Number.isFinite(currentFrontKnee);
        const currentAutoFrontKnee = resolveTrouserDefaultFrontKnee(prev.knee);
        const shouldUseAutoFrontKnee = !modifiedParams.includes('frontKnee')
          && (!hasFrontKnee || Math.abs(currentFrontKnee - currentAutoFrontKnee) < 1e-6);
        const nextFrontKnee = shouldUseAutoFrontKnee
          ? clampFrontSplit(resolveTrouserDefaultFrontKnee(knee), knee)
          : clampFrontSplit(currentFrontKnee, knee);
        return {
          ...prev,
          knee,
          frontKnee: nextFrontKnee,
        };
      }

      if (key === 'hem') {
        const hem = Number(normalizedValue || 0);
        const currentFrontHem = Number(prev.frontHem);
        const hasFrontHem = Number.isFinite(currentFrontHem);
        const currentGeometry = resolvePatternGeometryVersion(currentPattern);
        const currentHemAutoSplit = currentGeometry === 'trouser_geom_v4'
          ? resolveTrouserV4DefaultFrontHem(prev.hem)
          : resolveTrouserFrontSplitFromBackExcess(prev.hem, 2);
        const nextHemAutoSplit = currentGeometry === 'trouser_geom_v4'
          ? resolveTrouserV4DefaultFrontHem(hem)
          : resolveTrouserFrontSplitFromBackExcess(hem, 2);
        const shouldUseAutoFrontHem = !modifiedParams.includes('frontHem')
          && (!hasFrontHem || Math.abs(currentFrontHem - currentHemAutoSplit) < 1e-6);
        const nextFrontHem = shouldUseAutoFrontHem
          ? clampFrontSplit(nextHemAutoSplit, hem)
          : clampFrontSplit(currentFrontHem, hem);
        return {
          ...prev,
          hem,
          frontHem: nextFrontHem,
        };
      }

      const currentGarment = currentGarmentType;
      const currentGeometry = resolvePatternGeometryVersion(currentPattern);
      const isShirtV2 = currentGarment === 'shirt' && isShirtGeometryV2(currentGeometry);

      if (key === 'chest') {
        const chest = Number(normalizedValue || 0);
        const nextFront = isShirtV2
          ? resolvePortlyFrontSplit({ total: chest, portlyType: prev.portlyType })
          : clampShirtFrontSplit((chest / 2) / 2, chest / 2);

        return {
          ...prev,
          chest,
          frontChestSplit: nextFront,
        };
      }

      if (key === 'waist') {
        const waist = Number(normalizedValue || 0);
        const nextFront = isShirtV2
          ? resolvePortlyFrontSplit({
              total: waist,
              portlyType: prev.portlyType,
              dartIntake: prev.dartWidthWaist,
              applyDartCompensation: true,
            })
          : clampShirtFrontSplit((waist / 2) / 2, waist / 2);

        return {
          ...prev,
          waist,
          frontWaistSplit: nextFront,
        };
      }

      if (key === 'belly') {
        const belly = Number(normalizedValue || 0);
        const nextFront = isShirtV2
          ? resolvePortlyFrontSplit({
              total: belly,
              portlyType: prev.portlyType,
              dartIntake: prev.dartWidthBelly,
              applyDartCompensation: true,
            })
          : clampShirtFrontSplit((belly / 2) / 2, belly / 2);

        return {
          ...prev,
          belly,
          frontBellySplit: nextFront,
        };
      }

      if (key === 'seat') {
        const seat = Number(normalizedValue || 0);
        const nextFront = isShirtV2
          ? resolvePortlyFrontSplit({ total: seat, portlyType: prev.portlyType })
          : clampShirtFrontSplit((seat / 2) / 2, seat / 2);

        return {
          ...prev,
          seat,
          frontSeatSplit: nextFront,
        };
      }

      if (key === 'portlyType' && isShirtV2) {
        const nextPortlyType = String(normalizedValue || 'normal');
        return {
          ...prev,
          portlyType: nextPortlyType,
          frontChestSplit: resolvePortlyFrontSplit({ total: prev.chest, portlyType: nextPortlyType }),
          frontWaistSplit: resolvePortlyFrontSplit({
            total: prev.waist,
            portlyType: nextPortlyType,
            dartIntake: prev.dartWidthWaist,
            applyDartCompensation: true,
          }),
          frontBellySplit: resolvePortlyFrontSplit({
            total: prev.belly,
            portlyType: nextPortlyType,
            dartIntake: prev.dartWidthBelly,
            applyDartCompensation: true,
          }),
          frontSeatSplit: resolvePortlyFrontSplit({ total: prev.seat, portlyType: nextPortlyType }),
        };
      }

      if (key === 'dartWidthWaist' && isShirtV2) {
        const nextDartWaist = Number(normalizedValue || 0);
        return {
          ...prev,
          dartWidthWaist: nextDartWaist,
          frontWaistSplit: resolvePortlyFrontSplit({
            total: prev.waist,
            portlyType: prev.portlyType,
            dartIntake: nextDartWaist,
            applyDartCompensation: true,
          }),
        };
      }

      if (key === 'dartWidthBelly' && isShirtV2) {
        const nextDartBelly = Number(normalizedValue || 0);
        return {
          ...prev,
          dartWidthBelly: nextDartBelly,
          frontBellySplit: resolvePortlyFrontSplit({
            total: prev.belly,
            portlyType: prev.portlyType,
            dartIntake: nextDartBelly,
            applyDartCompensation: true,
          }),
        };
      }

      if (currentGarment === 'jacket' && key === 'bellyProfile') {
        const nextBellyProfile = normalizeJacketBellyProfile(normalizedValue);
        return applyJacketBellyProfileRule({
          ...prev,
          bellyProfile: nextBellyProfile,
        });
      }

      if (currentGarment === 'jacket' && key === 'posture') {
        const nextPosture = normalizeJacketPosture(normalizedValue);
        return applyJacketPostureRule({
          ...prev,
          posture: nextPosture,
        });
      }

      if (currentGarment === 'jacket' && (key === 'sleevePitchRotation' || key === 'sleevePitchShift')) {
        return applyJacketPitchRotationRule({
          ...prev,
          sleevePitchRotation: normalizedValue,
          sleevePitchShift: normalizedValue,
        }, { preserveExistingManualValues: true });
      }

      if (currentGarment === 'jacket' && (key === 'jacketDartMode' || key === 'fishDartEnabled')) {
        return applyJacketDartModeRule({
          ...prev,
          jacketDartMode: key === 'jacketDartMode'
            ? normalizedValue
            : ((normalizedValue !== false && normalizedValue !== 0 && normalizedValue !== '0') ? 'belly_and_fish' : 'off'),
          fishDartEnabled: key === 'fishDartEnabled'
            ? normalizedValue
            : (normalizedValue !== 'off'),
        });
      }

      return {
        ...prev,
        [key]: normalizedValue,
      };
    });

    if (key === 'yokeEnabled' && Boolean(normalizedValue)) {
      setShowYoke(true);
    }
    
    setHasUnsavedChanges(true);
    setHasGeometryChanges(true);
    
    // Track modified params
    const currentGarment = currentGarmentType;
    const currentGeometry = resolvePatternGeometryVersion(currentPattern);
    const isShirtV2 = currentGarment === 'shirt' && isShirtGeometryV2(currentGeometry);

    if (key === 'knee') {
      markModified(['knee', 'frontKnee']);
    } else if (key === 'hem') {
      markModified(['hem', 'frontHem']);
    } else if (isShirtV2 && key === 'portlyType') {
      markModified(['portlyType', 'frontChestSplit', 'frontWaistSplit', 'frontBellySplit', 'frontSeatSplit']);
    } else if (isShirtV2 && key === 'dartWidthWaist') {
      markModified(['dartWidthWaist', 'frontWaistSplit']);
    } else if (isShirtV2 && key === 'dartWidthBelly') {
      markModified(['dartWidthBelly', 'frontBellySplit']);
    } else if (currentGarment === 'jacket' && key === 'bellyProfile') {
      markModified(['bellyProfile']);
    } else if (currentGarment === 'jacket' && key === 'posture') {
      markModified(['posture', 'backToFtHps']);
    } else if (currentGarment === 'jacket' && (key === 'sleevePitchRotation' || key === 'sleevePitchShift')) {
      markModified(['sleevePitchRotation', 'sleevePitchShift']);
    } else if (currentGarment === 'jacket' && (key === 'jacketDartMode' || key === 'fishDartEnabled')) {
      markModified(['jacketDartMode', 'fishDartEnabled']);
    } else if (['chest', 'waist', 'belly', 'seat'].includes(key)) {
      const dependentMap = {
        chest: 'frontChestSplit',
        waist: 'frontWaistSplit',
        belly: 'frontBellySplit',
        seat: 'frontSeatSplit',
      };
      markModified([key, dependentMap[key]]);
    } else {
      markModified([key]);
    }
  };

  const handleAdjustDrawnMeasurement = (key, delta) => {
    const numericDelta = Number(delta);
    if (!Number.isFinite(numericDelta) || numericDelta === 0) return;

    const currentGarmentType = String(currentPattern?.garment_type || 'trouser').toLowerCase();
    const currentDrawnValue = readDrawnMeasurementValue(patternData, currentGarmentType, key);
    const formulaVersion = resolvePatternFormulaVersion(currentPattern);
    const geometryVersion = resolvePatternGeometryVersion(currentPattern);
    const patternOptions = getCurrentGarmentPatternOptions(currentGarmentType);

    if (Number.isFinite(Number(currentDrawnValue))) {
      const solved = solveSeedForDrawnTarget({
        garmentType: currentGarmentType,
        drawnKey: key,
        targetDrawn: Number(currentDrawnValue) + numericDelta,
        currentMeasurements: measurements,
        currentPatternData: patternData,
        formulaVersion,
        geometryVersion,
        patternOptions,
      });

      if (solved) {
        setPatternAutosaveHoldUntil(Date.now() + DRAWN_STEPPER_AUTOSAVE_HOLD_MS);
        handleMeasurementChange(solved.seedKey, solved.seedValue);
        return;
      }
    }

    const currentMeasurement = Number(measurements?.[key]);
    const fallbackReference = Number(requestedMeasurements?.[key]);
    const fallbackDefault = Number(getDefaultMeasurements(currentGarmentType)?.[key]);
    const baseValue = Number.isFinite(currentMeasurement)
      ? currentMeasurement
      : (Number.isFinite(fallbackReference)
        ? fallbackReference
        : (Number.isFinite(fallbackDefault) ? fallbackDefault : 0));

    setPatternAutosaveHoldUntil(Date.now() + DRAWN_STEPPER_AUTOSAVE_HOLD_MS);
    handleMeasurementChange(key, roundToEighth(baseValue + numericDelta));
  };

  const handleCommitDrawnMeasurement = (key, rawValue) => {
    const numericTarget = Number.parseFloat(rawValue);
    if (!Number.isFinite(numericTarget)) return;

    const currentGarmentType = String(currentPattern?.garment_type || 'trouser').toLowerCase();
    const formulaVersion = resolvePatternFormulaVersion(currentPattern);
    const geometryVersion = resolvePatternGeometryVersion(currentPattern);
    const currentDrawnValue = readDrawnMeasurementValue(patternData, currentGarmentType, key);
    const patternOptions = getCurrentGarmentPatternOptions(currentGarmentType);

    if (Number.isFinite(Number(currentDrawnValue)) && Math.abs(Number(currentDrawnValue) - numericTarget) < 0.0005) {
      return;
    }

    if (Number.isFinite(Number(currentDrawnValue))) {
      const solved = solveSeedForDrawnTarget({
        garmentType: currentGarmentType,
        drawnKey: key,
        targetDrawn: numericTarget,
        currentMeasurements: measurements,
        currentPatternData: patternData,
        formulaVersion,
        geometryVersion,
        patternOptions,
      });

      if (solved) {
        setPatternAutosaveHoldUntil(Date.now() + DRAWN_STEPPER_AUTOSAVE_HOLD_MS);
        handleMeasurementChange(solved.seedKey, solved.seedValue);
        return;
      }
    }

    setPatternAutosaveHoldUntil(Date.now() + DRAWN_STEPPER_AUTOSAVE_HOLD_MS);
    handleMeasurementChange(key, numericTarget);
  };

  const handleCurveHandleChange = (changes) => {
    if (!changes || typeof changes !== 'object') return;

    const currentGarmentType = (currentPattern?.garment_type || 'trouser').toLowerCase();
    const nextChanges = { ...changes };

    if (currentGarmentType === 'shirt') {
      const touchingTop = SHIRT_COLLAR_TOP_HANDLE_KEYS.some((key) => Object.prototype.hasOwnProperty.call(changes, key));
      const resettingAllShirtHandles = SHIRT_CURVE_HANDLE_KEYS.every((key) => (
        Object.prototype.hasOwnProperty.call(changes, key) && Math.abs(Number(changes[key]) || 0) < 1e-8
      ));
      if (resettingAllShirtHandles) {
        nextChanges.collarBandTopFollowBottom = 1;
        nextChanges.collarBandTopStartAbsX = 0;
        nextChanges.collarBandTopStartAbsY = 0;
        nextChanges.collarBandTopEndAbsX = 0;
        nextChanges.collarBandTopEndAbsY = 0;
      } else if (
        touchingTop
        && Number(changes.collarBandTopFollowBottom) !== 1
        && Number(nextChanges.collarBandTopFollowBottom ?? measurements.collarBandTopFollowBottom ?? 1) !== 0
      ) {
        const startHandle = (patternData?.curveHandles || []).find((handle) => handle.id === 'collar-band-top-start-ctrl');
        const endHandle = (patternData?.curveHandles || []).find((handle) => handle.id === 'collar-band-top-end-ctrl');
        if (startHandle?.point && endHandle?.point) {
          nextChanges.collarBandTopFollowBottom = 0;
          nextChanges.collarBandTopStartAbsX = Number(startHandle.point.x || 0) - (Number(measurements.collarBandTopStartCtrlDx) || 0);
          nextChanges.collarBandTopStartAbsY = (-Number(startHandle.point.y || 0)) - (Number(measurements.collarBandTopStartCtrlDy) || 0);
          nextChanges.collarBandTopEndAbsX = Number(endHandle.point.x || 0) - (Number(measurements.collarBandTopEndCtrlDx) || 0);
          nextChanges.collarBandTopEndAbsY = (-Number(endHandle.point.y || 0)) - (Number(measurements.collarBandTopEndCtrlDy) || 0);
        }
      }

      const mirroredPairs = {
        sleeveBackSideUpperCtrlDx: { mirrorKey: 'sleeveFrontSideUpperCtrlDx', transform: (value) => -value },
        sleeveBackSideUpperCtrlDy: { mirrorKey: 'sleeveFrontSideUpperCtrlDy', transform: (value) => value },
        sleeveBackSideLowerCtrlDx: { mirrorKey: 'sleeveFrontSideLowerCtrlDx', transform: (value) => -value },
        sleeveBackSideLowerCtrlDy: { mirrorKey: 'sleeveFrontSideLowerCtrlDy', transform: (value) => value },
        sleeveFrontSideUpperCtrlDx: { mirrorKey: 'sleeveBackSideUpperCtrlDx', transform: (value) => -value },
        sleeveFrontSideUpperCtrlDy: { mirrorKey: 'sleeveBackSideUpperCtrlDy', transform: (value) => value },
        sleeveFrontSideLowerCtrlDx: { mirrorKey: 'sleeveBackSideLowerCtrlDx', transform: (value) => -value },
        sleeveFrontSideLowerCtrlDy: { mirrorKey: 'sleeveBackSideLowerCtrlDy', transform: (value) => value },
      };

      Object.entries(changes).forEach(([key, value]) => {
        const mirrorRule = mirroredPairs[key];
        if (!mirrorRule) return;
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return;
        nextChanges[mirrorRule.mirrorKey] = mirrorRule.transform(numericValue);
      });
    }

    setMeasurements((prev) => ({
      ...prev,
      ...nextChanges,
    }));

    setHasUnsavedChanges(true);
    setHasGeometryChanges(true);
    markGeometryAsFreeEdited();
    setModifiedParams((prev) => {
      const next = new Set(prev);
      Object.keys(nextChanges).forEach((key) => next.add(key));
      return Array.from(next);
    });
  };

  const initializeJacketModeStateFromPattern = (patternLike = null) => {
    const garmentType = (patternLike?.garment_type || '').toLowerCase();
    if (garmentType !== 'jacket') {
      setJacketCreationMode('free');
      setJacketPointDeltas({});
      setJacketBaselinePointDeltas({});
      setSelectedJacketFreePoints([]);
      return;
    }

    const parsedPointDeltas = parseJacketPointDeltasPayload(patternLike?.point_deltas || {});
    const incomingPointDeltas = parsedPointDeltas.deltas;
    const incomingBaselineDeltas = parsedPointDeltas.baselineDeltas;
    setJacketCreationMode('free');
    setJacketPointDeltas(incomingPointDeltas);
    setJacketBaselinePointDeltas(incomingBaselineDeltas);
    setSelectedJacketFreePoints([]);
  };

  const initializeTrouserModeStateFromPattern = (patternLike = null) => {
    const garmentType = (patternLike?.garment_type || '').toLowerCase();
    if (garmentType !== 'trouser') {
      setTrouserCreationMode('free');
      setTrouserPointDeltas({});
      setTrouserBaselinePointDeltas({});
      setSelectedTrouserFreePoints([]);
      return;
    }

    const parsedPointDeltas = parseTrouserPointDeltasPayload(patternLike?.point_deltas || {});
    setTrouserCreationMode('free');
    setTrouserPointDeltas(parsedPointDeltas.deltas);
    setTrouserBaselinePointDeltas(parsedPointDeltas.baselineDeltas);
    setSelectedTrouserFreePoints([]);
  };

  const initializeShirtModeStateFromPattern = (patternLike = null) => {
    const garmentType = (patternLike?.garment_type || '').toLowerCase();
    if (garmentType !== 'shirt') {
      setShirtCreationMode('free');
      setShirtPointDeltas({});
      setShirtBaselinePointDeltas({});
      setSelectedShirtFreePoints([]);
      return;
    }

    const parsedPointDeltas = parseShirtPointDeltasPayload(patternLike?.point_deltas || {});
    setShirtCreationMode('free');
    setShirtPointDeltas(parsedPointDeltas.deltas);
    setShirtBaselinePointDeltas(parsedPointDeltas.baselineDeltas);
    setSelectedShirtFreePoints([]);
  };

  const initializeFreeEditModeStateFromPattern = (patternLike = null) => {
    initializeJacketModeStateFromPattern(patternLike);
    initializeTrouserModeStateFromPattern(patternLike);
    initializeShirtModeStateFromPattern(patternLike);
  };

  const handleJacketFreePointSelectionChange = (nextSelection = []) => {
    const sanitized = Array.from(new Set((nextSelection || []).filter(Boolean)));
    setSelectedJacketFreePoints(sanitized);
  };

  const handleClearJacketFreePointSelection = () => {
    setSelectedJacketFreePoints([]);
  };

  const handleJacketNudgeSelectedPoints = (deltaX = 0, deltaY = 0) => {
    if ((currentPattern?.garment_type || '').toLowerCase() !== 'jacket') return;
    if (jacketCreationMode !== 'free') return;
    if (!selectedJacketFreePoints.length) return;

    const dx = roundToSixteenth(Number(deltaX) || 0);
    const dy = roundToSixteenth(Number(deltaY) || 0);
    if (Math.abs(dx) < 1e-8 && Math.abs(dy) < 1e-8) return;

    const nextRawPointDeltas = { ...normalizeJacketPointDeltaMap(jacketPointDeltas) };
    selectedJacketFreePoints.forEach((pointName) => {
      const existing = nextRawPointDeltas[pointName] || { x: 0, y: 0 };
      const updated = {
        x: roundToSixteenth((Number(existing.x) || 0) + dx),
        y: roundToSixteenth((Number(existing.y) || 0) + dy),
      };

      if (Math.abs(updated.x) < 1e-8 && Math.abs(updated.y) < 1e-8) {
        delete nextRawPointDeltas[pointName];
      } else {
        nextRawPointDeltas[pointName] = updated;
      }
    });

    const effectivePointDeltas = resolveEffectiveJacketPointDeltas({
      mode: 'free',
      deltas: nextRawPointDeltas,
      baselineDeltas: jacketBaselinePointDeltas,
    });

    setJacketPointDeltas((prev) => {
      const next = { ...normalizeJacketPointDeltaMap(prev) };
      selectedJacketFreePoints.forEach((pointName) => {
        const existing = next[pointName] || { x: 0, y: 0 };
        const updated = {
          x: roundToSixteenth((Number(existing.x) || 0) + dx),
          y: roundToSixteenth((Number(existing.y) || 0) + dy),
        };

        if (Math.abs(updated.x) < 1e-8 && Math.abs(updated.y) < 1e-8) {
          delete next[pointName];
        } else {
          next[pointName] = updated;
        }
      });
      return next;
    });

    setHasUnsavedChanges(true);
    setHasGeometryChanges(true);
    markGeometryAsFreeEdited();
    setModifiedParams((prev) => Array.from(new Set([
      ...prev,
      'jacketPointDeltas',
    ])));
  };

  const handleTrouserFreePointSelectionChange = (nextSelection = []) => {
    const sanitized = Array.from(new Set((nextSelection || []).filter(Boolean)));
    setSelectedTrouserFreePoints(sanitized);
  };

  const handleClearTrouserFreePointSelection = () => {
    setSelectedTrouserFreePoints([]);
  };

  const handleTrouserNudgeSelectedPoints = (deltaX = 0, deltaY = 0) => {
    if ((currentPattern?.garment_type || '').toLowerCase() !== 'trouser') return;
    if (trouserCreationMode !== 'free') return;
    if (!selectedTrouserFreePoints.length) return;

    const dx = roundToSixteenth(Number(deltaX) || 0);
    const dy = roundToSixteenth(Number(deltaY) || 0);
    if (Math.abs(dx) < 1e-8 && Math.abs(dy) < 1e-8) return;

    setTrouserPointDeltas((prev) => {
      const next = { ...normalizeJacketPointDeltaMap(prev) };
      selectedTrouserFreePoints.forEach((pointName) => {
        const existing = next[pointName] || { x: 0, y: 0 };
        const updated = {
          x: roundToSixteenth((Number(existing.x) || 0) + dx),
          y: roundToSixteenth((Number(existing.y) || 0) + dy),
        };

        if (Math.abs(updated.x) < 1e-8 && Math.abs(updated.y) < 1e-8) {
          delete next[pointName];
        } else {
          next[pointName] = updated;
        }
      });
      return next;
    });

    setHasUnsavedChanges(true);
    setHasGeometryChanges(true);
    markGeometryAsFreeEdited();
    setModifiedParams((prev) => Array.from(new Set([
      ...prev,
      'trouserPointDeltas',
    ])));
  };

  const handleShirtFreePointSelectionChange = (nextSelection = []) => {
    const sanitized = Array.from(new Set((nextSelection || []).filter(Boolean)));
    setSelectedShirtFreePoints(sanitized);
  };

  const handleClearShirtFreePointSelection = () => {
    setSelectedShirtFreePoints([]);
  };

  const handleShirtNudgeSelectedPoints = (deltaX = 0, deltaY = 0) => {
    if ((currentPattern?.garment_type || '').toLowerCase() !== 'shirt') return;
    if (shirtCreationMode !== 'free') return;
    if (!selectedShirtFreePoints.length) return;

    const rawDx = roundToSixteenth(Number(deltaX) || 0);
    const dy = roundToSixteenth(Number(deltaY) || 0);
    const collarLayoutYOnly = new Set(['CbB0', 'CbB1']);
    const collarLeafXOnly = new Set(['ClB0']);
    if (Math.abs(rawDx) < 1e-8 && Math.abs(dy) < 1e-8) return;

    const movedBackEdge = selectedShirtFreePoints.some((pointName) => collarLayoutYOnly.has(pointName));
    const b0Selected = selectedShirtFreePoints.includes('CbB0');
    const b1Selected = selectedShirtFreePoints.includes('CbB1');
    let nextBackHeight = null;
    if (b1Selected && !b0Selected) {
      const layoutY = (name) => {
        const point = (patternData?.namedPoints || []).find((entry) => entry.piece === 'collarLayout' && entry.name === name);
        return point ? -Number(point.y) : null;
      };
      const y0 = layoutY('CbB0');
      let y1 = layoutY('CbB1');
      if (Number.isFinite(y1)) y1 -= dy;
      if (Number.isFinite(y0) && Number.isFinite(y1)) {
        nextBackHeight = Math.max(0.25, roundToSixteenth(Math.abs(y1 - y0)));
      }
    }

    setShirtPointDeltas((prev) => {
      const next = { ...normalizeJacketPointDeltaMap(prev) };
      if (movedBackEdge && next.CbB1) {
        delete next.CbB1;
      }
      selectedShirtFreePoints.forEach((pointName) => {
        if (pointName === 'CbB1') return;
        const existing = next[pointName] || { x: 0, y: 0 };
        const dx = collarLayoutYOnly.has(pointName) ? 0 : rawDx;
        const nextDy = collarLeafXOnly.has(pointName) ? 0 : dy;
        const updated = {
          x: roundToSixteenth((Number(existing.x) || 0) + dx),
          y: roundToSixteenth((Number(existing.y) || 0) + nextDy),
        };

        if (Math.abs(updated.x) < 1e-8 && Math.abs(updated.y) < 1e-8) {
          delete next[pointName];
        } else {
          next[pointName] = updated;
        }
      });
      return next;
    });

    if (movedBackEdge || Number.isFinite(nextBackHeight)) {
      setMeasurements((prev) => ({
        ...prev,
        ...(Number.isFinite(nextBackHeight) ? { collarBandBackHeight: nextBackHeight } : {}),
        collarBandBackManual: 1,
      }));
    }

    setHasUnsavedChanges(true);
    setHasGeometryChanges(true);
    markGeometryAsFreeEdited();
    setModifiedParams((prev) => Array.from(new Set([
      ...prev,
      'shirtPointDeltas',
      ...(movedBackEdge ? ['collarBandBackManual'] : []),
      ...(Number.isFinite(nextBackHeight) ? ['collarBandBackHeight'] : []),
    ])));
  };

  const handleResetShirtCurveHandles = () => {
    const resetValues = SHIRT_CURVE_HANDLE_KEYS.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});

    handleCurveHandleChange(resetValues);
  };

  const handleRecomputeShirtCollarBand = () => {
    if ((currentPattern?.garment_type || '').toLowerCase() !== 'shirt') return;
    const solved = recomputeShirtCollarBandV3(measurements, {
      geometryVersion: resolvePatternGeometryVersion(currentPattern),
      formulaVersion: resolveFormulaVersion('shirt', currentPattern),
      freeModeActive: shirtCreationMode === 'free',
      pointDeltas: shirtPointDeltas,
    });
    if (!solved) {
      toast.error('Could not recompute the collar band.');
      return;
    }

    const backMoved = Math.abs(Number(measurements.collarBandBackManual) || 0) > 1e-8
      || ['CbB0', 'CbB1'].some((name) => {
        const delta = shirtPointDeltas?.[name] || {};
        return Math.abs(Number(delta.x) || 0) > 1e-8 || Math.abs(Number(delta.y) || 0) > 1e-8;
      });
    if (!backMoved && solved.pointDeltas) {
      setShirtPointDeltas((prev) => {
        const next = { ...normalizeJacketPointDeltaMap(prev) };
        next.CbB0 = solved.pointDeltas.CbB0;
        delete next.CbB1;
        return next;
      });
    }
    handleCurveHandleChange(solved.measurements);
    setModifiedParams((prev) => Array.from(new Set([
      ...prev,
      'shirtPointDeltas',
      ...Object.keys(solved.measurements),
    ])));
    toast.success('Collar band recomputed.');
  };

  const handleResetJacketCurveHandles = () => {
    const resetValues = JACKET_CURVE_HANDLE_KEYS.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});

    handleCurveHandleChange(resetValues);
  };

  const handleRecomputeJacketCollar = () => {
    if ((currentPattern?.garment_type || '').toLowerCase() !== 'jacket') return;

    const stripCollarPointDeltas = (source = {}) => {
      const next = { ...normalizeJacketPointDeltaMap(source) };
      JACKET_COLLAR_FREE_POINT_KEYS.forEach((pointName) => {
        delete next[pointName];
      });
      return next;
    };

    const nextPointDeltas = stripCollarPointDeltas(jacketPointDeltas);
    const nextBaselinePointDeltas = stripCollarPointDeltas(jacketBaselinePointDeltas);
    const changed = JSON.stringify(nextPointDeltas) !== JSON.stringify(normalizeJacketPointDeltaMap(jacketPointDeltas))
      || JSON.stringify(nextBaselinePointDeltas) !== JSON.stringify(normalizeJacketPointDeltaMap(jacketBaselinePointDeltas));

    if (!changed) {
      toast.info('Collar is already using the computed geometry.');
      return;
    }

    setJacketPointDeltas(nextPointDeltas);
    setJacketBaselinePointDeltas(nextBaselinePointDeltas);
    setSelectedJacketFreePoints((prev) => prev.filter((pointName) => !JACKET_COLLAR_FREE_POINT_KEYS.includes(pointName)));
    setHasUnsavedChanges(true);
    setHasGeometryChanges(true);
    markGeometryAsFreeEdited();
    setModifiedParams((prev) => Array.from(new Set([
      ...prev,
      'jacketPointDeltas',
      'jacketBaselinePointDeltas',
    ])));
    toast.success('Collar recomputed from the current draft.');
  };

  const handleAutoShirtSideSeam = () => {
    if ((currentPattern?.garment_type || 'trouser') !== 'shirt') return;
    const payload = buildAutoShirtSideSeamPayload(measurements, currentPattern || {});

    if (Object.keys(payload).length === 0) return;

    setMeasurements((prev) => ({
      ...prev,
      ...payload,
    }));
    setHasUnsavedChanges(true);
    setHasGeometryChanges(true);
    markGeometryAsFreeEdited();
    setModifiedParams((prev) => {
      const next = new Set(prev);
      Object.keys(payload).forEach((key) => next.add(key));
      return Array.from(next);
    });
  };

  const applyShirtCurveOffsetReset = (offsetKeys = []) => {
    if ((currentPattern?.garment_type || 'trouser') !== 'shirt') return;
    if (!Array.isArray(offsetKeys) || offsetKeys.length === 0) return;

    const payload = offsetKeys.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});

    setMeasurements((prev) => ({
      ...prev,
      ...payload,
    }));
    setHasUnsavedChanges(true);
    setHasGeometryChanges(true);
    markGeometryAsFreeEdited();
    setModifiedParams((prev) => {
      const next = new Set(prev);
      offsetKeys.forEach((key) => next.add(key));
      return Array.from(next);
    });
  };

  const handleAutoShirtBackYokeDartCurve = () => {
    applyShirtCurveOffsetReset(SHIRT_YOKE_DART_OFFSET_KEYS.back);
  };

  const handleAutoShirtYokePanelDartCurve = () => {
    applyShirtCurveOffsetReset(SHIRT_YOKE_DART_OFFSET_KEYS.panel);
  };

  const handleAutoShirtArmholeCurves = () => {
    applyShirtCurveOffsetReset(SHIRT_ARMHOLE_OFFSET_KEYS);
  };

  const handleAutoShirtSleeveCurvesFpBp = () => {
    if ((currentPattern?.garment_type || 'trouser') !== 'shirt') return;
    const payload = buildAutoShirtSleeveCurvesPayload(measurements, currentPattern || {});

    if (Object.keys(payload).length === 0) return;

    setMeasurements((prev) => ({
      ...prev,
      ...payload,
    }));
    setHasUnsavedChanges(true);
    setHasGeometryChanges(true);
    markGeometryAsFreeEdited();
    setModifiedParams((prev) => {
      const next = new Set(prev);
      Object.keys(payload).forEach((key) => next.add(key));
      return Array.from(next);
    });
  };

  // ========== FILE ACTIONS ==========
  
  const checkUnsavedChanges = (action) => {
    if (hasUnsavedChanges && currentPattern) {
      setPendingAction(action);
      setUnsavedChangesModalOpen(true);
      return false;
    }
    return true;
  };

  const handleLoadPattern = () => {
    if (!checkUnsavedChanges('load')) return;
    setLoadPatternModalOpen(true);
  };

  const handleSiteModeChange = async (nextMode) => {
    if (!canManageSiteMode) {
      alert('Only owner can change site mode.');
      return;
    }

    if (!window.confirm(`Switch site mode to ${nextMode.toUpperCase()}?`)) return;

    try {
      await updateSiteMode(nextMode);
      await refreshSiteMode();
      alert(`Site mode switched to ${nextMode.toUpperCase()}.`);
    } catch (siteModeError) {
      alert(siteModeError.message || 'Unable to update site mode.');
    }
  };

  const handleManagePatterns = () => {
    if (!canManagePatterns) {
      alert('You do not have permission to manage patterns.');
      return;
    }
    if (!checkUnsavedChanges('manage_patterns')) return;
    setCurrentView('manage_patterns');
  };

  const resetWorkspaceToHome = () => {
    setCurrentPattern(null);
    setCurrentView('pattern');
    setHasUnsavedChanges(false);
    setHasGeometryChanges(false);
    setModifiedParams([]);
    setSelectedVersions([]);
    setComparisonPatterns([]);
    setSelectedCurveId(null);
    setIsPrintPreviewMode(false);
    setPreviewLayoutPieces([]);
    setRightSidebarOpen(false);
    setDraftingNotice('');
  };

  const handleGoHome = () => {
    if (!checkUnsavedChanges('home')) return;
    resetWorkspaceToHome();
  };

  const handleUnlockPattern = async () => {
    if (!currentPattern?.id || currentPattern.isNew) {
      setPatternEditLocked(false);
      lastPatternActivityRef.current = Date.now();
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/patterns/unlock/${encodeURIComponent(currentPattern.id)}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = payload.detail;
        const message = typeof detail === 'string'
          ? detail
          : (Array.isArray(detail) ? (detail[0]?.msg || 'Unable to unlock pattern') : 'Unable to unlock pattern');
        throw new Error(message);
      }
      lastPatternActivityRef.current = Date.now();
      setPatternEditLocked(false);
      setCurrentPattern((prev) => (prev ? { ...prev, ...payload, edit_locked: false } : prev));
    } catch (unlockError) {
      alert(unlockError.message || 'Unable to unlock pattern');
    }
  };

  const openPatternHandoffTab = (payload) => {
    const randomPart = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const handoffId = String(randomPart);
    const storageKey = `${PATTERN_TAB_HANDOFF_PREFIX}${handoffId}`;

    try {
      localStorage.setItem(storageKey, JSON.stringify(payload));
      const targetUrl = new URL(window.location.origin + window.location.pathname);
      targetUrl.searchParams.set(PATTERN_TAB_HANDOFF_QUERY, handoffId);
      const newTab = window.open(targetUrl.toString(), '_blank');

      if (!newTab) {
        localStorage.removeItem(storageKey);
        alert('The new pattern tab was blocked. Please allow pop-ups for PatternEngine and try again.');
        return false;
      }

      newTab.opener = null;
      return true;
    } catch (handoffError) {
      localStorage.removeItem(storageKey);
      alert(handoffError.message || 'Unable to open the pattern in a new tab.');
      return false;
    }
  };

  const handlePatternGeometryVersionChange = (nextGeometryVersion) => {
    if (!currentPattern || !nextGeometryVersion) return;

    const garmentType = currentPattern.garment_type || 'trouser';
    const nextFormulaVersion = resolveFormulaVersion(garmentType, {
      geometry_version: nextGeometryVersion,
    });

    setCurrentPattern((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        geometry_version: nextGeometryVersion,
        formula_version: nextFormulaVersion,
      };
    });
    setMeasurements((prev) => applyGeometryScopedDefaults(garmentType, nextGeometryVersion, prev));
    setGeometryEditState('parametric');
    setHasUnsavedChanges(true);
    setHasGeometryChanges(true);
  };

  const buildLoadedMeasurementsFromPattern = (resolvedPattern, options = {}) => {
    const { requiredMeasurements = {} } = options;
    const defaultsForGarment = getDefaultMeasurements(resolvedPattern?.garment_type || 'trouser');
    let loadedMeasurements = {
      ...defaultsForGarment,
      ...(resolvedPattern?.measurements || {}),
      ...(requiredMeasurements || {}),
    };

    if (String(resolvedPattern?.garment_type || '').toLowerCase() === 'shirt') {
      loadedMeasurements = preserveLegacyShirtSleeveValuesForLoadedPattern(loadedMeasurements, resolvedPattern?.measurements || {});
    }

    if (String(resolvedPattern?.garment_type || '').toLowerCase() === 'jacket') {
      loadedMeasurements = applyJacketBellyProfileRule(loadedMeasurements, { preserveExistingManualValues: true });
      loadedMeasurements = applyJacketPostureRule(loadedMeasurements, { preserveExistingManualValues: true });
      loadedMeasurements = applyJacketPitchRotationRule(loadedMeasurements, { preserveExistingManualValues: true });
      loadedMeasurements = applyJacketDartModeRule(loadedMeasurements, { preserveExistingManualValues: true });
      loadedMeasurements = applyJacketCurveDefaultRule(loadedMeasurements, { preserveExistingManualValues: true });
      loadedMeasurements = {
        ...loadedMeasurements,
        overrideAcrossBackDepthEnabled: Boolean(resolvedPattern?.measurements?.overrideAcrossBackDepthEnabled),
        overrideAcrossFrontDepthEnabled: Boolean(resolvedPattern?.measurements?.overrideAcrossFrontDepthEnabled),
        overrideAcrossBackDepth: Number(resolvedPattern?.measurements?.overrideAcrossBackDepth || 0),
        overrideAcrossFrontDepth: Number(resolvedPattern?.measurements?.overrideAcrossFrontDepth || 0),
      };
      loadedMeasurements = reconcileJacketMeasurementsToSnapshotGeometry(resolvedPattern, loadedMeasurements);
    }

    return loadedMeasurements;
  };

  const openExactDuplicatePattern = async (draftInfo = {}) => {
    const sourcePatternId = draftInfo?.sourcePattern?.id || draftInfo?.patternId;
    if (!sourcePatternId) {
      throw new Error('Duplicate source pattern is missing its ID. Return to Manage Patterns and try again.');
    }

    const resolvedPattern = await fetchPatternById(sourcePatternId);
    const loadedMeasurements = buildLoadedMeasurementsFromPattern(resolvedPattern);
    const duplicateGeometryVersion = draftInfo?.geometry_version
      || resolvePatternGeometryVersion(resolvedPattern)
      || getGarmentConfig(resolvedPattern?.garment_type || 'trouser')?.geometryVersion;
    const duplicateFormulaVersion = resolveFormulaVersion(resolvedPattern?.garment_type || 'trouser', {
      geometry_version: duplicateGeometryVersion,
    });
    const duplicateVersionNumber = String(draftInfo?.pattern_number || '').split('-')[1]
      || draftInfo?.version_number
      || resolvedPattern.version_number;
    const normalizedSnapshot = normalizeComparisonPatternData(
      resolvedPattern?.pattern_data,
      resolvedPattern?.garment_type || 'trouser',
      {
        formulaVersion: duplicateFormulaVersion,
        geometryVersion: duplicateGeometryVersion,
      },
    );
    const duplicateRequestedMeasurements = resolvePatternRequestedMeasurements(resolvedPattern, loadedMeasurements);
    const duplicateGeometryEditState = resolvePatternGeometryEditState(resolvedPattern);
    const duplicateDraftPattern = {
      ...resolvedPattern,
      id: undefined,
      client_number: draftInfo?.client_number || resolvedPattern.client_number,
      client_name: draftInfo?.client_name || resolvedPattern.client_name,
      garment_type: draftInfo?.garment_type || resolvedPattern.garment_type,
      pattern_number: draftInfo?.pattern_number || resolvedPattern.pattern_number,
      version_number: duplicateVersionNumber,
      geometry_version: duplicateGeometryVersion,
      formula_version: duplicateFormulaVersion,
      notes: draftInfo?.notes ?? resolvedPattern.notes ?? null,
      isNew: true,
    };

    setMeasurements(loadedMeasurements);
    setRequestedMeasurements(duplicateRequestedMeasurements);
    setGeometryEditState(duplicateGeometryEditState);
    setPatternData(hasRenderablePatternSnapshot(normalizedSnapshot) ? normalizedSnapshot : null);
    setModifiedParams([]);
    setCurrentPattern(duplicateDraftPattern);
    initializeFreeEditModeStateFromPattern(resolvedPattern);
    setIsOverwriteMode(false);
    setHasGeometryChanges(false);
    setHasUnsavedChanges(false);
    setPatternAutosaveStatus('saving');
    setPatternAutosaveError('');
    setSelectedVersions([]);
    setComparisonPatterns([]);
    setSelectedCurveId(null);
    setIsPrintPreviewMode(false);
    setPreviewLayoutPieces([]);
    setRightSidebarOpen(true);
    setCanvasViewportResetToken((prev) => prev + 1);
    setCurrentView('pattern');

    try {
      await persistExactDuplicatePattern({
        duplicateDraftPattern,
        loadedMeasurements,
        duplicateRequestedMeasurements,
        duplicateGeometryEditState,
        normalizedSnapshot,
        sourcePattern: resolvedPattern,
      });
    } catch (duplicateSaveError) {
      console.error('Exact duplicate save failed', duplicateSaveError);
      setHasUnsavedChanges(true);
      setPatternAutosaveStatus('error');
      setPatternAutosaveError(duplicateSaveError.message || 'Duplicate save failed');
      alert(duplicateSaveError.message || 'The duplicate opened, but the initial save failed.');
    }
  };

  async function persistExactDuplicatePattern({
    duplicateDraftPattern,
    loadedMeasurements,
    duplicateRequestedMeasurements,
    duplicateGeometryEditState,
    normalizedSnapshot,
    sourcePattern,
  }) {
    const payload = {
      pattern_number: duplicateDraftPattern.pattern_number,
      client_number: duplicateDraftPattern.client_number,
      client_name: duplicateDraftPattern.client_name,
      garment_type: duplicateDraftPattern.garment_type,
      measurements: loadedMeasurements,
      parameters: {
        backCrotchScoopX: loadedMeasurements?.backCrotchScoopX,
        backCrotchScoopY: loadedMeasurements?.backCrotchScoopY,
        requested_measurements: duplicateRequestedMeasurements,
        geometry_edit_state: duplicateGeometryEditState,
      },
      pattern_data: JSON.parse(JSON.stringify({
        ...(normalizedSnapshot || {}),
        formulaVersion: duplicateDraftPattern.formula_version,
        geometryVersion: duplicateDraftPattern.geometry_version,
        garmentType: duplicateDraftPattern.garment_type,
      })),
      export_layout: sourcePattern?.export_layout || null,
      point_deltas: sourcePattern?.point_deltas || null,
      formula_version: duplicateDraftPattern.formula_version,
      geometry_version: duplicateDraftPattern.geometry_version,
      notes: duplicateDraftPattern.notes || null,
    };

    const response = await fetch(`${API_BASE}/api/patterns`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to save duplicate pattern');
    }

    const savedPattern = await response.json();
    const persistedPattern = {
      ...savedPattern,
      isNew: false,
    };

    setCurrentPattern(persistedPattern);
    setPatternData(hasRenderablePatternSnapshot(normalizedSnapshot) ? normalizedSnapshot : null);
    setIsOverwriteMode(true);
    setHasUnsavedChanges(false);
    setHasGeometryChanges(false);
    setIsPatternAutosaveDirty(false);
    setPatternAutosaveStatus('saved');
    setPatternAutosaveError('');
    setPatternAutosaveUpdatedAt(Date.now());
    setLastSaveTime(Date.now());
    setShowSaveWarning(false);
    return persistedPattern;
  }

  const handleNewPatternSuccess = (patternInfo) => {
    const garmentConfig = getGarmentConfig(patternInfo.garment_type);
    const sourcePattern = patternInfo.sourcePattern || null;
    const selectedGeometryVersion = String(patternInfo.geometry_version || '').trim();
    const resolvedGeometryVersion = selectedGeometryVersion
      || garmentConfig?.geometryVersion
      || getGarmentConfig(patternInfo.garment_type)?.geometryVersion;

    let nextDefaults = getDefaultMeasurements(patternInfo.garment_type);
    if (sourcePattern?.measurements && typeof sourcePattern.measurements === 'object') {
      nextDefaults = {
        ...nextDefaults,
        ...sourcePattern.measurements,
      };
    } else if (patternInfo.garment_type === 'shirt') {
      const shoulder = Number(nextDefaults.shoulder) || 0;
      nextDefaults = {
        ...nextDefaults,
        acrossBack: roundToEighth(Math.max(0, shoulder - 1)),
        acrossFront: roundToEighth(Math.max(0, shoulder - 2)),
      };
    }

    if (patternInfo?.requiredMeasurements && typeof patternInfo.requiredMeasurements === 'object') {
      nextDefaults = {
        ...nextDefaults,
        ...patternInfo.requiredMeasurements,
      };

      nextDefaults = applyGeometryScopedDefaults(
        patternInfo.garment_type,
        resolvedGeometryVersion,
        nextDefaults,
        { isFreshDraft: !sourcePattern },
      );

      if (String(patternInfo?.garment_type || '').toLowerCase() === 'shirt') {
        nextDefaults = applyShirtPortlySplitRule(nextDefaults, {
          geometryVersion: resolvedGeometryVersion,
        });
        const shirtDraftPatternMeta = {
          garment_type: 'shirt',
          geometry_version: resolvedGeometryVersion,
          formula_version: resolveFormulaVersion('shirt', { geometry_version: resolvedGeometryVersion }),
        };
        if (sourcePattern) {
          nextDefaults = preserveLegacyShirtSleeveValuesForLoadedPattern(nextDefaults, sourcePattern.measurements || {});
        } else {
          nextDefaults = applyShirtNewDraftSleevePreset(nextDefaults);
          nextDefaults = {
            ...nextDefaults,
            ...SHIRT_SIDE_SEAM_OFFSET_KEYS.reduce((acc, key) => {
              acc[key] = 0;
              return acc;
            }, {}),
          };
        }
      } else if (String(patternInfo?.garment_type || '').toLowerCase() === 'jacket') {
        nextDefaults = applyJacketBellyProfileRule(nextDefaults);
        nextDefaults = applyJacketPostureRule(nextDefaults);
        nextDefaults = applyJacketPitchRotationRule(nextDefaults);
        nextDefaults = applyJacketDartModeRule(nextDefaults);
        nextDefaults = applyJacketCurveDefaultRule(nextDefaults);
        nextDefaults = {
          ...nextDefaults,
          overrideAcrossBackDepthEnabled: false,
          overrideAcrossFrontDepthEnabled: false,
          overrideAcrossBackDepth: 0,
          overrideAcrossFrontDepth: 0,
          jacketBxbDx: 0,
          jacketFxfDx: 0,
          jacketBxbDy: 0,
          jacketFxfDy: 0,
        };
      }
    } else {
      nextDefaults = applyGeometryScopedDefaults(
        patternInfo.garment_type,
        resolvedGeometryVersion,
        nextDefaults,
        { isFreshDraft: !sourcePattern },
      );
    }

    // Create a new pattern with the given info and default measurements
    setCurrentPattern({
      pattern_number: patternInfo.pattern_number,
      client_number: patternInfo.client_number,
      client_name: patternInfo.client_name,
      garment_type: patternInfo.garment_type,
      version_number: patternInfo.version_number,
      geometry_version: resolvedGeometryVersion,
      formula_version: resolveFormulaVersion(patternInfo.garment_type, {
        geometry_version: resolvedGeometryVersion,
      }),
      point_deltas: patternInfo?.sourcePattern?.point_deltas || null,
      isNew: true // Mark as new (not yet saved)
    });

    initializeFreeEditModeStateFromPattern({
      garment_type: patternInfo.garment_type,
      point_deltas: patternInfo?.sourcePattern?.point_deltas || null,
    });
    
    setMeasurements(nextDefaults);
    setRequestedMeasurements(pickRequestedMeasurementValues(patternInfo.garment_type, nextDefaults));
    setGeometryEditState('parametric');
    setHasGeometryChanges(false);
    setModifiedParams([]);
    setHasUnsavedChanges(false);
    setIsOverwriteMode(false);
    setRightSidebarOpen(true);
    setSelectedVersions([]);
    setComparisonPatterns([]);
    setSelectedCurveId(null);
    setIsPrintPreviewMode(false);
    setPreviewLayoutPieces([]);
    setCanvasViewportResetToken((prev) => prev + 1);
    setCurrentView('pattern');
  };

  const handleLoadPatternFromManage = async (pattern, options = {}) => {
    if (!pattern) return;

    if (!options.direct) {
      if (!pattern.id) {
        alert('Unable to open this saved pattern because its ID is missing.');
        return;
      }
      openPatternHandoffTab({
        type: 'open-existing',
        patternId: pattern.id,
      });
      return;
    }

    let resolvedPattern = pattern;

    try {
      if (pattern?.id && !pattern?.pattern_data) {
        resolvedPattern = await fetchPatternById(pattern.id);
      }
    } catch (loadError) {
      alert(loadError.message || 'Failed to load pattern details.');
      return;
    }

    const loadedMeasurements = buildLoadedMeasurementsFromPattern(resolvedPattern, {
      requiredMeasurements: options.requiredMeasurements,
    });
    setMeasurements(loadedMeasurements);
    setRequestedMeasurements(options.requiredMeasurements
      ? pickRequestedMeasurementValues(resolvedPattern.garment_type || 'trouser', options.requiredMeasurements, loadedMeasurements)
      : resolvePatternRequestedMeasurements(resolvedPattern, loadedMeasurements));
    setGeometryEditState(resolvePatternGeometryEditState(resolvedPattern));
    setModifiedParams([]);

    const overwriteFormulaVersion = resolvePatternFormulaVersion({
      ...resolvedPattern,
      isNew: false,
    });
    const overwriteGeometryVersion = resolvePatternGeometryVersion({
      ...resolvedPattern,
      isNew: false,
    });

    initializeFreeEditModeStateFromPattern(resolvedPattern);
    setHasGeometryChanges(false);
    setCurrentPattern({
      ...resolvedPattern,
      formula_version: overwriteFormulaVersion,
      geometry_version: overwriteGeometryVersion,
      isNew: false,
    });

    setIsOverwriteMode(true);
    setHasUnsavedChanges(false);
    setSelectedVersions([]);
    setComparisonPatterns([]);
    setSelectedCurveId(null);
    setIsPrintPreviewMode(false);
    setPreviewLayoutPieces([]);
    setRightSidebarOpen(true);
    setCanvasViewportResetToken((prev) => prev + 1);
    setCurrentView('pattern');
  };

  const handleStartDraftFromManage = (draftInfo) => {
    if (draftInfo?.duplicateExact) {
      const opened = openPatternHandoffTab({
        type: 'duplicate-existing',
        draftInfo: {
          ...draftInfo,
          sourcePattern: draftInfo?.sourcePattern?.id
            ? { id: draftInfo.sourcePattern.id }
            : draftInfo?.sourcePattern,
        },
      });
      if (!opened) return;
      setPendingDraftInfo(null);
      setDraftMeasurementsModalOpen(false);
      return;
    }

    const garment = String(draftInfo?.garment_type || '').toLowerCase();
    if (garment === 'shirt' || garment === 'trouser' || garment === 'jacket') {
      setPendingDraftInfo({
        ...draftInfo,
        handoffAction: 'new-draft',
        requiredMeasurements: garment === 'jacket'
          ? {
              ...(draftInfo?.requiredMeasurements || {}),
              ...(draftInfo?.bellyProfile ? { bellyProfile: draftInfo.bellyProfile } : {}),
              ...(draftInfo?.posture ? { posture: draftInfo.posture } : {}),
            }
          : draftInfo?.requiredMeasurements,
      });
      setDraftMeasurementsModalOpen(true);
      return;
    }

    handleNewPatternSuccess(draftInfo);
  };

  const handleConfirmDraftMeasurements = (requiredMeasurements) => {
    if (!pendingDraftInfo) return;
    const handoffPayload = {
      type: 'new-draft',
      draftInfo: {
        ...pendingDraftInfo,
        handoffAction: undefined,
        requiredMeasurements,
      },
    };

    const opened = openPatternHandoffTab(handoffPayload);
    if (!opened) return;

    setDraftMeasurementsModalOpen(false);
    setPendingDraftInfo(null);
  };

  patternTabHandoffActionsRef.current = {
    loadExisting: handleLoadPatternFromManage,
    openDraft: handleNewPatternSuccess,
    openDuplicate: openExactDuplicatePattern,
  };

  useEffect(() => {
    if (!token || patternTabHandoffConsumedRef.current) return;

    const currentUrl = new URL(window.location.href);
    const handoffId = currentUrl.searchParams.get(PATTERN_TAB_HANDOFF_QUERY);
    if (!handoffId) return;

    patternTabHandoffConsumedRef.current = true;
    const storageKey = `${PATTERN_TAB_HANDOFF_PREFIX}${handoffId}`;
    const rawPayload = localStorage.getItem(storageKey);
    localStorage.removeItem(storageKey);
    currentUrl.searchParams.delete(PATTERN_TAB_HANDOFF_QUERY);
    window.history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);

    const consumeHandoff = async () => {
      if (!rawPayload) throw new Error('Pattern tab handoff expired. Return to Manage Patterns and try again.');
      const payload = JSON.parse(rawPayload);

      if (payload.type === 'open-existing') {
        if (!payload.patternId) throw new Error('Pattern tab handoff is missing the pattern ID.');
        await patternTabHandoffActionsRef.current.loadExisting(
          { id: payload.patternId },
          { direct: true, requiredMeasurements: payload.requiredMeasurements },
        );
        return;
      }

      if (payload.type === 'new-draft' && payload.draftInfo) {
        patternTabHandoffActionsRef.current.openDraft(payload.draftInfo);
        return;
      }

      if (payload.type === 'duplicate-existing' && payload.draftInfo) {
        await patternTabHandoffActionsRef.current.openDuplicate(payload.draftInfo);
        return;
      }

      throw new Error('Pattern tab handoff is invalid.');
    };

    consumeHandoff().catch((handoffError) => {
      console.error('Pattern tab handoff failed', handoffError);
      alert(handoffError.message || 'Unable to open the pattern in this tab.');
    });
  }, [token]);

  const handleSelectPatternToLoad = (pattern) => {
    setSelectedPatternForAction(pattern);
    setLoadPatternModalOpen(false);
    setActionConfirmModalOpen(true);
  };

  const handleActionConfirm = async ({ action, pattern, newPatternNumber, geometryVersion, bellyProfile, posture }) => {
    let resolvedPattern = pattern;

    try {
      if (pattern?.id && !pattern?.pattern_data) {
        resolvedPattern = await fetchPatternById(pattern.id);
      }
    } catch (loadError) {
      alert(loadError.message || 'Failed to load pattern details.');
      return;
    }

    // Load the pattern's measurements
    const defaultsForGarment = getDefaultMeasurements(resolvedPattern.garment_type || 'trouser');
    let loadedMeasurements = {
      ...defaultsForGarment,
      ...(resolvedPattern.measurements || {}),
    };
    if (String(resolvedPattern.garment_type || '').toLowerCase() === 'shirt') {
      loadedMeasurements = preserveLegacyShirtSleeveValuesForLoadedPattern(loadedMeasurements, resolvedPattern.measurements || {});
    }
    if (String(resolvedPattern.garment_type || '').toLowerCase() === 'jacket') {
      loadedMeasurements = applyJacketBellyProfileRule(loadedMeasurements, { preserveExistingManualValues: true });
      loadedMeasurements = applyJacketPostureRule(loadedMeasurements, { preserveExistingManualValues: true });
      loadedMeasurements = applyJacketPitchRotationRule(loadedMeasurements, { preserveExistingManualValues: true });
      loadedMeasurements = applyJacketDartModeRule(loadedMeasurements, { preserveExistingManualValues: true });
      loadedMeasurements = applyJacketCurveDefaultRule(loadedMeasurements, { preserveExistingManualValues: true });
      loadedMeasurements = {
        ...loadedMeasurements,
        overrideAcrossBackDepthEnabled: Boolean(resolvedPattern.measurements?.overrideAcrossBackDepthEnabled),
        overrideAcrossFrontDepthEnabled: Boolean(resolvedPattern.measurements?.overrideAcrossFrontDepthEnabled),
        overrideAcrossBackDepth: Number(resolvedPattern.measurements?.overrideAcrossBackDepth || 0),
        overrideAcrossFrontDepth: Number(resolvedPattern.measurements?.overrideAcrossFrontDepth || 0),
      };
      loadedMeasurements = reconcileJacketMeasurementsToSnapshotGeometry(resolvedPattern, loadedMeasurements);
    }
    setMeasurements(loadedMeasurements);
    setRequestedMeasurements(resolvePatternRequestedMeasurements(resolvedPattern, loadedMeasurements));
    setGeometryEditState(resolvePatternGeometryEditState(resolvedPattern));
    setHasGeometryChanges(false);
    setModifiedParams([]);
    
    if (action === 'overwrite') {
      // Edit & Overwrite mode
      const overwriteFormulaVersion = resolvePatternFormulaVersion({
        ...resolvedPattern,
        isNew: false,
      });
      const overwriteGeometryVersion = resolvePatternGeometryVersion({
        ...resolvedPattern,
        isNew: false,
      });

      setCurrentPattern({
        ...resolvedPattern,
        formula_version: overwriteFormulaVersion,
        geometry_version: overwriteGeometryVersion,
        isNew: false
      });
      initializeFreeEditModeStateFromPattern(resolvedPattern);
      setIsOverwriteMode(true);
      setHasUnsavedChanges(false);
      setSelectedVersions([]);
      setComparisonPatterns([]);
      setSelectedCurveId(null);
      setIsPrintPreviewMode(false);
      setPreviewLayoutPieces([]);
    } else {
      // Create New Revision mode
      const revisionGarmentConfig = getGarmentConfig(resolvedPattern.garment_type || 'trouser');
      const revisionGeometryVersion = geometryVersion
        || resolvePatternGeometryVersion(resolvedPattern)
        || revisionGarmentConfig.geometryVersion;
      const revisionFormulaVersion = resolveFormulaVersion(resolvedPattern.garment_type || 'trouser', {
        geometry_version: revisionGeometryVersion,
      });

      setCurrentPattern({
        ...resolvedPattern,
        pattern_number: newPatternNumber,
        version_number: newPatternNumber.split('-')[1],
        formula_version: revisionFormulaVersion,
        geometry_version: revisionGeometryVersion,
        isNew: true
      });
      const revisionMeasurementsBase = {
        ...defaultsForGarment,
        ...(resolvedPattern.measurements || {}),
        ...(String(resolvedPattern.garment_type || '').toLowerCase() === 'jacket' && bellyProfile
          ? { bellyProfile }
          : {}),
        ...(String(resolvedPattern.garment_type || '').toLowerCase() === 'jacket' && posture
          ? { posture }
          : {}),
      };
      const revisionMeasurements = String(resolvedPattern.garment_type || '').toLowerCase() === 'jacket'
        ? applyJacketCurveDefaultRule(
            applyJacketDartModeRule(applyJacketPitchRotationRule(applyJacketPostureRule(applyJacketBellyProfileRule(revisionMeasurementsBase)))),
            { preserveExistingManualValues: true },
          )
        : revisionMeasurementsBase;
      setMeasurements(revisionMeasurements);
      setRequestedMeasurements(resolvePatternRequestedMeasurements(resolvedPattern, revisionMeasurements));
      setGeometryEditState(resolvePatternGeometryEditState(resolvedPattern));
      initializeFreeEditModeStateFromPattern({
        ...resolvedPattern,
        measurements: revisionMeasurements,
      });
      setIsOverwriteMode(false);
      setHasGeometryChanges(false);
      setHasUnsavedChanges(false);
      setSelectedVersions([]);
      setComparisonPatterns([]);
      setSelectedCurveId(null);
      setIsPrintPreviewMode(false);
      setPreviewLayoutPieces([]);
    }
    
    setRightSidebarOpen(true);
    setCanvasViewportResetToken((prev) => prev + 1);
    setActionConfirmModalOpen(false);
  };

  const handleUnsavedChangesSave = async () => {
    const saved = await handleSave();
    if (!saved) return;
    setUnsavedChangesModalOpen(false);
    
    // Execute the pending action
    if (pendingAction === 'load') {
      setLoadPatternModalOpen(true);
    } else if (pendingAction === 'new_version') {
      setLoadPatternModalOpen(true);
    } else if (pendingAction === 'manage_patterns') {
      setCurrentView('manage_patterns');
    } else if (pendingAction === 'home') {
      resetWorkspaceToHome();
    }
    
    setPendingAction(null);
  };

  const handleUnsavedChangesDiscard = () => {
    setHasUnsavedChanges(false);
    setHasGeometryChanges(false);
    setUnsavedChangesModalOpen(false);
    
    // Execute the pending action
    if (pendingAction === 'load') {
      setLoadPatternModalOpen(true);
    } else if (pendingAction === 'new_version') {
      setLoadPatternModalOpen(true);
    } else if (pendingAction === 'manage_patterns') {
      setCurrentView('manage_patterns');
    } else if (pendingAction === 'home') {
      resetWorkspaceToHome();
    }
    
    setPendingAction(null);
  };

  // ========== SAVE LOGIC ==========

  const buildPatternSavePayload = (pattern, payloadOverrides = {}) => {
    const payloadMeasurements = payloadOverrides.measurements || measurements;
    const payloadJacketCreationMode = payloadOverrides.jacketCreationMode || jacketCreationMode;
    const payloadJacketPointDeltas = payloadOverrides.jacketPointDeltas || jacketPointDeltas;
    const payloadJacketBaselinePointDeltas = payloadOverrides.jacketBaselinePointDeltas || jacketBaselinePointDeltas;
    const payloadTrouserCreationMode = payloadOverrides.trouserCreationMode || trouserCreationMode;
    const payloadTrouserPointDeltas = payloadOverrides.trouserPointDeltas || trouserPointDeltas;
    const payloadTrouserBaselinePointDeltas = payloadOverrides.trouserBaselinePointDeltas || trouserBaselinePointDeltas;
    const payloadShirtCreationMode = payloadOverrides.shirtCreationMode || shirtCreationMode;
    const payloadShirtPointDeltas = payloadOverrides.shirtPointDeltas || shirtPointDeltas;
    const payloadShirtBaselinePointDeltas = payloadOverrides.shirtBaselinePointDeltas || shirtBaselinePointDeltas;
    const payloadRequestedMeasurements = pickRequestedMeasurementValues(
      pattern.garment_type || 'trouser',
      payloadOverrides.requestedMeasurements || requestedMeasurements,
      payloadMeasurements,
    );
    const payloadGeometryEditState = payloadOverrides.geometryEditState || geometryEditState;
    const payloadHasGeometryChanges = payloadOverrides.hasGeometryChanges ?? hasGeometryChanges;
    const garmentType = pattern.garment_type || 'trouser';
    const formulaVersion = resolvePatternFormulaVersion(pattern);
    const geometryVersion = resolvePatternGeometryVersion(pattern);
    const isJacket = garmentType.toLowerCase() === 'jacket';
    const isTrouser = garmentType.toLowerCase() === 'trouser';
    const isShirt = garmentType.toLowerCase() === 'shirt';
    const effectivePointDeltas = isJacket
      ? resolveEffectiveJacketPointDeltas({
          mode: payloadJacketCreationMode,
          deltas: payloadJacketPointDeltas,
          baselineDeltas: payloadJacketBaselinePointDeltas,
        })
      : (isTrouser
        ? resolveEffectiveTrouserPointDeltas({
            mode: payloadTrouserCreationMode,
            deltas: payloadTrouserPointDeltas,
            baselineDeltas: payloadTrouserBaselinePointDeltas,
          })
        : (isShirt
          ? resolveEffectiveShirtPointDeltas({
              mode: payloadShirtCreationMode,
              deltas: payloadShirtPointDeltas,
              baselineDeltas: payloadShirtBaselinePointDeltas,
            })
          : {}));

    const snapshotSource = !payloadHasGeometryChanges && hasRenderablePatternSnapshot(patternData)
      ? patternData
      : generatePattern(payloadMeasurements, {
        backCrotchScoopX: payloadMeasurements.backCrotchScoopX,
        backCrotchScoopY: payloadMeasurements.backCrotchScoopY,
        formulaVersion,
        geometryVersion,
        freeModeActive: (isJacket && payloadJacketCreationMode === 'free')
          || (isTrouser && payloadTrouserCreationMode === 'free')
          || (isShirt && payloadShirtCreationMode === 'free'),
        pointDeltas: effectivePointDeltas,
      }, garmentType);

    const payloadPatternData = JSON.parse(JSON.stringify({
      ...snapshotSource,
      formulaVersion,
      geometryVersion,
      garmentType,
    }));

    return ({
    pattern_number: pattern.pattern_number,
    client_number: pattern.client_number,
    client_name: pattern.client_name,
    garment_type: pattern.garment_type,
    measurements: payloadMeasurements,
    parameters: {
      backCrotchScoopX: payloadMeasurements.backCrotchScoopX,
      backCrotchScoopY: payloadMeasurements.backCrotchScoopY,
      requested_measurements: payloadRequestedMeasurements,
      geometry_edit_state: payloadGeometryEditState,
    },
    pattern_data: payloadPatternData,
    export_layout: pattern.export_layout || null,
    point_deltas: (pattern?.garment_type || '').toLowerCase() === 'jacket'
      ? {
          mode: payloadJacketCreationMode,
          deltas: normalizeJacketPointDeltaMap(payloadJacketPointDeltas),
          baseline_deltas: normalizeJacketPointDeltaMap(payloadJacketBaselinePointDeltas),
        }
      : ((pattern?.garment_type || '').toLowerCase() === 'trouser'
        ? {
            mode: payloadTrouserCreationMode,
            deltas: normalizeJacketPointDeltaMap(payloadTrouserPointDeltas),
            baseline_deltas: normalizeJacketPointDeltaMap(payloadTrouserBaselinePointDeltas),
          }
        : ((pattern?.garment_type || '').toLowerCase() === 'shirt'
          ? {
              mode: payloadShirtCreationMode,
              deltas: normalizeJacketPointDeltaMap(payloadShirtPointDeltas),
              baseline_deltas: normalizeJacketPointDeltaMap(payloadShirtBaselinePointDeltas),
            }
          : null)),
    formula_version: formulaVersion,
    geometry_version: geometryVersion,
    notes: pattern.notes || null,
  });
  };

  const buildExportLayoutPayload = (diagnostics) => ({
    pieces: previewLayoutPieces.map((piece) => ({
      key: piece.key,
      visible: piece.visible !== false,
      x: piece.x,
      y: piece.y,
      rotation: piece.rotation,
      flipHorizontal: Boolean(piece.flipHorizontal),
      flipVertical: Boolean(piece.flipVertical),
      labelInsetInches: resolvePieceLabelInsetInches(piece.key, piece.labelInsetInches),
      labelOffsetXmm: Number(piece.labelOffsetXmm) || 0,
      labelOffsetYmm: Number(piece.labelOffsetYmm) || 0,
      useDefaultLabel: piece.useDefaultLabel !== false,
      labelTextOverride: String(piece.labelTextOverride || ''),
      labelFontPt: Number(piece.labelFontPt) || DEFAULT_PRINT_PIECE_SETTINGS.labelFontPt,
    })),
    cutLengthInches: Number(previewCutLengthInches) > 0 ? Number(previewCutLengthInches) : diagnostics.effectiveCutLengthInches,
    updatedAt: new Date().toISOString(),
  });

  const handleSave = async (options = {}) => {
    const {
      silent = false,
      source = 'manual',
      force = false,
      payloadOverrides = null,
    } = options;

    if (!canSavePatternVersion) {
      if (!silent) {
        alert('You do not have permission to save pattern versions.');
      }
      return false;
    }

    if (patternEditLocked && currentPattern?.id && !currentPattern?.isNew) {
      if (!silent) {
        alert('This pattern is locked. Click Unlock to edit it.');
      }
      return false;
    }

    if (!currentPattern) {
      if (!silent) {
        alert('No pattern loaded');
      }
      return false;
    }

    const payload = buildPatternSavePayload(currentPattern, payloadOverrides || {});
    const isUpdatingExisting = Boolean((isOverwriteMode && !currentPattern.isNew) || currentPattern.id);
    const saveSignature = JSON.stringify({
      patternId: currentPattern.id || null,
      mode: isUpdatingExisting ? 'update' : 'create',
      payload,
    });
    const saveEditVersion = patternEditVersionRef.current;

    if (!force && source === 'auto' && saveSignature === patternLastSavedSignatureRef.current) {
      setHasUnsavedChanges(false);
      setIsPatternAutosaveDirty(false);
      return true;
    }

    if (patternSaveInFlightRef.current) {
      patternQueuedSaveRef.current = {
        silent: source !== 'manual',
        source: source === 'manual' ? 'manual' : 'auto',
        force: true,
        payloadOverrides: payloadOverrides || null,
      };

      if (source === 'manual') {
        setPatternAutosaveStatus('saving');
        setPatternAutosaveError('');
      }
      return false;
    }

    patternSaveInFlightRef.current = true;
    if (source === 'auto') {
      setPatternAutosaveStatus('saving');
      setPatternAutosaveError('');
    }

    try {
      let persistedPattern = currentPattern;

      if (isUpdatingExisting) {
        const response = await fetch(`${API_BASE}/api/patterns/${currentPattern.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to update pattern');
        }

        const updatedPattern = await response.json();
        persistedPattern = {
          ...updatedPattern,
          isNew: false,
        };

        setCurrentPattern(persistedPattern);

        if (!silent) {
          alert(`Pattern ${currentPattern.pattern_number} updated successfully!`);
        }
      } else {
        const response = await fetch(`${API_BASE}/api/patterns`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to save pattern');
        }

        const savedPattern = await response.json();
        persistedPattern = {
          ...savedPattern,
          isNew: false,
        };

        setCurrentPattern(persistedPattern);
        setIsOverwriteMode(true);

        if (!silent) {
          alert(`Pattern ${savedPattern.pattern_number} saved successfully!`);
        }
      }

      const persistedSignature = JSON.stringify({
        patternId: persistedPattern.id || currentPattern.id || null,
        mode: persistedPattern.isNew ? 'create' : 'update',
        payload: buildPatternSavePayload(persistedPattern, payloadOverrides || {}),
      });
      const newerEditsQueuedDuringSave = patternEditVersionRef.current !== saveEditVersion
        || Boolean(patternQueuedSaveRef.current);

      patternLastSavedSignatureRef.current = persistedSignature;
      if (!newerEditsQueuedDuringSave) {
        setHasUnsavedChanges(false);
        setHasGeometryChanges(false);
        setIsPatternAutosaveDirty(false);
      } else {
        setIsPatternAutosaveDirty(true);
      }
      setLastSaveTime(Date.now());
      setShowSaveWarning(false);
      setPatternAutosaveStatus(newerEditsQueuedDuringSave ? 'idle' : 'saved');
      setPatternAutosaveError('');
      setPatternAutosaveUpdatedAt(Date.now());
      return true;
    } catch (err) {
      if (source === 'auto') {
        setPatternAutosaveStatus('error');
        setPatternAutosaveError(err.message || 'Autosave failed');
        if (shouldShowAutosaveErrorToast(patternErrorToastAtRef)) {
          toast.error('Pattern autosave failed. Retrying automatically.');
        }
      }
      if (!silent) {
        alert(`Error saving pattern: ${err.message}`);
      }
      return false;
    } finally {
      patternSaveInFlightRef.current = false;
      const queuedSave = patternQueuedSaveRef.current;
      if (queuedSave) {
        patternQueuedSaveRef.current = null;
        Promise.resolve().then(() => {
          handleSave(queuedSave);
        });
      }
    }
  };

  const handlePatternDeleted = (deletedPattern) => {
    // Return to welcome screen
    setCurrentPattern(null);
    setMeasurements(getDefaultMeasurements('trouser'));
    setRequestedMeasurements(pickRequestedMeasurementValues('trouser', getDefaultMeasurements('trouser')));
    setGeometryEditState('parametric');
    setHasGeometryChanges(false);
    setHasUnsavedChanges(false);
    setRightSidebarOpen(false);
    setModifiedParams([]);
    setPatternData(null);
    setDraftingNotice('');
    setSelectedVersions([]);
    setComparisonPatterns([]);
    setSelectedCurveId(null);
    setIsPrintPreviewMode(false);
    setPreviewLayoutPieces([]);
    setJacketCreationMode('free');
    setJacketPointDeltas({});
    setJacketBaselinePointDeltas({});
    setSelectedJacketFreePoints([]);
    setTrouserCreationMode('free');
    setTrouserPointDeltas({});
    setTrouserBaselinePointDeltas({});
    setSelectedTrouserFreePoints([]);
    setShirtCreationMode('free');
    setShirtPointDeltas({});
    setShirtBaselinePointDeltas({});
    setSelectedShirtFreePoints([]);
  };

  const initializePrintPreview = (targetPattern = currentPattern) => {
    if (!targetPattern || !patternData) return;

    const savedLayout = targetPattern.export_layout;
    const basePieces = createDefaultLayout(patternData);

    let initialPieces;
    if (savedLayout?.pieces?.length) {
      initialPieces = basePieces.map((basePiece) => {
        const savedPiece = savedLayout.pieces.find((p) => p.key === basePiece.key);
        const pieceDefaults = basePiece;
        if (!savedPiece) {
          return resolvePiecePrintSettings({
            ...basePiece,
            visible: (basePiece.key === 'collar' || basePiece.key === 'collarLeaf') ? false : basePiece.visible,
          }, pieceDefaults);
        }

        return resolvePiecePrintSettings({
          ...basePiece,
          visible: (basePiece.key === 'collar' || basePiece.key === 'collarLeaf')
            ? savedPiece.visible === true
            : savedPiece.visible !== false,
          x: Number(savedPiece.x ?? basePiece.x),
          y: Number(savedPiece.y ?? basePiece.y),
          rotation: Number(savedPiece.rotation ?? basePiece.rotation ?? 0),
          flipHorizontal: Boolean(savedPiece.flipHorizontal),
          flipVertical: Boolean(savedPiece.flipVertical),
          labelInsetInches: resolvePieceLabelInsetInches(
            basePiece.key,
            savedPiece.labelInsetInches ?? basePiece.labelInsetInches,
          ),
          useDefaultLabel: savedPiece.useDefaultLabel,
          labelTextOverride: savedPiece.labelTextOverride,
          labelFontPt: savedPiece.labelFontPt,
          labelOffsetXmm: savedPiece.labelOffsetXmm,
          labelOffsetYmm: savedPiece.labelOffsetYmm,
        }, pieceDefaults);
      });
    } else {
      initialPieces = applyResolvedLabelInsets(compactArrangeLayout(patternData, basePieces), basePieces);
    }

    const initialCutLength = Number(savedLayout?.cutLengthInches) > 0
      ? Number(savedLayout.cutLengthInches)
      : 0;

    const normalizedPieces = clampLayoutPiecesToViewport(
      patternData,
      applyResolvedLabelInsets(initialPieces, basePieces),
      initialCutLength,
      8,
    );

    setPreviewLayoutPieces(normalizedPieces);
    setPreviewCutLengthInches(initialCutLength);
    setIsLayoutAutosaveDirty(false);
    setLayoutAutosaveStatus('idle');
    setLayoutAutosaveError('');

    const initialDiagnostics = getLayoutDiagnostics(patternData, normalizedPieces, initialCutLength);
    const initialLayoutSignature = JSON.stringify({
      patternId: targetPattern.id || null,
      exportLayout: {
        pieces: normalizedPieces.map((piece) => ({
          key: piece.key,
          visible: piece.visible !== false,
          x: piece.x,
          y: piece.y,
          rotation: piece.rotation,
          flipHorizontal: Boolean(piece.flipHorizontal),
          flipVertical: Boolean(piece.flipVertical),
          labelInsetInches: resolvePieceLabelInsetInches(piece.key, piece.labelInsetInches),
          labelOffsetXmm: Number(piece.labelOffsetXmm) || 0,
          labelOffsetYmm: Number(piece.labelOffsetYmm) || 0,
          useDefaultLabel: piece.useDefaultLabel !== false,
          labelTextOverride: String(piece.labelTextOverride || ''),
          labelFontPt: Number(piece.labelFontPt) || DEFAULT_PRINT_PIECE_SETTINGS.labelFontPt,
        })),
        cutLengthInches: Number(initialCutLength) > 0 ? Number(initialCutLength) : initialDiagnostics.effectiveCutLengthInches,
      },
    });

    layoutLastSavedSignatureRef.current = initialLayoutSignature;
  };

  const setPreviewLayoutPiecesWithAutosave = (updater) => {
    setPreviewLayoutPieces((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return next;
    });
    setIsLayoutAutosaveDirty(true);
    setLayoutAutosaveStatus('idle');
    setLayoutAutosaveError('');
  };

  const setPreviewCutLengthInchesWithAutosave = (nextValue) => {
    setPreviewCutLengthInches(nextValue);
    setIsLayoutAutosaveDirty(true);
    setLayoutAutosaveStatus('idle');
    setLayoutAutosaveError('');
  };

  const handleExitPrintPreview = async () => {
    if (isLayoutAutosaveDirty) {
      await handleSaveExportLayoutSettings({
        silent: true,
        source: 'auto',
        force: true,
      });
    }
    setIsPrintPreviewMode(false);
  };

  const handleTogglePrintPreview = () => {
    if (!currentPattern || !patternData) {
      alert('Load a pattern before entering print preview mode.');
      return;
    }

    if (!isPrintPreviewMode) {
      initializePrintPreview(currentPattern);
      setIsPrintPreviewMode(true);
    } else {
      handleExitPrintPreview();
    }
  };

  const handleExport = async (previewSvgElement = null, exportOptions = {}) => {
    if (!currentPattern) {
      alert('No pattern loaded');
      return;
    }

    if (!isPrintPreviewMode) {
      initializePrintPreview(currentPattern);
      setIsPrintPreviewMode(true);
      return;
    }

    const diagnostics = getLayoutDiagnostics(patternData, previewLayoutPieces, previewCutLengthInches);
    if (diagnostics.warnings.length > 0) {
      alert(`Fix border warnings before export:\n- ${diagnostics.warnings.join('\n- ')}`);
      return;
    }

    try {
      await generatePDF({
        patternData,
        currentPattern,
        measurements,
        controls: {
          backCrotchScoopX: measurements.backCrotchScoopX,
          backCrotchScoopY: measurements.backCrotchScoopY,
          formulaVersion: resolvePatternFormulaVersion(currentPattern),
          geometryVersion: resolvePatternGeometryVersion(currentPattern),
        },
        layoutConfig: {
          pieces: previewLayoutPieces,
          cutLengthInches: previewCutLengthInches,
        },
        previewSvgElement,
        exportOptions,
      });
    } catch (error) {
      console.error('Vector PDF export failed, trying preview-image fallback', error);
      const exportedFromPreview = await generatePDFFromPreviewSVG({
        previewSvgElement,
        currentPattern,
        cutLengthInches: previewCutLengthInches,
        requiredLengthInches: diagnostics.requiredLengthInches,
      });

      if (!exportedFromPreview) {
        alert('PDF export failed. Please try again after adjusting the print layout.');
      }
    }
  };

  const handleSaveExportLayoutSettings = async (options = {}) => {
    const {
      silent = false,
      source = 'manual',
      force = false,
    } = options;

    if (!canSavePrintLayout) {
      if (!silent) {
        alert('You do not have permission to save print settings.');
      }
      return false;
    }

    if (!currentPattern?.id) {
      const message = 'Save this pattern version first, then save print settings.';
      if (source === 'auto') {
        setLayoutAutosaveStatus('error');
        setLayoutAutosaveError(message);
      }
      if (!silent) {
        alert(message);
      }
      return false;
    }

    const diagnostics = getLayoutDiagnostics(patternData, previewLayoutPieces, previewCutLengthInches);
    if (diagnostics.warnings.length > 0) {
      const warningMessage = `Cannot save layout with border warnings: ${diagnostics.warnings.join(', ')}`;
      if (source === 'auto') {
        setLayoutAutosaveStatus('error');
        setLayoutAutosaveError(warningMessage);
      }
      if (!silent) {
        alert(`Cannot save layout with border warnings:\n- ${diagnostics.warnings.join('\n- ')}`);
      }
      return false;
    }

    const exportLayout = buildExportLayoutPayload(diagnostics);
    const saveSignature = JSON.stringify({
      patternId: currentPattern.id,
      exportLayout,
    });

    if (!force && source === 'auto' && saveSignature === layoutLastSavedSignatureRef.current) {
      setIsLayoutAutosaveDirty(false);
      return true;
    }

    if (layoutSaveInFlightRef.current) {
      layoutQueuedSaveRef.current = {
        silent: source !== 'manual',
        source: source === 'manual' ? 'manual' : 'auto',
        force: true,
      };

      if (source === 'manual') {
        setLayoutAutosaveStatus('saving');
        setLayoutAutosaveError('');
      }
      return false;
    }

    layoutSaveInFlightRef.current = true;
    if (source === 'auto') {
      setLayoutAutosaveStatus('saving');
      setLayoutAutosaveError('');
    }

    try {
      const response = await fetch(`${API_BASE}/api/patterns/${currentPattern.id}/export-layout`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ export_layout: exportLayout }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Failed to save print settings');

      layoutLastSavedSignatureRef.current = saveSignature;
      setCurrentPattern((prev) => ({
        ...prev,
        export_layout: exportLayout,
      }));
      setIsLayoutAutosaveDirty(false);
      setLayoutAutosaveStatus('saved');
      setLayoutAutosaveError('');
      setLayoutAutosaveUpdatedAt(Date.now());

      if (!silent) {
        alert('Print preview settings saved.');
      }

      return true;
    } catch (err) {
      if (source === 'auto') {
        setLayoutAutosaveStatus('error');
        setLayoutAutosaveError(err.message || 'Autosave failed');
        if (shouldShowAutosaveErrorToast(layoutErrorToastAtRef)) {
          toast.error('Print preview autosave failed. Retrying automatically.');
        }
      }
      if (!silent) {
        alert(`Error saving print settings: ${err.message}`);
      }
      return false;
    } finally {
      layoutSaveInFlightRef.current = false;
      const queuedSave = layoutQueuedSaveRef.current;
      if (queuedSave) {
        layoutQueuedSaveRef.current = null;
        Promise.resolve().then(() => {
          handleSaveExportLayoutSettings(queuedSave);
        });
      }
    }
  };

  useEffect(() => {
    if (!currentPattern || hasUnsavedChanges) return;

    const payload = buildPatternSavePayload(currentPattern);
    const isUpdatingExisting = Boolean((isOverwriteMode && !currentPattern.isNew) || currentPattern.id);
    patternLastSavedSignatureRef.current = JSON.stringify({
      patternId: currentPattern.id || null,
      mode: isUpdatingExisting ? 'update' : 'create',
      payload,
    });
  }, [
    currentPattern,
    hasUnsavedChanges,
    isOverwriteMode,
    measurements,
    requestedMeasurements,
    geometryEditState,
    hasGeometryChanges,
    jacketCreationMode,
    jacketPointDeltas,
    jacketBaselinePointDeltas,
    trouserCreationMode,
    trouserPointDeltas,
    trouserBaselinePointDeltas,
    shirtCreationMode,
    shirtPointDeltas,
    shirtBaselinePointDeltas,
  ]);

  useEffect(() => {
    if (!currentPattern || !canSavePatternVersion || patternEditLocked || !isPatternAutosaveDirty) return;
    const autosaveDelay = Math.max(AUTOSAVE_DEBOUNCE_MS, patternAutosaveHoldUntil - Date.now(), 0);
    const timeoutId = window.setTimeout(() => {
      handleSave({
        silent: true,
        source: 'auto',
      });
    }, autosaveDelay);

    return () => window.clearTimeout(timeoutId);
  }, [
    currentPattern,
    measurements,
    requestedMeasurements,
    geometryEditState,
    hasGeometryChanges,
    isPatternAutosaveDirty,
    patternAutosaveHoldUntil,
    canSavePatternVersion,
    isOverwriteMode,
    jacketCreationMode,
    jacketPointDeltas,
    jacketBaselinePointDeltas,
    trouserCreationMode,
    trouserPointDeltas,
    trouserBaselinePointDeltas,
    shirtCreationMode,
    shirtPointDeltas,
    shirtBaselinePointDeltas,
  ]);

  useEffect(() => {
    if (!currentPattern || !canSavePatternVersion || patternEditLocked || !isPatternAutosaveDirty) return;

    const intervalId = window.setInterval(() => {
      handleSave({
        silent: true,
        source: 'auto',
      });
    }, AUTOSAVE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [
    currentPattern,
    isPatternAutosaveDirty,
    requestedMeasurements,
    geometryEditState,
    hasGeometryChanges,
    canSavePatternVersion,
    isOverwriteMode,
    jacketCreationMode,
    jacketPointDeltas,
    jacketBaselinePointDeltas,
    trouserCreationMode,
    trouserPointDeltas,
    trouserBaselinePointDeltas,
    shirtCreationMode,
    shirtPointDeltas,
    shirtBaselinePointDeltas,
  ]);

  useEffect(() => {
    if (!isPrintPreviewMode || !currentPattern || !canSavePrintLayout || !isLayoutAutosaveDirty) return;

    const timeoutId = window.setTimeout(() => {
      handleSaveExportLayoutSettings({
        silent: true,
        source: 'auto',
      });
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    isPrintPreviewMode,
    currentPattern,
    canSavePrintLayout,
    isLayoutAutosaveDirty,
    previewLayoutPieces,
    previewCutLengthInches,
    patternData,
  ]);

  useEffect(() => {
    if (!isPrintPreviewMode || !currentPattern || !canSavePrintLayout || !isLayoutAutosaveDirty) return;

    const intervalId = window.setInterval(() => {
      handleSaveExportLayoutSettings({
        silent: true,
        source: 'auto',
      });
    }, AUTOSAVE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isPrintPreviewMode, currentPattern, canSavePrintLayout, isLayoutAutosaveDirty]);

  useEffect(() => {
    const previousView = previousViewRef.current;
    if (previousView === 'pattern' && currentView !== 'pattern') {
      if (isPatternAutosaveDirty) {
        handleSave({
          silent: true,
          source: 'auto',
          force: true,
        });
      }

      if (isPrintPreviewMode && isLayoutAutosaveDirty) {
        handleSaveExportLayoutSettings({
          silent: true,
          source: 'auto',
          force: true,
        });
      }
    }

    previousViewRef.current = currentView;
  }, [
    currentView,
    isPatternAutosaveDirty,
    isPrintPreviewMode,
    isLayoutAutosaveDirty,
  ]);

  const handleCompactArrangePreview = () => {
    setPreviewLayoutPiecesWithAutosave((prev) => {
      const visibleKeys = new Set(prev.filter((piece) => piece.visible !== false).map((piece) => piece.key));
      if (visibleKeys.size <= 1) {
        return prev;
      }

      const seeded = prev.map((piece) => {
        if (piece.key === 'front') {
          return {
            ...piece,
            rotation: -6,
            flipHorizontal: true,
            flipVertical: true,
          };
        }

        if (piece.key === 'back') {
          return {
            ...piece,
            rotation: -6,
            flipHorizontal: false,
            flipVertical: false,
          };
        }

        return piece;
      });

      const styleMap = new Map(seeded.map((piece) => [piece.key, piece]));
      const filteredPatternData = {
        ...patternData,
        frontPath: visibleKeys.has('front') ? patternData?.frontPath : '',
        sidePath: visibleKeys.has('side') ? patternData?.sidePath : '',
        backPath: visibleKeys.has('back') ? patternData?.backPath : '',
        yokePath: visibleKeys.has('yoke') ? patternData?.yokePath : '',
        collarBandPath: visibleKeys.has('collar') ? patternData?.collarBandPath : '',
        collarBandLayoutPath: visibleKeys.has('collar') ? patternData?.collarBandLayoutPath : '',
        collarLeafLayoutPath: visibleKeys.has('collar') ? patternData?.collarLeafLayoutPath : '',
        collarLeafPath: visibleKeys.has('collar') ? patternData?.collarLeafPath : '',
        sleevePath: visibleKeys.has('sleeve') ? patternData?.sleevePath : '',
      };
      const compactArranged = compactArrangeLayout(filteredPatternData, seeded.filter((piece) => visibleKeys.has(piece.key)));
      const arranged = applyResolvedLabelInsets(
        seeded.map((piece) => (visibleKeys.has(piece.key)
          ? {
              ...styleMap.get(piece.key),
              ...(compactArranged.find((arrangedPiece) => arrangedPiece.key === piece.key) || {}),
            }
          : piece)),
        seeded,
      );
      const required = estimateRollLengthInches(patternData, arranged);
      const recommendedCutLength = getRecommendedCutLengthInches(required, 0.5);
      const effectiveCutForViewport = Number(previewCutLengthInches) > 0 ? Number(previewCutLengthInches) : 0;
      const normalized = clampLayoutPiecesToViewport(patternData, arranged, effectiveCutForViewport, 8);
      if (Number(previewCutLengthInches) > 0 && Number(previewCutLengthInches) < recommendedCutLength) {
        setPreviewCutLengthInchesWithAutosave(recommendedCutLength);
      }
      return normalized;
    });
  };

  // ========== RENDER ==========

  if (currentView === 'profile') {
    return <Profile onBack={() => setCurrentView('pattern')} />;
  }

  if (currentView === 'admin' && canAccessUserManagement) {
    return <AdminPanel onBack={() => setCurrentView('pattern')} />;
  }

  if (currentView === 'admin' && !canAccessUserManagement) {
    return <Profile onBack={() => setCurrentView('pattern')} />;
  }

  if (currentView === 'manage_patterns') {
    return (
      <>
        <ManagePatterns
          token={token}
          user={user}
          onBack={() => setCurrentView('pattern')}
          onOpenPattern={handleLoadPatternFromManage}
          onStartPatternDraft={handleStartDraftFromManage}
        />
        <DraftMeasurementsModal
          open={draftMeasurementsModalOpen}
          garmentType={pendingDraftInfo?.garment_type || ''}
          geometryVersion={pendingDraftInfo?.geometry_version || ''}
          baseMeasurements={(() => {
            if (!pendingDraftInfo) return {};
            const sourcePattern = pendingDraftInfo?.sourcePattern;
            const defaults = getDefaultMeasurements(pendingDraftInfo.garment_type || 'trouser');
            const seeded = sourcePattern?.measurements
              ? { ...defaults, ...sourcePattern.measurements }
              : defaults;
            return {
              ...seeded,
              ...(pendingDraftInfo?.requiredMeasurements || {}),
            };
          })()}
          onOpenChange={(nextOpen) => {
            setDraftMeasurementsModalOpen(nextOpen);
            if (!nextOpen) setPendingDraftInfo(null);
          }}
          onCancel={() => setPendingDraftInfo(null)}
          onConfirm={handleConfirmDraftMeasurements}
          confirmLabel="Open Draft in New Tab"
        />
      </>
    );
  }

  if (currentView === 'backup_management' && canAccessBackupManagement) {
    return <BackupManagement onBack={() => setCurrentView('pattern')} />;
  }

  if (currentView === 'backup_management' && !canAccessBackupManagement) {
    return <Profile onBack={() => setCurrentView('pattern')} />;
  }

  // Show welcome screen if no pattern is loaded
  if (!currentPattern) {
    return (
      <>
        <WelcomeScreen
          onManagePatterns={handleManagePatterns}
          onUserManagement={() => {
            if (!canAccessUserManagement) {
              alert('You do not have permission to access user management.');
              return;
            }
            setCurrentView('admin');
          }}
          onBackupManagement={() => {
            if (!canAccessBackupManagement) {
              alert('You do not have permission to access backup management.');
              return;
            }
            setCurrentView('backup_management');
          }}
          onSiteModeChange={handleSiteModeChange}
          onLogout={logout}
          canManagePatterns={canManagePatterns}
          canAccessUserManagement={canAccessUserManagement}
          canAccessBackupManagement={canAccessBackupManagement}
          canManageSiteMode={canManageSiteMode}
          siteMode={siteMode}
          siteModeUpdatedAt={siteModeMeta?.updatedAt || null}
          siteModeUpdatedBy={siteModeMeta?.updatedByEmail || null}
        />
        
        <LoadPatternModal
          open={loadPatternModalOpen}
          onOpenChange={(open) => {
            setLoadPatternModalOpen(open);
            if (!open) setPendingAction(null);
          }}
          onSelectPattern={handleSelectPatternToLoad}
          token={token}
        />
        
        <ActionConfirmModal
          open={actionConfirmModalOpen}
          onOpenChange={setActionConfirmModalOpen}
          pattern={selectedPatternForAction}
          onConfirm={handleActionConfirm}
          token={token}
          canSavePatternVersion={canSavePatternVersion}
        />

        <DraftMeasurementsModal
          open={draftMeasurementsModalOpen}
          garmentType={pendingDraftInfo?.garment_type || ''}
          geometryVersion={pendingDraftInfo?.geometry_version || ''}
          baseMeasurements={(() => {
            if (!pendingDraftInfo) return {};
            const sourcePattern = pendingDraftInfo?.sourcePattern;
            const defaults = getDefaultMeasurements(pendingDraftInfo.garment_type || 'trouser');
            const seeded = sourcePattern?.measurements
              ? { ...defaults, ...sourcePattern.measurements }
              : defaults;
            return {
              ...seeded,
              ...(pendingDraftInfo?.requiredMeasurements || {}),
            };
          })()}
          onOpenChange={(nextOpen) => {
            setDraftMeasurementsModalOpen(nextOpen);
            if (!nextOpen) setPendingDraftInfo(null);
          }}
          onCancel={() => setPendingDraftInfo(null)}
          onConfirm={handleConfirmDraftMeasurements}
          confirmLabel="Open Draft in New Tab"
        />
      </>
    );
  }

  // Main Pattern Engine UI
  const currentGarmentConfig = getGarmentConfig(currentPattern?.garment_type || 'trouser');
  const currentGarmentType = String(currentPattern?.garment_type || 'trouser').toLowerCase();
  const isJacketEditingEnabled = currentGarmentType === 'jacket';
  const isTrouserEditingEnabled = currentGarmentType === 'trouser';
  const isShirtEditingEnabled = currentGarmentType === 'shirt';
  const isFreePointEditingEnabled = isJacketEditingEnabled || isTrouserEditingEnabled || isShirtEditingEnabled;
  const activeSelectedFreePoints = isJacketEditingEnabled
    ? selectedJacketFreePoints
    : (isTrouserEditingEnabled ? selectedTrouserFreePoints : selectedShirtFreePoints);
  const handleActiveFreePointSelectionChange = isJacketEditingEnabled
    ? handleJacketFreePointSelectionChange
    : (isTrouserEditingEnabled ? handleTrouserFreePointSelectionChange : handleShirtFreePointSelectionChange);
  const handleClearActiveFreePointSelection = isJacketEditingEnabled
    ? handleClearJacketFreePointSelection
    : (isTrouserEditingEnabled ? handleClearTrouserFreePointSelection : handleClearShirtFreePointSelection);
  const handleNudgeActiveFreePoints = isJacketEditingEnabled
    ? handleJacketNudgeSelectedPoints
    : (isTrouserEditingEnabled ? handleTrouserNudgeSelectedPoints : handleShirtNudgeSelectedPoints);
  const leftPanelReferenceMeasurements = pickRequestedMeasurementValues(currentGarmentType, requestedMeasurements, measurements);

  return (
    <div className="flex flex-col h-screen w-full bg-background overflow-hidden">
      {/* Save Warning */}
      {showSaveWarning && (
        <div className="bg-yellow-500 text-white px-4 py-2 text-sm text-center font-medium" data-testid="save-warning-banner">
          ⚠️ You haven&apos;t saved in over 5 minutes. Consider saving your work.
        </div>
      )}

      {patternEditLocked && currentPattern && !currentPattern.isNew && (
        <div className="bg-amber-600 text-white px-4 py-2 text-sm text-center font-medium flex items-center justify-center gap-3" data-testid="pattern-edit-lock-banner">
          <span>This pattern is locked after 30 minutes without edits. Unlock it to make changes.</span>
          {canEditPattern && (
            <button
              type="button"
              className="rounded-md bg-white px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-50"
              onClick={handleUnlockPattern}
              data-testid="pattern-edit-unlock-banner-button"
            >
              Unlock
            </button>
          )}
        </div>
      )}

      {draftingNotice && (
        <div className="bg-slate-700 text-white px-4 py-2 text-sm text-center" data-testid="garment-drafting-notice-banner">
          {draftingNotice}
        </div>
      )}
      
      {/* Top Bar */}
      <TopBar
        currentPattern={currentPattern}
        onHomeClick={handleGoHome}
        onLoadPattern={handleLoadPattern}
        onManagePatterns={handleManagePatterns}
        onSave={handleSave}
        onExport={handleExport}
        onTogglePrintPreview={handleTogglePrintPreview}
        isPrintPreviewMode={isPrintPreviewMode}
        onProfileClick={() => setCurrentView('profile')}
        onAdminClick={() => {
          if (!canAccessUserManagement) {
            alert('You do not have permission to access user management.');
            return;
          }
          setCurrentView('admin');
        }}
        onBackupClick={() => {
          if (!canAccessBackupManagement) {
            alert('You do not have permission to access backup management.');
            return;
          }
          setCurrentView('backup_management');
        }}
        canManagePatterns={canManagePatterns}
        canSavePatternVersion={canSavePatternVersion && !patternEditLocked}
        patternEditLocked={patternEditLocked}
        onUnlockPattern={handleUnlockPattern}
        canAccessUserManagement={canAccessUserManagement}
        canAccessBackupManagement={canAccessBackupManagement}
        onGeometryVersionChange={handlePatternGeometryVersionChange}
      />

      <DraftMeasurementsModal
        open={draftMeasurementsModalOpen}
        garmentType={pendingDraftInfo?.garment_type || ''}
        geometryVersion={pendingDraftInfo?.geometry_version || ''}
        baseMeasurements={(() => {
          if (!pendingDraftInfo) return {};
          const sourcePattern = pendingDraftInfo?.sourcePattern;
          const defaults = getDefaultMeasurements(pendingDraftInfo.garment_type || 'trouser');
          const seeded = sourcePattern?.measurements
            ? { ...defaults, ...sourcePattern.measurements }
            : defaults;
          return {
            ...seeded,
            ...(pendingDraftInfo?.requiredMeasurements || {}),
          };
        })()}
        onOpenChange={(nextOpen) => {
          setDraftMeasurementsModalOpen(nextOpen);
          if (!nextOpen) setPendingDraftInfo(null);
        }}
        onCancel={() => setPendingDraftInfo(null)}
        onConfirm={handleConfirmDraftMeasurements}
        confirmLabel="Open Draft in New Tab"
      />
      
      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden relative">
        {isPrintPreviewMode ? (
          <PrintPreviewWorkspace
            patternData={patternData}
            currentPattern={currentPattern}
            measurements={measurements}
            layoutPieces={previewLayoutPieces}
            setLayoutPieces={setPreviewLayoutPiecesWithAutosave}
            cutLengthInches={previewCutLengthInches}
            setCutLengthInches={setPreviewCutLengthInchesWithAutosave}
            onCompactNesting={handleCompactArrangePreview}
            onSaveLayout={handleSaveExportLayoutSettings}
            canSaveLayout={canSavePrintLayout}
            autosaveStatus={layoutAutosaveStatus}
            autosaveMessage={layoutAutosaveMessage}
            onRetryAutosave={() => handleSaveExportLayoutSettings({
              silent: true,
              source: 'auto',
              force: true,
            })}
            onExport={handleExport}
            onExit={handleExitPrintPreview}
          />
        ) : (
          <>
            {/* Left Panel */}
            <LeftPanel
              measurements={measurements}
              referenceMeasurements={leftPanelReferenceMeasurements}
              onMeasurementChange={handleMeasurementChange}
              onAdjustDrawnMeasurement={handleAdjustDrawnMeasurement}
              onCommitDrawnMeasurement={handleCommitDrawnMeasurement}
              onResetCurveHandles={(currentPattern?.garment_type || 'trouser') === 'jacket' ? handleResetJacketCurveHandles : handleResetShirtCurveHandles}
              onRecomputeJacketCollar={handleRecomputeJacketCollar}
              onRecomputeShirtCollarBand={handleRecomputeShirtCollarBand}
              onAutoShirtSideSeam={handleAutoShirtSideSeam}
              onAutoShirtBackYokeDartCurve={handleAutoShirtBackYokeDartCurve}
              onAutoShirtYokePanelDartCurve={handleAutoShirtYokePanelDartCurve}
              onAutoShirtArmholeCurves={handleAutoShirtArmholeCurves}
              onAutoShirtSleeveCurvesFpBp={handleAutoShirtSleeveCurvesFpBp}
              modifiedParams={modifiedParams}
              canEdit={canEditLoadedPattern}
              garmentType={currentPattern?.garment_type || 'trouser'}
              garmentStatus={currentGarmentConfig.status}
              patternData={patternData}
              geometryEditState={geometryEditState}
              selectedFreePoints={activeSelectedFreePoints}
            />
            
            {/* Canvas */}
            <Canvas
              patternData={patternData}
              comparisonPatterns={comparisonPatterns}
              viewportResetToken={canvasViewportResetToken}
              measurements={measurements}
              zoom={zoom}
              setZoom={setZoom}
              showFront={showFront}
              showSide={showSide}
              showBack={showBack}
              showYoke={showYoke}
              showCollar={showCollar}
              showSleeve={showSleeve}
              showMeasurements={showMeasurements}
              showPoints={showPoints}
              seamWalkMode={seamWalkMode}
              sleeveOnBodyMode={sleeveOnBodyMode}
              selectedPiece={selectedPiece}
              onPieceClick={setSelectedPiece}
              selectedCurveId={selectedCurveId}
              onCurveSelect={setSelectedCurveId}
              canEdit={canEditLoadedPattern}
              onCurveHandleChange={handleCurveHandleChange}
              freeModeActive={isFreePointEditingEnabled && canEditLoadedPattern}
              selectedFreePointNames={activeSelectedFreePoints}
              onFreePointSelectionChange={handleActiveFreePointSelectionChange}
            />

            {isFreePointEditingEnabled && canEditLoadedPattern && activeSelectedFreePoints.length > 0 && (
              <div className="absolute bottom-20 right-6 z-40 rounded-2xl border bg-background/95 shadow-xl backdrop-blur p-3 space-y-3 max-w-[240px]" data-testid={`${currentGarmentType}-free-mode-floating-nudge-modal`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid={`${currentGarmentType}-free-mode-floating-nudge-title-text`}>
                    Point Nudge
                  </p>
                  <span className="text-[11px] text-muted-foreground" data-testid={`${currentGarmentType}-free-mode-floating-nudge-count-text`}>
                    {activeSelectedFreePoints.length} selected
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2" data-testid={`${currentGarmentType}-free-mode-floating-selection-row`}>
                  <p className="text-[11px] text-muted-foreground" data-testid={`${currentGarmentType}-free-mode-floating-selection-text`}>
                    Selected points: <span className="font-semibold">{activeSelectedFreePoints.length}</span>
                  </p>
                  <button
                    type="button"
                    className="rounded-md border px-2 py-1 text-[11px] hover:bg-muted disabled:opacity-50"
                    onClick={() => handleClearActiveFreePointSelection()}
                    disabled={!canEditLoadedPattern || activeSelectedFreePoints.length === 0}
                    data-testid={`${currentGarmentType}-free-mode-floating-clear-selection-button`}
                  >
                    Clear
                  </button>
                </div>
                <p className="text-[11px] leading-relaxed text-muted-foreground" data-testid={`${currentGarmentType}-free-mode-floating-nudge-help-text`}>
                  Click points to multi-select. Left-drag on canvas for lasso. Pan with middle-drag or Space+left-drag. Nudge step: 1/16&quot;.
                </p>
                <div className="grid grid-cols-3 gap-2" data-testid={`${currentGarmentType}-free-mode-floating-nudge-grid`}>
                  <div />
                  <button
                    type="button"
                    className="h-9 rounded-md border bg-background hover:bg-muted disabled:opacity-50"
                    onClick={() => handleNudgeActiveFreePoints(0, 0.0625)}
                    disabled={!canEditLoadedPattern}
                    data-testid={`${currentGarmentType}-free-mode-floating-nudge-up-button`}
                  >
                    ↑
                  </button>
                  <div />
                  <button
                    type="button"
                    className="h-9 rounded-md border bg-background hover:bg-muted disabled:opacity-50"
                    onClick={() => handleNudgeActiveFreePoints(-0.0625, 0)}
                    disabled={!canEditLoadedPattern}
                    data-testid={`${currentGarmentType}-free-mode-floating-nudge-left-button`}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    className="h-9 rounded-md border bg-background hover:bg-muted disabled:opacity-50"
                    onClick={() => handleNudgeActiveFreePoints(0, -0.0625)}
                    disabled={!canEditLoadedPattern}
                    data-testid={`${currentGarmentType}-free-mode-floating-nudge-down-button`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="h-9 rounded-md border bg-background hover:bg-muted disabled:opacity-50"
                    onClick={() => handleNudgeActiveFreePoints(0.0625, 0)}
                    disabled={!canEditLoadedPattern}
                    data-testid={`${currentGarmentType}-free-mode-floating-nudge-right-button`}
                  >
                    →
                  </button>
                </div>
              </div>
            )}

            <CurveHandlePanel
              open={Boolean(selectedCurveId) && Array.isArray(patternData?.curveSegments) && patternData.curveSegments.length > 0}
              selectedCurveId={selectedCurveId}
              curveSegments={patternData?.curveSegments || []}
              curveHandles={patternData?.curveHandles || []}
              measurements={measurements}
              canEdit={canEditLoadedPattern}
              onClose={() => setSelectedCurveId(null)}
              onHandleValueChange={(key, value) => handleCurveHandleChange({ [key]: value })}
              onHandleBatchChange={handleCurveHandleChange}
            />
            
            {/* Right Sidebar - Always render, but show collapsed version when closed */}
            <RightSidebar
              showFront={showFront}
              setShowFront={setShowFront}
              showSide={showSide}
              setShowSide={setShowSide}
              showBack={showBack}
              setShowBack={setShowBack}
              showYoke={showYoke}
              setShowYoke={setShowYoke}
              hasYokePiece={Boolean(patternData?.yokePath)}
              showCollar={showCollar}
              setShowCollar={setShowCollar}
              hasCollarPiece={Boolean(patternData?.collarBandPath || patternData?.collarBandLayoutPath)}
              showSleeve={showSleeve}
              setShowSleeve={setShowSleeve}
              hasSleevePiece={Boolean(patternData?.sleevePath || patternData?.topSleevePath || patternData?.underSleevePath)}
              hasSidePiece={Boolean(patternData?.sidePath)}
              showMeasurements={showMeasurements}
              setShowMeasurements={setShowMeasurements}
              seamWalkMode={seamWalkMode}
              setSeamWalkMode={setSeamWalkMode}
              sleeveOnBodyMode={sleeveOnBodyMode}
              setSleeveOnBodyMode={setSleeveOnBodyMode}
              isOpen={rightSidebarOpen}
              onToggle={() => setRightSidebarOpen(!rightSidebarOpen)}
              currentPattern={currentPattern}
              garmentDisplayOptions={currentGarmentConfig.displayOptions}
              selectedVersions={selectedVersions}
              setSelectedVersions={setSelectedVersions}
              onVersionsLoaded={setAvailableVersions}
              token={token}
              autosaveStatus={patternAutosaveStatus}
              autosaveMessage={patternAutosaveMessage}
              onRetryAutosave={() => handleSave({
                silent: true,
                source: 'auto',
                force: true,
              })}
            />

            <div className="absolute bottom-3 left-1/2 z-40 -translate-x-1/2 rounded-full border bg-background/95 px-2 py-1 shadow-md backdrop-blur-sm" data-testid="canvas-zoom-controls">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCanvasZoomOut}
                  className="h-8 w-8 rounded-full border text-sm font-semibold hover:bg-muted"
                  data-testid="canvas-zoom-out-button"
                >
                  −
                </button>
                <span className="min-w-[70px] text-center text-xs font-medium" data-testid="canvas-zoom-level-text">
                  {(zoom * 100).toFixed(0)}%
                </span>
                <button
                  type="button"
                  onClick={handleCanvasZoomIn}
                  className="h-8 w-8 rounded-full border text-sm font-semibold hover:bg-muted"
                  data-testid="canvas-zoom-in-button"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={handleCanvasZoomReset}
                  className="h-8 rounded-full border px-3 text-xs font-medium hover:bg-muted"
                  data-testid="canvas-zoom-reset-button"
                >
                  Reset
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Dialogs */}
      <LoadPatternModal
        open={loadPatternModalOpen}
        onOpenChange={(open) => {
          setLoadPatternModalOpen(open);
          if (!open) setPendingAction(null);
        }}
        onSelectPattern={handleSelectPatternToLoad}
        token={token}
      />
      
      <ActionConfirmModal
        open={actionConfirmModalOpen}
        onOpenChange={setActionConfirmModalOpen}
        pattern={selectedPatternForAction}
        onConfirm={handleActionConfirm}
        token={token}
      />
      
      <UnsavedChangesModal
        open={unsavedChangesModalOpen}
        onOpenChange={setUnsavedChangesModalOpen}
        patternNumber={currentPattern?.pattern_number}
        onSave={handleUnsavedChangesSave}
        onDiscard={handleUnsavedChangesDiscard}
      />

    </div>
  );
}
